import React, { useState, useEffect } from 'react';
import { format, parseISO } from 'date-fns';
import {
  DndContext,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  DragStartEvent,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  Heart, Play, Check, Shuffle, Settings, Users, User,
  Clock, Dice6, ArrowLeft, ArrowRight, X,
  ClipboardList, Coins, Eye, Gift, Copy, CheckCheck, Calendar, Video, Lightbulb, Star, Target, Link as LinkIcon,
  Bell, CalendarCheck, ChevronRight, Moon, Plus, Search, SlidersHorizontal
} from 'lucide-react';
import { TagBadge, PrimaryButton, VideoModal, VideoPlayer, Modal, EmptyState } from '@/components/ui';
import { CoinFlip } from '@/components/CoinFlip';
import { ScheduleActivityModal } from '@/components/ScheduleActivityModal';
import { UpcomingActivities } from '@/components/UpcomingActivities';
import { ClipSidebar } from '@/components/ClipSidebar';
import { DragDropCalendarView } from '@/components/DragDropCalendarView';
import { ClipDragPreview } from '@/components/ClipDragPreview';
import { ActivityDragPreview } from '@/components/ActivityDragPreview';
import { FloatingChat } from '@/components/FloatingChat';
import { CategoryQAModal, CategoryQAPrompt } from '@/components/CategoryQAModal';
import { ActivityFollowUpModal } from '@/components/ActivityFollowUpModal';
import { ClipExperienceHistory } from '@/components/ClipExperienceHistory';
import { AvatarPair, DateNiteSideNav, DateNiteTabBar, WeekStrip, areaTone } from '@/components/datenite/parts';
import { ClipThumb } from '@/components/ClipThumb';
import { ClipBrowser, ClipSearchField } from '@/components/datenite/ClipBrowser';
import { PopularNow } from '@/components/datenite/PopularNow';
import { SwipeDeck } from '@/components/datenite/SwipeDeck';
import { FluidBackground } from '@/components/Brand';
import type { DateNiteTab } from '@/components/datenite/parts';
import { CATEGORIES, AREAS, TAGS, TRIED_RATINGS, COLORS } from '@/data';
import { HIS_SIZE_SCALE, HIS_GIRTH_SCALE, HIS_CUM_SCALE, HIS_CONSISTENCY_SCALE, HIS_TASTE_SCALE, HIS_PRECUM_SCALE, HER_WETNESS_SCALE, HER_SQUIRT_SCALE, HER_SENSITIVITY_SCALE, HER_ORGASM_EASE_SCALE } from '@/data/profile-scales';
import { getRedGifsWatchUrl, now, generateCoupleCode, generateAccessCode, generateId } from '@/lib/utils';
import { getCoupleByAccessCode, getCoupleByCode, saveCoupleByCode } from '@/lib/storage';
import { d1List, d1Upsert, d1Delete } from '@/lib/d1-storage';
import { getClipCategoryIds } from '@/types';
import type { Clip, CoupleData, ClipSelection, CategoryPreference, CoupleRecommendation, ScheduledActivity, ActivityActor, CoupleSubmission, CoupleRequest, ClipView, CategoryQAResponse, WishlistItem, CoupleFollowUp, CoupleQuestion, ClipSuggestionRequest, PickForUsRequest, PickForUsItem, ActivityFollowUp, TriedClip, HerProfile } from '@/types';
import type { TriedRatingValue } from '@/data/ratings';
import { CopyLinkButton, coupleLoginLink } from '@/components/CopyLinkButton';

const MOOD_AREAS = ['her', 'special', 'body', 'kinky'] as const;
const PREFERENCE_BADGES: Record<Exclude<CategoryPreference, 'neutral'>, { label: string; className: string }> = {
  love: { label: 'Love', className: 'bg-her-tint text-her' },
  like: { label: 'Like', className: 'bg-him-tint text-him' },
  curious: { label: 'Curious', className: 'bg-attention-tint text-attention' },
  nope: { label: 'Nope', className: 'bg-raised text-ink-muted' },
};

// "For Her" covers both the 'her' and 'below' areas
const matchesArea = (catArea: string | undefined, key: string) =>
  key === 'all' || catArea === key || (key === 'her' && catArea === 'below');

interface DateNiteViewProps {
  clips: Clip[];
  saveClips: (clips: Clip[]) => Promise<void>;
  coupleData: CoupleData | null;
  saveCoupleData: (data: CoupleData) => Promise<void>;
  recommendations: CoupleRecommendation[];
  saveRecommendations: (recs: CoupleRecommendation[]) => Promise<void>;
  onBack: () => void;
  initialClipId?: string;
  // Clip view tracking
  clipViews?: ClipView[];
  saveClipViews?: (views: ClipView[]) => Promise<void>;
  // Wishlist (Want to Try)
  wishlist?: WishlistItem[];
  saveWishlist?: (items: WishlistItem[]) => Promise<void>;
  // AI Follow-up questions for couples
  coupleFollowUps?: CoupleFollowUp[];
  saveCoupleFollowUps?: (followUps: CoupleFollowUp[]) => Promise<void>;
  // Reviewer/admin questions for couples
  coupleQuestions?: CoupleQuestion[];
  saveCoupleQuestions?: (questions: CoupleQuestion[]) => Promise<void>;
  // Clip suggestions from admin for rating
  clipSuggestions?: ClipSuggestionRequest[];
  saveClipSuggestions?: (suggestions: ClipSuggestionRequest[]) => Promise<void>;
  // Pick for us requests
  pickForUsRequests?: PickForUsRequest[];
  savePickForUsRequests?: (requests: PickForUsRequest[]) => Promise<void>;
}

export const DateNiteView: React.FC<DateNiteViewProps> = ({
  clips,
  saveClips,
  coupleData,
  saveCoupleData,
  recommendations,
  saveRecommendations,
  onBack,
  initialClipId,
  clipViews = [],
  saveClipViews,
  wishlist = [],
  saveWishlist,
  coupleFollowUps = [],
  saveCoupleFollowUps,
  coupleQuestions = [],
  saveCoupleQuestions,
  clipSuggestions = [],
  saveClipSuggestions,
  pickForUsRequests = [],
  savePickForUsRequests
}) => {
  const [tab, setTab] = useState<DateNiteTab>('browse');
  const [historyTab, setHistoryTab] = useState<'tried' | 'favorites'>('tried');

  // Favorite note prompt state
  const [favoritePrompt, setFavoritePrompt] = useState<{ clipId: string; clipTitle: string } | null>(null);
  const [favoriteNote, setFavoriteNote] = useState('');

  // Calendar scheduling state
  const [scheduledActivities, setScheduledActivities] = useState<ScheduledActivity[]>([]);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>();
  const [rescheduleActivityId, setRescheduleActivityId] = useState<string | null>(null);
  const [activeClip, setActiveClip] = useState<Clip | null>(null);
  const [activeActivity, setActiveActivity] = useState<{ activity: ScheduledActivity; clip: Clip | undefined } | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selection, setSelection] = useState<ClipSelection | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [area, setArea] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState<string[]>([]);
  const [showTagFilter, setShowTagFilter] = useState(false);
  const [clipQuery, setClipQuery] = useState('');
  const [exploreMode, setExploreMode] = useState<'categories' | 'clips'>('categories');
  const [focusSearch, setFocusSearch] = useState(false);
  const [showMonthCalendar, setShowMonthCalendar] = useState(false);
  const [schedulingTonight, setSchedulingTonight] = useState(false);
  // Note + own-clip link collected before the up-next date is marked done
  const [upNextDone, setUpNextDone] = useState<{ note: string; clipUrl: string } | null>(null);

  // Category Q&A state
  const [showCategoryQA, setShowCategoryQA] = useState<string | null>(null); // categoryId
  const [dismissedQAPrompts, setDismissedQAPrompts] = useState<string[]>([]);

  // Couple mode state
  const [showCoupleSetup, setShowCoupleSetup] = useState(false);
  const [setupTab, setSetupTab] = useState<'profile' | 'content' | 'more'>('profile');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [coupleForm, setCoupleForm] = useState({ hisName: '', herName: '' });
  const [hisProfileForm, setHisProfileForm] = useState<{ size?: number; girth?: number; cumIntensity?: number }>({});
  const [herProfileForm, setHerProfileForm] = useState<{ typicalWetness?: number; squirtingAbility?: number }>({});
  const [feedbackForm, setFeedbackForm] = useState<{
    wouldSubmitClips: 'yes' | 'maybe' | 'no' | '';
    productExcitement: 1 | 2 | 3 | 4 | 5 | null;
    generalFeedback: string;
  }>({
    wouldSubmitClips: '',
    productExcitement: null,
    generalFeedback: '',
  });
  const [showHisProfile, setShowHisProfile] = useState(false);
  const [showHerProfileOnboarding, setShowHerProfileOnboarding] = useState(false);
  // Content submission state
  const [submissions, setSubmissions] = useState<CoupleSubmission[]>([]);
  const [showSubmissionModal, setShowSubmissionModal] = useState(false);
  const [submissionForm, setSubmissionForm] = useState({ videoUrl: '', title: '', description: '' });
  // Reviewer feedback on submissions
  const [submissionFeedback, setSubmissionFeedback] = useState<{totalReviews: number, avgRating: number, wantMore: number, wantSolo: number, quotes: string[]}>({ totalReviews: 0, avgRating: 0, wantMore: 0, wantSolo: 0, quotes: [] });
  // Content request state
  const [requests, setRequests] = useState<CoupleRequest[]>([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null);
  const [requestForm, setRequestForm] = useState({
    type: 'clip' as 'clip' | 'category',
    description: '',
    categoryId: '',
    exampleUrl: '',      // RedGifs example link (for clip requests)
    exampleUrls: [''],   // Multiple example links (for category requests)
    categoryName: '',    // For category suggestions
    whyWanted: ''        // Why they want this
  });
  const [hisPick, setHisPick] = useState<Clip | null>(null);
  const [herPick, setHerPick] = useState<Clip | null>(null);
  const [showCoinFlip, setShowCoinFlip] = useState(false);
  const [showTriedModal, setShowTriedModal] = useState<Clip | null>(null);
  const [triedForm, setTriedForm] = useState({
    rating: '' as TriedRatingValue | '',
    hisThoughts: '',
    herThoughts: '',
    triedAt: new Date().toISOString().split('T')[0],
    wouldDoAgain: '' as 'yes' | 'maybe' | 'no' | '',
    inspiresDifferent: '' as 'yes' | 'no' | '',
    howDifferent: '',
    cumVolume: 0,
    cumConsistency: 0,
    cumTaste: 0,
    cumNotes: ''
  });
  const [codeCopied, setCodeCopied] = useState(false);

  // Old tried clips that need feedback prompt
  const [pendingFeedbackClips, setPendingFeedbackClips] = useState<string[]>([]);
  const [showFeedbackPrompt, setShowFeedbackPrompt] = useState(false);
  const [currentFeedbackClipId, setCurrentFeedbackClipId] = useState<string | null>(null);
  const [pendingFeedbackForm, setPendingFeedbackForm] = useState({
    wouldDoAgain: '' as 'yes' | 'maybe' | 'no' | '',
    inspiresDifferent: '' as 'yes' | 'no' | '',
    howDifferent: ''
  });
  const [showRecommendations, setShowRecommendations] = useState(false);
  // Follow-up answer form state
  const [followUpAnswers, setFollowUpAnswers] = useState<Record<string, string>>({});
  // Follow-up shared links state: { followUpId: [{ url, label }] }
  const [followUpLinks, setFollowUpLinks] = useState<Record<string, { url: string; label: string }[]>>({});
  const [followUpLinkInput, setFollowUpLinkInput] = useState<Record<string, string>>({});
  // Clip suggestion response state: { clipId: { rating, comment, interested } }
  const [clipSuggestionResponses, setClipSuggestionResponses] = useState<Record<string, { rating?: number; comment?: string; interested?: boolean }>>({});
  // Couple question answers state: { questionId: answer }
  const [coupleQuestionAnswers, setCoupleQuestionAnswers] = useState<Record<string, string>>({});
  // "Pick for us" state - clips couple wants reviewer to choose from
  const [pickForUsList, setPickForUsList] = useState<PickForUsItem[]>([]);
  const [showNewCode, setShowNewCode] = useState(false);
  const [newCoupleCode, setNewCoupleCode] = useState('');
  const [newAccessCode, setNewAccessCode] = useState('');
  const [watchingClip, setWatchingClip] = useState<Clip | null>(null);
  // Activity follow-up survey state
  const [activityFollowUps, setActivityFollowUps] = useState<ActivityFollowUp[]>([]);
  const [activityFollowUpsLoaded, setActivityFollowUpsLoaded] = useState(false);
  const [pendingFollowUpActivities, setPendingFollowUpActivities] = useState<ScheduledActivity[]>([]);
  const [currentFollowUpActivity, setCurrentFollowUpActivity] = useState<ScheduledActivity | null>(null);
  const [showFollowUpSurvey, setShowFollowUpSurvey] = useState(false);
  const [copiedClipId, setCopiedClipId] = useState<string | null>(null);

  // Copy clip link helper
  const copyClipLink = async (clipId: string) => {
    const url = `${window.location.origin}/clip/${clipId}`;
    await navigator.clipboard.writeText(url);
    setCopiedClipId(clipId);
    setTimeout(() => setCopiedClipId(null), 2000);
  };

  const getLinkedQuestionClips = (source: {
    clipId?: string;
    clipIds?: string[];
    question?: string;
    context?: string;
  }) => {
    const explicitIds = new Set<string>();
    if (source.clipId) explicitIds.add(source.clipId);
    (source.clipIds || []).forEach(id => explicitIds.add(id));

    const mentionText = `${source.question || ''} ${source.context || ''}`.toLowerCase();
    const seen = new Set<string>();

    return clips
      .filter(clip => {
        const watchUrl = getRedGifsWatchUrl(clip.link).toLowerCase();
        const title = clip.title.toLowerCase();
        const isExplicit = explicitIds.has(clip.id);
        const mentionsTitle = title.length > 5 && mentionText.includes(title);
        const mentionsLink = !!clip.link && (mentionText.includes(clip.link.toLowerCase()) || mentionText.includes(watchUrl));
        return isExplicit || mentionsTitle || mentionsLink;
      })
      .filter(clip => {
        if (seen.has(clip.id)) return false;
        seen.add(clip.id);
        return true;
      })
      .map(clip => {
        const category = CATEGORIES.find(c => c.id === clip.categoryId);
        return {
          id: clip.id,
          url: clip.link,
          title: clip.title,
          subtitle: category ? `${category.emoji} ${category.name}` : 'Video',
          clip,
        };
      });
  };

  const renderLinkedQuestionClips = (source: {
    clipId?: string;
    clipIds?: string[];
    question?: string;
    context?: string;
  }) => {
    const linkedClips = getLinkedQuestionClips(source);
    if (linkedClips.length === 0) return null;

    return (
      <div className="mb-3 space-y-2">
        {linkedClips.map(item => (
          <div key={item.id} className="rounded-lg overflow-hidden border border-zinc-600 bg-zinc-900">
            <VideoPlayer
              url={item.url}
              title={item.title}
              showThumbnail
              onClick={() => setWatchingClip(item.clip)}
            />
            <div className="px-3 py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-zinc-300 text-sm font-medium truncate">{item.title}</p>
                <p className="text-zinc-500 text-xs">{item.subtitle}</p>
              </div>
              <a
                href={getRedGifsWatchUrl(item.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0 inline-flex items-center gap-1 text-xs text-lime-400 hover:text-lime-300"
              >
                <LinkIcon className="w-3 h-3" /> Open
              </a>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // "Pick for us" helpers
  const addToPickForUs = (clip: Clip) => {
    if (pickForUsList.some(p => p.clipId === clip.id)) return; // Already added
    setPickForUsList(prev => [...prev, {
      clipId: clip.id,
      clipTitle: clip.title
    }]);
  };

  const removeFromPickForUs = (clipId: string) => {
    setPickForUsList(prev => prev.filter(p => p.clipId !== clipId));
  };

  const submitPickForUs = async () => {
    if (!savePickForUsRequests || !coupleData?.coupleId || !coupleData?.managedByReviewerId || pickForUsList.length === 0) return;

    const request: PickForUsRequest = {
      id: generateId('pick'),
      coupleId: coupleData.coupleId,
      reviewerId: coupleData.managedByReviewerId,
      items: pickForUsList,
      submittedAt: now(),
      status: 'pending'
    };

    await savePickForUsRequests([...pickForUsRequests, request]);
    setPickForUsList([]);
  };

  // Open clip from shared link (initialClipId) - use inline browse view
  useEffect(() => {
    if (initialClipId && clips.length > 0) {
      const clip = clips.find(c => c.id === initialClipId);
      if (clip) {
        const category = CATEGORIES.find(c => c.id === clip.categoryId);
        if (category) {
          setSelection({ category, clip });
          setRevealed(true);
          // Track this view from shared link
          if (saveClipViews && coupleData?.coupleId) {
            const view: ClipView = {
              clipId: clip.id,
              viewerId: coupleData.coupleId,
              viewerName: `${coupleData.hisName} & ${coupleData.herName}`,
              viewedAt: now(),
              source: 'datenite'
            };
            saveClipViews([...clipViews, view]);
          }
        }
      }
    }
  }, [initialClipId, clips]);

  // Returning couple state
  const [showReturningModal, setShowReturningModal] = useState(false);
  const [returningCode, setReturningCode] = useState('');
  const [returningError, setReturningError] = useState('');
  const [returningLoading, setReturningLoading] = useState(false);

  // His Profile modal state (dedicated screen)
  const [showHisProfileModal, setShowHisProfileModal] = useState(false);
  const [profileTab, setProfileTab] = useState<'stats' | 'details' | 'settings'>('stats');
  const [hisProfileEditForm, setHisProfileEditForm] = useState<{
    size?: number;
    girth?: number;
    cumIntensity?: number;
    cumConsistency?: number;
    consistencyNotes?: string;
    cumTaste?: number;
    tasteNotes?: string;
    precumProduction?: number;
    precumNotes?: string;
    managedBy?: 'him' | 'her' | 'both';
    profileNotes?: string;
    availableDays?: number[]; // 0=Sun, 1=Mon, ..., 6=Sat
    // Visual sharing fields
    visualSharingOpen?: 'yes' | 'maybe' | 'no';
    visualUrls?: {
      sizeUrl?: string;
      cumUrl?: string;
      precumUrl?: string;
      otherUrl?: string;
    };
    visualSharingNotes?: string;
  }>({});

  // Her Profile modal state (dedicated screen)
  const [showHerProfileModal, setShowHerProfileModal] = useState(false);
  const [herProfileTab, setHerProfileTab] = useState<'stats' | 'details' | 'settings'>('stats');
  const [herProfileEditForm, setHerProfileEditForm] = useState<{
    typicalWetness?: number;
    wetnessNotes?: string;
    squirtingAbility?: number;
    squirtingNotes?: string;
    sensitivity?: number;
    sensitivityNotes?: string;
    orgasmEase?: number;
    orgasmNotes?: string;
    // Visual sharing fields
    visualSharingOpen?: 'yes' | 'maybe' | 'no';
    visualUrls?: {
      wetnessUrl?: string;
      squirtUrl?: string;
      otherUrl?: string;
    };
    visualSharingNotes?: string;
  }>({});

  const unseenRecommendations = recommendations.filter(r => !r.seen);

  // Load scheduled activities when couple is set up
  useEffect(() => {
    const loadScheduledActivities = async () => {
      if (coupleData?.coupleId) {
        try {
          const activities = await d1List<ScheduledActivity>('scheduled_activities', { coupleId: coupleData.coupleId });
          setScheduledActivities(activities);
        } catch {
          setScheduledActivities([]);
        }
      }
    };
    loadScheduledActivities();
  }, [coupleData?.coupleId]);

  // Load activity follow-ups and check for pending surveys
  useEffect(() => {
    const loadActivityFollowUps = async () => {
      if (coupleData?.coupleId) {
        try {
          const followUps = await d1List<ActivityFollowUp>('activity_followups', { coupleId: coupleData.coupleId });
          setActivityFollowUps(followUps);
        } catch {
          setActivityFollowUps([]);
        } finally {
          setActivityFollowUpsLoaded(true);
        }
      }
    };
    loadActivityFollowUps();
  }, [coupleData?.coupleId]);

  // Check for past-due activities needing follow-up on login
  // Wait for activityFollowUps to load to avoid race condition
  useEffect(() => {
    if (!activityFollowUpsLoaded) return; // Don't check until follow-ups are loaded
    if (coupleData?.coupleId && scheduledActivities.length > 0) {
      const today = new Date().toISOString().split('T')[0] ?? '';
      const pastDueWithoutFollowUp = scheduledActivities.filter(activity => {
        const isPastDue = activity.scheduledDate < today;
        const hasFollowUp = activityFollowUps.some(f => f.scheduledActivityId === activity.id);
        const isAlreadyCompleted = !!activity.completedAt;
        const isAlreadyTried = (coupleData.tried || []).some(t => t.clipId === activity.clipId);
        return isPastDue && !hasFollowUp && !isAlreadyCompleted && !isAlreadyTried;
      });

      if (pastDueWithoutFollowUp.length > 0 && pastDueWithoutFollowUp[0]) {
        setPendingFollowUpActivities(pastDueWithoutFollowUp);
        setCurrentFollowUpActivity(pastDueWithoutFollowUp[0]);
        setShowFollowUpSurvey(true);
      }
    }
  }, [coupleData?.coupleId, scheduledActivities, activityFollowUps, activityFollowUpsLoaded]);

  // Load couple submissions when couple is set up
  useEffect(() => {
    const loadSubmissions = async () => {
      if (coupleData?.coupleId) {
        try {
          const subs = await d1List<CoupleSubmission>('couple_submissions', { coupleId: coupleData.coupleId });
          setSubmissions(subs);
        } catch {
          setSubmissions([]);
        }
      }
    };
    loadSubmissions();
  }, [coupleData?.coupleId]);

  // Auto-trigger onboarding for couples without preferences (e.g., reviewers who just created couple profile)
  useEffect(() => {
    if (coupleData?.coupleId && (!coupleData.preferences || Object.keys(coupleData.preferences).length === 0)) {
      // Check if they've seen onboarding before (don't re-show if they dismissed it)
      const dismissedKey = `vb-onboarding-dismissed:${coupleData.coupleId}`;
      const wasDismissed = localStorage.getItem(dismissedKey);
      if (!wasDismissed) {
        setShowOnboarding(true);
        setOnboardingStep(0);
      }
    }
  }, [coupleData?.coupleId, coupleData?.preferences]);

  // Save a new submission
  const saveSubmission = async (submission: Omit<CoupleSubmission, 'id' | 'coupleId' | 'status' | 'createdAt'>) => {
    if (!coupleData?.coupleId) return;
    const newSubmission: CoupleSubmission = {
      id: generateId('sub'),
      coupleId: coupleData.coupleId,
      videoUrl: submission.videoUrl,
      title: submission.title,
      description: submission.description,
      status: 'pending',
      createdAt: now(),
    };
    await d1Upsert('couple_submissions', newSubmission);
    // Also promote to global submitted_clips so all reviewers can review
    await d1Upsert('submitted_clips', {
      id: newSubmission.id,
      clipTitle: newSubmission.title,
      contentLink: newSubmission.videoUrl,
      creatorId: coupleData.coupleId,
      product: 'Load Boost',
      gender: 'couple',
      status: 'Submitted',
      dateSubmitted: new Date().toISOString().split('T')[0],
      standoutNotes: newSubmission.description || `Couple submission from ${coupleData.hisName} & ${coupleData.herName}`,
    });
    setSubmissions([...submissions, newSubmission]);
    setSubmissionForm({ videoUrl: '', title: '', description: '' });
    setShowSubmissionModal(false);
  };

  // Load couple requests when couple is set up
  useEffect(() => {
    const loadRequests = async () => {
      if (coupleData?.coupleId) {
        try {
          const reqs = await d1List<CoupleRequest>('couple_requests', { coupleId: coupleData.coupleId });
          setRequests(reqs);
        } catch {
          setRequests([]);
        }
      }
    };
    loadRequests();
  }, [coupleData?.coupleId]);

  // Load aggregated reviewer feedback on couple's submitted content
  useEffect(() => {
    if (setupTab === 'content' && coupleData?.coupleId) {
      (async () => {
        const myClips = await d1List<any>('submitted_clips', { creatorId: coupleData.coupleId });
        if (myClips.length === 0) return;
        const allReviews = await d1List<any>('submitted_clip_reviews');
        const myClipIds = new Set(myClips.map((c: any) => c.id));
        const myReviews = allReviews.filter((r: any) => myClipIds.has(r.submittedClipId));
        if (myReviews.length === 0) return;
        const avgRating = myReviews.reduce((sum: number, r: any) => sum + (r.qualityRating || 0), 0) / myReviews.length;
        const wantMore = myReviews.filter((r: any) => r.wantMoreFromCreator === 'yes').length;
        const wantSolo = myReviews.filter((r: any) => r.wouldWatchMaleSolo === 'yes').length;
        const quotes = myReviews
          .filter((r: any) => r.howItMadeThemFeel || r.intrigues)
          .map((r: any) => r.howItMadeThemFeel || r.intrigues)
          .slice(0, 3);
        setSubmissionFeedback({ totalReviews: myReviews.length, avgRating: Math.round(avgRating * 10) / 10, wantMore, wantSolo, quotes });
      })();
    }
  }, [setupTab, coupleData?.coupleId]);

  // Check for old tried clips missing feedback on login
  useEffect(() => {
    if (coupleData?.tried && coupleData.tried.length > 0) {
      const needsFeedback = coupleData.tried
        .filter(t => !t.wouldDoAgain && !t.inspiresDifferent)
        .map(t => t.clipId);
      if (needsFeedback.length > 0) {
        setPendingFeedbackClips(needsFeedback);
        setCurrentFeedbackClipId(needsFeedback[0] ?? null);
        setShowFeedbackPrompt(true);
      }
    }
  }, [coupleData?.coupleId]); // Only run when couple changes (login)

  // Save feedback for an old tried clip
  const savePendingFeedback = async () => {
    if (!currentFeedbackClipId || !coupleData) return;
    if (!pendingFeedbackForm.wouldDoAgain) return; // Require at least one answer

    const updatedTried = (coupleData.tried || []).map(t =>
      t.clipId === currentFeedbackClipId
        ? {
            ...t,
            wouldDoAgain: pendingFeedbackForm.wouldDoAgain || undefined,
            inspiresDifferent: pendingFeedbackForm.inspiresDifferent || undefined,
            howDifferent: pendingFeedbackForm.howDifferent || undefined
          }
        : t
    );

    const updated = { ...coupleData, tried: updatedTried, lastActivityAt: now() };
    await saveCoupleData(updated);
    await saveCoupleByCode(updated);

    // Move to next clip or close
    const remaining = pendingFeedbackClips.filter(id => id !== currentFeedbackClipId);
    setPendingFeedbackClips(remaining);
    setPendingFeedbackForm({ wouldDoAgain: '', inspiresDifferent: '', howDifferent: '' });

    if (remaining.length > 0) {
      setCurrentFeedbackClipId(remaining[0] ?? null);
    } else {
      setShowFeedbackPrompt(false);
      setCurrentFeedbackClipId(null);
    }
  };

  // Skip this feedback prompt
  const skipFeedback = () => {
    const remaining = pendingFeedbackClips.filter(id => id !== currentFeedbackClipId);
    setPendingFeedbackClips(remaining);
    setPendingFeedbackForm({ wouldDoAgain: '', inspiresDifferent: '', howDifferent: '' });

    if (remaining.length > 0) {
      setCurrentFeedbackClipId(remaining[0] ?? null);
    } else {
      setShowFeedbackPrompt(false);
      setCurrentFeedbackClipId(null);
    }
  };

  // Save activity follow-up survey
  const saveActivityFollowUpSurvey = async (
    followUp: ActivityFollowUp,
    triedClip?: TriedClip,
    newActivity?: ScheduledActivity
  ) => {
    if (!coupleData) return;

    // Save the activity follow-up (d1Upsert handles duplicates via INSERT OR REPLACE)
    await d1Upsert('activity_followups', followUp);
    const alreadyExists = activityFollowUps.some(f => f.scheduledActivityId === followUp.scheduledActivityId);
    const updatedFollowUps = alreadyExists ? activityFollowUps : [...activityFollowUps, followUp];
    setActivityFollowUps(updatedFollowUps);

    // Dual-write to tried clips if completed
    if (triedClip) {
      const updatedCoupleData = {
        ...coupleData,
        tried: [...coupleData.tried, triedClip],
        lastActivityAt: now()
      };
      await saveCoupleData(updatedCoupleData);
      await saveCoupleByCode(updatedCoupleData);
    } else {
      // Still update lastActivityAt even if not adding to tried
      const updatedCoupleData = { ...coupleData, lastActivityAt: now() };
      await saveCoupleData(updatedCoupleData);
      await saveCoupleByCode(updatedCoupleData);
    }

    // Create new scheduled activity if rescheduling
    if (newActivity) {
      const activityWithCoupleId = {
        ...newActivity,
        coupleId: newActivity.coupleId || coupleData.coupleId,
      };
      await d1Upsert('scheduled_activities', activityWithCoupleId);
      setScheduledActivities([...scheduledActivities, activityWithCoupleId]);
    }

    // Move to next activity or close
    const remaining = pendingFollowUpActivities.filter(a => a.id !== currentFollowUpActivity?.id);
    const nextActivity = remaining[0];
    if (remaining.length > 0 && nextActivity) {
      setPendingFollowUpActivities(remaining);
      setCurrentFollowUpActivity(nextActivity);
    } else {
      setShowFollowUpSurvey(false);
      setPendingFollowUpActivities([]);
      setCurrentFollowUpActivity(null);
    }
  };

  // Skip activity follow-up survey
  const skipActivityFollowUp = () => {
    const remaining = pendingFollowUpActivities.filter(a => a.id !== currentFollowUpActivity?.id);
    const nextActivity = remaining[0];
    if (remaining.length > 0 && nextActivity) {
      setPendingFollowUpActivities(remaining);
      setCurrentFollowUpActivity(nextActivity);
    } else {
      setShowFollowUpSurvey(false);
      setPendingFollowUpActivities([]);
      setCurrentFollowUpActivity(null);
    }
  };

  // Dismiss all activity follow-up surveys
  const dismissAllActivityFollowUps = () => {
    setShowFollowUpSurvey(false);
    setPendingFollowUpActivities([]);
    setCurrentFollowUpActivity(null);
  };

  // Save a new content request
  const saveRequest = async (request: {
    type: 'clip' | 'category';
    description: string;
    categoryId?: string;
    exampleUrl?: string;
    exampleUrls?: string[];
    categoryName?: string;
    whyWanted?: string;
  }) => {
    if (!coupleData?.coupleId) return;
    // Filter out empty URLs
    const validUrls = request.exampleUrls?.filter(url => url.trim()) || [];
    const newRequest: CoupleRequest = {
      id: generateId('req'),
      coupleId: coupleData.coupleId,
      type: request.type,
      description: request.description,
      categoryId: request.categoryId || undefined,
      exampleUrl: request.exampleUrl?.trim() || undefined,
      exampleUrls: validUrls.length > 0 ? validUrls : undefined,
      categoryName: request.categoryName?.trim() || undefined,
      whyWanted: request.whyWanted?.trim() || undefined,
      status: 'pending',
      createdAt: now(),
    };
    await d1Upsert('couple_requests', newRequest);
    setRequests([...requests, newRequest]);
    setRequestForm({ type: 'clip', description: '', categoryId: '', exampleUrl: '', exampleUrls: [''], categoryName: '', whyWanted: '' });
    setEditingRequestId(null);
    setShowRequestModal(false);
  };

  // Update an existing content request
  const updateRequest = async (requestId: string) => {
    if (!coupleData?.coupleId) return;
    const validUrls = requestForm.exampleUrls?.filter(url => url.trim()) || [];
    const updatedItem = {
      ...requests.find(r => r.id === requestId)!,
      type: requestForm.type,
      description: requestForm.description,
      categoryId: requestForm.categoryId?.trim() || undefined,
      exampleUrl: requestForm.exampleUrl?.trim() || undefined,
      exampleUrls: validUrls.length > 0 ? validUrls : undefined,
      categoryName: requestForm.categoryName?.trim() || undefined,
      whyWanted: requestForm.whyWanted?.trim() || undefined,
    };
    await d1Upsert('couple_requests', updatedItem);
    const updated = requests.map(r => r.id === requestId ? updatedItem : r);
    setRequests(updated);
    setRequestForm({ type: 'clip', description: '', categoryId: '', exampleUrl: '', exampleUrls: [''], categoryName: '', whyWanted: '' });
    setEditingRequestId(null);
    setShowRequestModal(false);
  };

  // Delete a content request
  const deleteRequest = async (requestId: string) => {
    if (!coupleData?.coupleId) return;
    await d1Delete('couple_requests', requestId);
    setRequests(requests.filter(r => r.id !== requestId));
  };

  // Get context for activity creation
  const getCoupleContext = (): ActivityActor => ({
    type: 'couple',
    id: coupleData?.coupleId || '',
    name: coupleData ? `${coupleData.hisName} & ${coupleData.herName}` : 'Couple',
  });

  // Save scheduled activities (keyed by coupleId for shared access)
  const saveScheduledActivities = async (activities: ScheduledActivity[]) => {
    if (!coupleData?.coupleId) return;
    const activitiesWithCoupleId = activities.map(a => ({
      ...a,
      coupleId: a.coupleId || coupleData.coupleId,
    }));
    // Diff: find added, changed, and removed activities
    const prevIds = new Set(scheduledActivities.map(a => a.id));
    const nextIds = new Set(activitiesWithCoupleId.map(a => a.id));
    // Upsert any new or changed activities
    for (const a of activitiesWithCoupleId) {
      const prev = scheduledActivities.find(p => p.id === a.id);
      if (!prev || JSON.stringify(prev) !== JSON.stringify(a)) {
        await d1Upsert('scheduled_activities', a);
      }
    }
    // Delete removed activities
    for (const id of prevIds) {
      if (!nextIds.has(id)) {
        await d1Delete('scheduled_activities', id);
      }
    }
    setScheduledActivities(activitiesWithCoupleId);
  };

  // Handle scheduling a new activity
  const handleScheduleActivity = async (activity: ScheduledActivity) => {
    const activityWithCreator: ScheduledActivity = {
      ...activity,
      coupleId: activity.coupleId || coupleData?.coupleId,
      createdBy: activity.createdBy || getCoupleContext(),
    };
    // If rescheduling, remove the old activity first
    let baseActivities = scheduledActivities;
    if (rescheduleActivityId) {
      baseActivities = scheduledActivities.filter(a => a.id !== rescheduleActivityId);
      setRescheduleActivityId(null);
    }
    const updated = [...baseActivities, activityWithCreator];
    await saveScheduledActivities(updated);
    setShowScheduleModal(false);
    setSelectedCalendarDate(undefined);
  };

  // Handle marking activity as complete with optional note and user clip URL
  const handleMarkActivityComplete = async (activityId: string, note?: string, userClipUrl?: string) => {
    const updated = scheduledActivities.map(a =>
      a.id === activityId ? {
        ...a,
        completedAt: now(),
        notes: note || a.notes, // Add or keep existing note
        userClipUrl: userClipUrl || a.userClipUrl, // Add user-submitted clip URL
        modifiedBy: getCoupleContext(),
        modifiedAt: now()
      } : a
    );
    await saveScheduledActivities(updated);
  };

  // Handle deleting activity
  const handleDeleteActivity = async (activityId: string) => {
    const updated = scheduledActivities.filter(a => a.id !== activityId);
    await saveScheduledActivities(updated);
  };

  // Handle reschedule (opens modal with activity context - only deletes when user confirms new schedule)
  const handleRescheduleActivity = (activityId: string) => {
    const activity = scheduledActivities.find(a => a.id === activityId);
    if (activity) {
      const clip = clips.find(c => c.id === activity.clipId);
      if (clip) {
        const category = CATEGORIES.find(c => c.id === clip.categoryId);
        if (category) {
          setSelection({ category, clip });
        }
      }
    }
    // Store the ID - the old activity will be removed when user confirms new schedule
    setRescheduleActivityId(activityId);
    setShowScheduleModal(true);
  };

  // DnD sensors configuration
  const sensors = useSensors(
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
    useSensor(KeyboardSensor)
  );

  // Handle drag start
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    if (active.data.current?.type === 'clip') {
      setActiveClip(active.data.current.clip);
      setActiveActivity(null);
    } else if (active.data.current?.type === 'activity') {
      setActiveActivity({
        activity: active.data.current.activity,
        clip: active.data.current.clip,
      });
      setActiveClip(null);
    }
  };

  // Handle drag end - create activity on valid drop or reschedule existing
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveClip(null);
    setActiveActivity(null);

    if (!over) return; // Dropped outside

    // Check if dropped on a calendar cell
    if (over.data.current?.type === 'calendar-cell') {
      const targetDate = over.data.current.date as string;
      if (!targetDate) return;

      // Check if dragging a clip (new activity) or an existing activity (reschedule)
      if (active.data.current?.type === 'clip') {
        // Creating new activity from clip
        const clipId = active.id as string;
        const activity: ScheduledActivity = {
          id: generateId('sched'),
          clipId,
          scheduledDate: targetDate,
          createdAt: now(),
          createdBy: getCoupleContext(),
        };
        const updated = [...scheduledActivities, activity];
        await saveScheduledActivities(updated);
      } else if (active.data.current?.type === 'activity') {
        // Rescheduling existing activity
        const activityId = active.data.current.activity.id;
        const currentActivity = scheduledActivities.find(a => a.id === activityId);

        // Don't update if dropped on the same date
        if (currentActivity && currentActivity.scheduledDate !== targetDate) {
          const updated = scheduledActivities.map(a =>
            a.id === activityId
              ? {
                  ...a,
                  scheduledDate: targetDate,
                  modifiedBy: getCoupleContext(),
                  modifiedAt: now(),
                }
              : a
          );
          await saveScheduledActivities(updated);
        }
      }
    }
  };

  const copyCode = async () => {
    if (coupleData?.coupleId) {
      await navigator.clipboard.writeText(coupleData.coupleId);
      setCodeCopied(true);
      setTimeout(() => setCodeCopied(false), 2000);
    }
  };

  // Handle returning couple login - supports both VBA access codes and legacy VBC codes
  const handleReturningCouple = async () => {
    const code = returningCode.toUpperCase().trim();

    // Accept both VBA (access codes) and VBC (legacy/share codes) for backwards compatibility
    const isVBA = code.startsWith('VBA-') && code.length === 10;
    const isVBC = code.startsWith('VBC-') && code.length === 10;

    if (!isVBA && !isVBC) {
      setReturningError('Enter your code (VBA-XXXXXX or VBC-XXXXXX).');
      return;
    }

    setReturningLoading(true);
    setReturningError('');

    try {
      let existingData: CoupleData | null = null;

      if (isVBA) {
        // Try VBA access code lookup first
        existingData = await getCoupleByAccessCode(code);
      }

      if (!existingData && isVBC) {
        // Fallback: try direct VBC code lookup (legacy couples or share code login)
        existingData = await getCoupleByCode(code);
      }

      if (!existingData) {
        setReturningError('Code not found. Check your code or start fresh.');
        setReturningLoading(false);
        return;
      }

      // Migration: If logged in with VBC and no access code exists, generate one
      let updatedData = { ...existingData, lastActivityAt: now() };
      let migratedAccessCode: string | null = null;

      if (isVBC && !existingData.accessCode) {
        // Generate new access code for migration
        migratedAccessCode = generateAccessCode();
        updatedData.accessCode = migratedAccessCode;
      }

      // Save updated data
      await saveCoupleData(updatedData);
      await saveCoupleByCode(updatedData); // Sync to global storage + access index
      localStorage.setItem('vb-my-couple-id', existingData.coupleId);

      // If we migrated, show the new access code
      if (migratedAccessCode) {
        setNewAccessCode(migratedAccessCode);
        setNewCoupleCode(existingData.coupleId);
        setShowNewCode(true);
      }

      setShowReturningModal(false);
      setReturningCode('');
      setReturningError('');
    } catch {
      setReturningError('Something went wrong. Please try again.');
    } finally {
      setReturningLoading(false);
    }
  };

  const markRecommendationSeen = async (recId: string) => {
    const updated = recommendations.map(r => r.id === recId ? { ...r, seen: true } : r);
    await saveRecommendations(updated);
  };

  // Open His Profile modal with current data
  const openHisProfileModal = () => {
    setHisProfileEditForm({
      size: coupleData?.hisProfile?.size,
      girth: coupleData?.hisProfile?.girth,
      cumIntensity: coupleData?.hisProfile?.cumIntensity,
      cumConsistency: coupleData?.hisProfile?.cumConsistency,
      consistencyNotes: coupleData?.hisProfile?.consistencyNotes || '',
      cumTaste: coupleData?.hisProfile?.cumTaste,
      tasteNotes: coupleData?.hisProfile?.tasteNotes || '',
      precumProduction: coupleData?.hisProfile?.precumProduction,
      precumNotes: coupleData?.hisProfile?.precumNotes || '',
      managedBy: coupleData?.managedBy || 'both',
      profileNotes: coupleData?.profileNotes || '',
      availableDays: coupleData?.availableDays || [],
      // Visual sharing fields
      visualSharingOpen: coupleData?.hisProfile?.visualSharingOpen,
      visualUrls: coupleData?.hisProfile?.visualUrls || {},
      visualSharingNotes: coupleData?.hisProfile?.visualSharingNotes || '',
    });
    setShowHisProfileModal(true);
  };

  // Save His Profile from modal
  const saveHisProfileModal = async () => {
    if (!coupleData?.coupleId) return;

    const updatedProfile = {
      ...(coupleData.hisProfile || {}),
      size: hisProfileEditForm.size,
      girth: hisProfileEditForm.girth,
      cumIntensity: hisProfileEditForm.cumIntensity,
      cumConsistency: hisProfileEditForm.cumConsistency,
      consistencyNotes: hisProfileEditForm.consistencyNotes,
      cumTaste: hisProfileEditForm.cumTaste,
      tasteNotes: hisProfileEditForm.tasteNotes,
      precumProduction: hisProfileEditForm.precumProduction,
      precumNotes: hisProfileEditForm.precumNotes,
      // Visual sharing fields
      visualSharingOpen: hisProfileEditForm.visualSharingOpen,
      visualUrls: hisProfileEditForm.visualUrls,
      visualSharingNotes: hisProfileEditForm.visualSharingNotes,
    };

    const updated = {
      ...coupleData,
      hisProfile: updatedProfile,
      managedBy: hisProfileEditForm.managedBy,
      profileNotes: hisProfileEditForm.profileNotes,
      availableDays: hisProfileEditForm.availableDays,
      lastActivityAt: now(), // Update activity on profile save
    };

    await saveCoupleData(updated);
    await saveCoupleByCode(updated);
    setShowHisProfileModal(false);
  };

  // Open Her Profile modal with current data
  const openHerProfileModal = () => {
    setHerProfileEditForm({
      typicalWetness: coupleData?.herProfile?.typicalWetness,
      wetnessNotes: coupleData?.herProfile?.wetnessNotes || '',
      squirtingAbility: coupleData?.herProfile?.squirtingAbility,
      squirtingNotes: coupleData?.herProfile?.squirtingNotes || '',
      sensitivity: coupleData?.herProfile?.sensitivity,
      sensitivityNotes: coupleData?.herProfile?.sensitivityNotes || '',
      orgasmEase: coupleData?.herProfile?.orgasmEase,
      orgasmNotes: coupleData?.herProfile?.orgasmNotes || '',
      // Visual sharing fields
      visualSharingOpen: coupleData?.herProfile?.visualSharingOpen,
      visualUrls: coupleData?.herProfile?.visualUrls || {},
      visualSharingNotes: coupleData?.herProfile?.visualSharingNotes || '',
    });
    setHerProfileTab('stats');
    setShowHerProfileModal(true);
  };

  // Save Her Profile from modal
  const saveHerProfileModal = async () => {
    if (!coupleData?.coupleId) return;

    const updatedProfile: HerProfile = {
      ...(coupleData.herProfile || {}),
      typicalWetness: herProfileEditForm.typicalWetness,
      wetnessNotes: herProfileEditForm.wetnessNotes,
      squirtingAbility: herProfileEditForm.squirtingAbility,
      squirtingNotes: herProfileEditForm.squirtingNotes,
      sensitivity: herProfileEditForm.sensitivity,
      sensitivityNotes: herProfileEditForm.sensitivityNotes,
      orgasmEase: herProfileEditForm.orgasmEase,
      orgasmNotes: herProfileEditForm.orgasmNotes,
      // Visual sharing fields
      visualSharingOpen: herProfileEditForm.visualSharingOpen,
      visualUrls: herProfileEditForm.visualUrls,
      visualSharingNotes: herProfileEditForm.visualSharingNotes,
    };

    const updated = {
      ...coupleData,
      herProfile: updatedProfile,
      lastActivityAt: now(), // Update activity on profile save
    };

    await saveCoupleData(updated);
    await saveCoupleByCode(updated);
    setShowHerProfileModal(false);
  };

  const viewRecommendation = async (rec: CoupleRecommendation) => {
    const clip = clips.find(c => c.id === rec.clipId);
    if (clip) {
      if (!rec.seen) {
        await markRecommendationSeen(rec.id);
      }
      setShowRecommendations(false);
      setWatchingClip(clip);
    }
  };

  const isSetup = coupleData?.hisName && coupleData?.herName;
  const hisName = coupleData?.hisName || 'Him';
  const herName = coupleData?.herName || 'Her';
  const preferences = coupleData?.preferences || {};
  const tried = coupleData?.tried || [];
  const favorites = coupleData?.favorites || [];

  const getClipsForCategory = (catId: string) => clips.filter(c => getClipCategoryIds(c).includes(catId));

  const filteredCategories = CATEGORIES.filter(cat => {
    if (!matchesArea(cat.area, area)) return false;
    const catClips = getClipsForCategory(cat.id);
    if (tagFilter.length > 0) {
      return catClips.some(clip => tagFilter.some(tag => clip.tags?.includes(tag)));
    }
    return catClips.length > 0;
  });

  const setupCouple = async () => {
    if (!coupleForm.hisName.trim() || !coupleForm.herName.trim()) return;
    const isNewSetup = !coupleData?.coupleId;
    const shareCode = coupleData?.coupleId || generateCoupleCode();
    const accessCode = coupleData?.accessCode || generateAccessCode();

    // Build His Profile - use form values if set, otherwise preserve existing
    const hisProfile = (hisProfileForm.size || hisProfileForm.girth || hisProfileForm.cumIntensity)
      ? {
          size: hisProfileForm.size ?? coupleData?.hisProfile?.size,
          girth: hisProfileForm.girth ?? coupleData?.hisProfile?.girth,
          cumIntensity: hisProfileForm.cumIntensity ?? coupleData?.hisProfile?.cumIntensity,
        }
      : coupleData?.hisProfile;

    // Build Her Profile - use form values if set, otherwise preserve existing
    const herProfile = (herProfileForm.typicalWetness !== undefined || herProfileForm.squirtingAbility !== undefined)
      ? {
          ...coupleData?.herProfile,
          typicalWetness: herProfileForm.typicalWetness ?? coupleData?.herProfile?.typicalWetness,
          squirtingAbility: herProfileForm.squirtingAbility ?? coupleData?.herProfile?.squirtingAbility,
        }
      : coupleData?.herProfile;

    const newCoupleData: CoupleData = {
      // Preserve all existing data first
      ...coupleData,
      // Then override only specific fields
      coupleId: shareCode,
      accessCode: accessCode,
      hisName: coupleForm.hisName.trim(),
      herName: coupleForm.herName.trim(),
      setupAt: coupleData?.setupAt || now(),
      lastActivityAt: now(), // Track profile creation/update
      preferences: coupleData?.preferences || {},
      tried: coupleData?.tried || [],
      favorites: coupleData?.favorites || [],
      hisProfile,
      herProfile,
    };

    await saveCoupleData(newCoupleData);
    // Also save to couple-keyed storage for global access (re-authentication)
    await saveCoupleByCode(newCoupleData);
    setShowCoupleSetup(false);

    if (isNewSetup) {
      // Show the new codes to the user
      setNewCoupleCode(shareCode);
      setNewAccessCode(accessCode);
      setShowNewCode(true);
    }
  };

  const savePreference = async (catId: string, pref: CategoryPreference) => {
    const newPrefs = { ...preferences, [catId]: pref };
    const updated = { ...coupleData!, preferences: newPrefs, lastActivityAt: now() };
    await saveCoupleData(updated);
    await saveCoupleByCode(updated); // Keep global storage in sync
  };

  const saveCategoryQA = async (response: CategoryQAResponse) => {
    if (!coupleData) return;
    const existingQA = coupleData.categoryQA || {};
    // Merge new answers with any existing answers for this category
    const existingCategoryAnswers = existingQA[response.categoryId]?.answers || {};
    const mergedAnswers = { ...existingCategoryAnswers, ...response.answers };
    const updated = {
      ...coupleData,
      categoryQA: {
        ...existingQA,
        [response.categoryId]: {
          categoryId: response.categoryId,
          answeredAt: response.answeredAt,
          answers: mergedAnswers
        }
      },
      lastActivityAt: now()
    };
    await saveCoupleData(updated);
    await saveCoupleByCode(updated);
    setShowCategoryQA(null);
  };

  const toggleFavorite = async (clipId: string, clipTitle?: string) => {
    const isFav = favorites.includes(clipId);
    if (isFav) {
      // Removing favorite - just remove it
      const newFavs = favorites.filter(f => f !== clipId);
      const newNotes = { ...(coupleData?.favoriteNotes || {}) };
      delete newNotes[clipId];
      const updated = { ...coupleData!, favorites: newFavs, favoriteNotes: newNotes, lastActivityAt: now() };
      await saveCoupleData(updated);
      await saveCoupleByCode(updated);
    } else {
      // Adding favorite - show prompt for note
      const clip = clips.find(c => c.id === clipId);
      setFavoritePrompt({ clipId, clipTitle: clipTitle || clip?.title || 'this clip' });
      setFavoriteNote('');
    }
  };

  const saveFavoriteWithNote = async (note?: string) => {
    if (!favoritePrompt) return;
    const newFavs = [...favorites, favoritePrompt.clipId];
    const newNotes = { ...(coupleData?.favoriteNotes || {}) };
    if (note?.trim()) {
      newNotes[favoritePrompt.clipId] = note.trim();
    }
    const updated = { ...coupleData!, favorites: newFavs, favoriteNotes: newNotes, lastActivityAt: now() };
    await saveCoupleData(updated);
    await saveCoupleByCode(updated);
    setFavoritePrompt(null);
    setFavoriteNote('');
  };

  // Track clip view (when couple watches a clip)
  const trackClipView = async (clip: Clip) => {
    if (!saveClipViews || !coupleData?.coupleId) return;

    const view: ClipView = {
      clipId: clip.id,
      viewerId: coupleData.coupleId,
      viewerName: `${coupleData.hisName} & ${coupleData.herName}`,
      viewedAt: now(),
      source: 'datenite'
    };
    await saveClipViews([...clipViews, view]);
  };

  // Mark clip as watched (explicit play tracking)
  const markAsWatched = async (clip: Clip) => {
    await trackClipView(clip);
    // Optional: could show a confirmation toast
  };

  // Select a clip and automatically track the view
  const selectClipWithTracking = (clipSelection: ClipSelection) => {
    setSelection(clipSelection);
    trackClipView(clipSelection.clip);
  };

  // Wishlist (Want to Try) functions
  const isInWishlist = (clipId: string) => {
    return wishlist.some(w => w.clipId === clipId && w.coupleId === coupleData?.coupleId && w.status === 'want-to-try');
  };

  const toggleWishlist = async (clip: Clip) => {
    if (!saveWishlist || !coupleData?.coupleId) return;

    const existing = wishlist.find(w => w.clipId === clip.id && w.coupleId === coupleData.coupleId);
    if (existing) {
      // Remove from wishlist
      await saveWishlist(wishlist.filter(w => w.id !== existing.id));
    } else {
      // Add to wishlist
      const item: WishlistItem = {
        id: `wish-${Date.now()}`,
        coupleId: coupleData.coupleId,
        clipId: clip.id,
        clipTitle: clip.title,
        addedBy: `${coupleData.hisName} & ${coupleData.herName}`,
        addedAt: now(),
        status: 'want-to-try'
      };
      await saveWishlist([...wishlist, item]);
    }
  };

  // Save couple feedback from onboarding or settings
  const saveFeedback = async (data: { wouldSubmitClips?: 'yes' | 'maybe' | 'no'; productExcitement?: 1 | 2 | 3 | 4 | 5; generalFeedback?: string }) => {
    if (!coupleData?.coupleId) return;
    const updated = {
      ...coupleData,
      wouldSubmitClips: data.wouldSubmitClips || coupleData.wouldSubmitClips,
      productExcitement: data.productExcitement || coupleData.productExcitement,
      generalFeedback: data.generalFeedback !== undefined ? data.generalFeedback : coupleData.generalFeedback,
      feedbackSubmittedAt: now(),
      lastActivityAt: now(),
    };
    await saveCoupleData(updated);
    await saveCoupleByCode(updated);
  };

  const markAsTried = async (clipId: string, data: {
    rating: TriedRatingValue;
    hisThoughts: string;
    herThoughts: string;
    triedAt?: string;
    wouldDoAgain?: 'yes' | 'maybe' | 'no';
    inspiresDifferent?: 'yes' | 'no';
    howDifferent?: string;
    cumVolume?: number;
    cumConsistency?: number;
    cumTaste?: number;
    cumNotes?: string;
  }) => {
    const existing = tried.filter(t => t.clipId !== clipId);
    // Use the provided triedAt date or default to now()
    const triedAtDate = data.triedAt ? new Date(data.triedAt).toISOString() : now();
    const newTried = [...existing, {
      clipId,
      rating: data.rating,
      hisThoughts: data.hisThoughts,
      herThoughts: data.herThoughts,
      triedAt: triedAtDate,
      wouldDoAgain: data.wouldDoAgain,
      inspiresDifferent: data.inspiresDifferent,
      howDifferent: data.howDifferent,
      cumVolume: data.cumVolume,
      cumConsistency: data.cumConsistency,
      cumTaste: data.cumTaste,
      cumNotes: data.cumNotes
    }];
    const updated = { ...coupleData!, tried: newTried, lastActivityAt: now() };
    await saveCoupleData(updated);
    await saveCoupleByCode(updated); // Keep global storage in sync

    // Also update global clips for reviewer visibility
    const updatedClips = clips.map(c => c.id === clipId ? {
      ...c,
      couplesTried: (c.couplesTried || 0) + 1,
      couplesRatings: [...(c.couplesRatings || []), { rating: data.rating, date: triedAtDate }]
    } : c);
    await saveClips(updatedClips);
    setShowTriedModal(null);
    setTriedForm({ rating: '', hisThoughts: '', herThoughts: '', triedAt: new Date().toISOString().split('T')[0], wouldDoAgain: '', inspiresDifferent: '', howDifferent: '', cumVolume: 0, cumConsistency: 0, cumTaste: 0, cumNotes: '' });
  };

  // Clips a spin can land on with the current area/tag filters
  const getSpinPool = () => clips.filter(c => {
    const cat = CATEGORIES.find(cat => cat.id === c.categoryId);
    if (!matchesArea(cat?.area, area)) return false;
    if (tagFilter.length > 0 && !tagFilter.some(tag => c.tags?.includes(tag))) return false;
    if (preferences[c.categoryId] === 'nope') return false;
    return true;
  });

  const spin = () => {
    setSpinning(true);
    setRevealed(false);
    setSelection(null);
    window.scrollTo({ top: 0 });
    let count = 0;
    const interval = setInterval(() => {
      const validClips = getSpinPool();
      if (validClips.length === 0) {
        clearInterval(interval);
        setSpinning(false);
        return;
      }
      const randomClip = validClips[Math.floor(Math.random() * validClips.length)]!;
      const cat = CATEGORIES.find(c => c.id === randomClip.categoryId);
      if (cat) setSelection({ category: cat, clip: randomClip });
      count++;
      if (count >= 20) {
        clearInterval(interval);
        setSpinning(false);
        // Track the final selected clip when spin stops
        trackClipView(randomClip);
        setTimeout(() => setRevealed(true), 300);
      }
    }, 100);
  };

  const selectCategory = (cat: typeof CATEGORIES[number]) => {
    let catClips = getClipsForCategory(cat.id);
    if (tagFilter.length > 0) catClips = catClips.filter(c => tagFilter.some(tag => c.tags?.includes(tag)));
    if (catClips.length === 0) return;
    const randomClip = catClips[Math.floor(Math.random() * catClips.length)]!;
    selectClipWithTracking({ category: cat, clip: randomClip });
    setRevealed(true);
  };

  const toggleTag = (tag: string) => setTagFilter(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  // Onboarding categories
  const onboardingCategories = CATEGORIES.filter(c => clips.some(cl => getClipCategoryIds(cl).includes(c.id))).slice(0, 6);

  // Get only tags that are actually used in clips (filter out empty tags)
  const usedTagIds = new Set(clips.flatMap(clip => clip.tags || []));
  const usedTags = TAGS.filter(tag => usedTagIds.has(tag.id));

  // ---- Date Night layout helpers ----
  const todayKey = format(new Date(), 'yyyy-MM-dd');
  const monthKey = todayKey.slice(0, 7);
  const formatDayKey = (key: string) => {
    const day = parseISO(key);
    return isNaN(day.getTime()) ? key : format(day, 'EEE d MMM');
  };
  const upcomingActivities = scheduledActivities
    .filter(a => !a.completedAt && a.scheduledDate >= todayKey)
    .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
  // First upcoming date whose clip still exists
  const nextActivity = upcomingActivities.find(a => clips.some(c => c.id === a.clipId));
  const nextActivityClip = nextActivity ? clips.find(c => c.id === nextActivity.clipId) : undefined;
  const nextActivityLabel = nextActivity
    ? nextActivity.scheduledDate === todayKey ? 'Planned for tonight' : `Up next · ${formatDayKey(nextActivity.scheduledDate)}`
    : '';
  const datesThisMonth = scheduledActivities.filter(a => a.scheduledDate.startsWith(monthKey)).length;
  const inboxCount = unseenRecommendations.length
    + coupleFollowUps.filter(fu => fu.coupleId === coupleData?.coupleId && fu.status === 'pending').length
    + coupleQuestions.filter(q => q.coupleId === coupleData?.coupleId && q.status === 'pending').length;
  const featuredRec = unseenRecommendations.find(r => clips.some(c => c.id === r.clipId));
  const featuredRecClip = featuredRec ? clips.find(c => c.id === featuredRec.clipId) : undefined;
  const hisProfileMissing = !coupleData?.hisProfile?.size && !coupleData?.hisProfile?.cumIntensity;
  const herProfileMissing = !coupleData?.herProfile?.typicalWetness && !coupleData?.herProfile?.squirtingAbility;
  const spinPoolSize = getSpinPool().length;
  const clipsInArea = (key: string) => clips.filter(c => matchesArea(CATEGORIES.find(cat => cat.id === c.categoryId)?.area, key)).length;
  const showClipView = !!selection && (!isSetup || tab === 'browse' || tab === 'explore');
  const wideLayout = tab === 'calendar' && showMonthCalendar;
  // Browsing uses the full width on desktop; reading and forms stay a comfortable column
  const contentWidth = selection ? 'max-w-3xl lg:max-w-4xl'
    : tab === 'explore' || !isSetup ? 'max-w-2xl lg:max-w-6xl'
    : wideLayout ? 'max-w-4xl lg:max-w-5xl'
    : tab === 'browse' ? 'max-w-2xl lg:max-w-4xl'
    : 'max-w-2xl lg:max-w-3xl';

  const goToTab = (next: DateNiteTab) => {
    setTab(next);
    setSelection(null);
    setRevealed(false);
    setWatchingClip(null);
    window.scrollTo({ top: 0 });
  };

  const openClip = (clip: Clip) => {
    const category = CATEGORIES.find(c => c.id === clip.categoryId);
    if (!category) {
      setWatchingClip(clip);
      return;
    }
    selectClipWithTracking({ category, clip });
    setRevealed(true);
    window.scrollTo({ top: 0 });
  };

  const isPlannedTonight = (clipId: string) =>
    scheduledActivities.some(a => a.clipId === clipId && a.scheduledDate === todayKey && !a.completedAt);

  const scheduleTonight = async (clip: Clip) => {
    if (schedulingTonight) return;
    setSchedulingTonight(true);
    try {
      await handleScheduleActivity({ id: `sched-${Date.now()}`, clipId: clip.id, scheduledDate: todayKey, createdAt: now() });
      goToTab('calendar');
    } finally {
      setSchedulingTonight(false);
    }
  };

  const confirmUpNextDone = async () => {
    if (!nextActivity || !upNextDone) return;
    await handleMarkActivityComplete(nextActivity.id, upNextDone.note.trim() || undefined, upNextDone.clipUrl.trim() || undefined);
    setUpNextDone(null);
  };

  const chipClass = (active: boolean) =>
    `shrink-0 h-11 px-4 rounded-full border text-[14px] transition-colors ${active ? 'bg-action border-action text-ground font-semibold' : 'bg-raised border-line text-ink-2 font-medium hover:text-ink'}`;

  const areaChips = (
    <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-5 px-5">
      {Object.entries(AREAS).map(([key, a]) => (
        <button key={key} onClick={() => setArea(key)} aria-pressed={area === key} className={chipClass(area === key)}>
          {a.emoji} {a.name}
        </button>
      ))}
    </div>
  );

  const tagFilterBlock = usedTags.length > 0 && (
    <div>
      <button
        onClick={() => setShowTagFilter(v => !v)}
        aria-expanded={showTagFilter}
        className="h-9 text-[13px] font-semibold text-ink-2 hover:text-ink flex items-center gap-1.5"
      >
        <SlidersHorizontal className="w-4 h-4" />
        {tagFilter.length > 0 ? `Tags · ${tagFilter.length} on` : 'Filter by tags'}
      </button>
      {showTagFilter && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {usedTags.map(tag => (
            <TagBadge key={tag.id} tag={tag.id} selected={tagFilter.includes(tag.id)} onClick={() => toggleTag(tag.id)} small />
          ))}
          {tagFilter.length > 0 && (
            <button onClick={() => setTagFilter([])} className="h-7 px-2.5 text-[12px] font-semibold text-ink-muted hover:text-ink">Clear</button>
          )}
        </div>
      )}
    </div>
  );

  const spinCard = (
    <section className="bg-surface border border-line rounded-[20px] p-4 space-y-3.5">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[17px] font-bold flex items-center gap-2"><Dice6 className="w-5 h-5 text-action" /> Let fate decide</h2>
        <span className="text-[13px] text-ink-muted">{spinPoolSize} in the pool</span>
      </div>
      {areaChips}
      {tagFilterBlock}
      <button
        onClick={spin}
        disabled={spinning || spinPoolSize === 0}
        className="w-full h-14 rounded-2xl bg-action text-ground text-[17px] font-bold flex items-center justify-center gap-2 hover:brightness-105 disabled:opacity-50"
      >
        <Shuffle className={`w-5 h-5 ${spinning ? 'animate-spin' : ''}`} /> {spinning ? 'Spinning…' : 'Spin for tonight'}
      </button>
    </section>
  );

  return (
    <div className={`min-h-screen bg-ground text-ink ${isSetup ? 'lg:pl-64' : ''}`}>
      <FluidBackground />
      <div className={`relative z-10 ${contentWidth} mx-auto px-5 lg:px-10 pt-5 lg:pt-8 ${isSetup ? 'pb-32 lg:pb-16' : 'pb-12'}`}>
        {/* Top bar */}
        <header className="flex items-center gap-3 mb-6">
          <button
            onClick={onBack}
            aria-label="Back"
            className="w-11 h-11 -ml-1.5 shrink-0 rounded-full flex items-center justify-center text-ink-2 hover:text-ink hover:bg-surface"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          {isSetup ? (
            <>
              <AvatarPair hisName={hisName} herName={herName} />
              <div className="flex-1 min-w-0">
                <p className="text-[15px] font-semibold truncate">{hisName} & {herName}</p>
                <p className="text-[12px] text-ink-muted">{datesThisMonth} date{datesThisMonth === 1 ? '' : 's'} this month</p>
              </div>
              <button
                onClick={() => goToTab('inbox')}
                aria-label={inboxCount > 0 ? `Inbox, ${inboxCount} new` : 'Inbox'}
                className="relative w-11 h-11 shrink-0 rounded-full bg-surface border border-line flex items-center justify-center text-ink-2 hover:text-ink"
              >
                <Bell className="w-5 h-5" />
                {inboxCount > 0 && <span className="absolute top-2 right-2.5 w-2.5 h-2.5 rounded-full bg-her ring-2 ring-surface" />}
              </button>
            </>
          ) : (
            <p className="flex-1 font-display text-[22px] font-medium">Date Night</p>
          )}
        </header>

        {/* Couple setup */}
        {!isSetup && (
          <section className="bg-surface border border-line rounded-3xl p-6 mb-6 text-center">
            <span className="w-14 h-14 mx-auto rounded-2xl bg-her-tint text-her flex items-center justify-center">
              <Heart className="w-7 h-7" />
            </span>
            <h1 className="font-display text-[28px] font-medium mt-4">Make it yours</h1>
            <p className="text-[15px] text-ink-2 mt-2 leading-relaxed">Add your names to track what you’ve tried, save favourites and plan dates together.</p>
            <button
              onClick={() => setShowCoupleSetup(true)}
              className="w-full h-14 mt-5 rounded-2xl bg-action text-ground text-[17px] font-bold flex items-center justify-center gap-2 hover:brightness-105"
            >
              <Users className="w-5 h-5" /> Get started
            </button>
            <button onClick={() => setShowReturningModal(true)} className="mt-2 h-11 text-[14px] font-semibold text-ink-2 hover:text-ink">
              Already have a code?
            </button>
          </section>
        )}

        {/* Us tab */}
        {tab === 'history' && isSetup && (
          <div className="space-y-5">
            <div className="flex items-center gap-3.5">
              <AvatarPair hisName={hisName} herName={herName} size="lg" />
              <div className="min-w-0">
                <h1 className="font-display text-[28px] leading-tight font-medium truncate">{hisName} & {herName}</h1>
                <p className="text-[14px] text-ink-muted">{tried.length} tried · {favorites.length} favourite{favorites.length === 1 ? '' : 's'}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2.5">
              <button onClick={openHisProfileModal} className="bg-surface border border-line rounded-[20px] p-4 text-left hover:border-line-strong">
                <span className="w-10 h-10 rounded-xl bg-him-tint text-him flex items-center justify-center"><User className="w-5 h-5" /></span>
                <span className="block mt-3 text-[15px] font-semibold truncate">{hisName}’s profile</span>
                <span className="block text-[13px] text-ink-muted">{hisProfileMissing ? 'Not filled in yet' : 'View & edit'}</span>
              </button>
              <button onClick={openHerProfileModal} className="bg-surface border border-line rounded-[20px] p-4 text-left hover:border-line-strong">
                <span className="w-10 h-10 rounded-xl bg-her-tint text-her flex items-center justify-center"><Heart className="w-5 h-5" /></span>
                <span className="block mt-3 text-[15px] font-semibold truncate">{herName}’s profile</span>
                <span className="block text-[13px] text-ink-muted">{herProfileMissing ? 'Not filled in yet' : 'View & edit'}</span>
              </button>
            </div>

            <div className="bg-surface border border-line rounded-[20px]">
              <button onClick={() => setShowCoupleSetup(true)} className="w-full flex items-center gap-3 p-4 text-left">
                <Settings className="w-5 h-5 text-ink-2 shrink-0" />
                <span className="flex-1 min-w-0">
                  <span className="block text-[15px] font-semibold">Settings</span>
                  <span className="block text-[13px] text-ink-muted">Names, codes, your clips and feedback</span>
                </span>
                <ChevronRight className="w-5 h-5 text-ink-muted shrink-0" />
              </button>
            </div>

            <div role="group" aria-label="Show" className="grid grid-cols-2 gap-1 p-1 bg-surface border border-line rounded-[14px]">
              <button
                onClick={() => setHistoryTab('tried')}
                aria-pressed={historyTab === 'tried'}
                className={`h-11 rounded-[10px] text-[14px] font-semibold ${historyTab === 'tried' ? 'bg-[#2f2a35] text-ink' : 'text-ink-muted'}`}
              >
                We tried ({tried.length})
              </button>
              <button
                onClick={() => setHistoryTab('favorites')}
                aria-pressed={historyTab === 'favorites'}
                className={`h-11 rounded-[10px] text-[14px] font-semibold ${historyTab === 'favorites' ? 'bg-[#2f2a35] text-ink' : 'text-ink-muted'}`}
              >
                Favourites ({favorites.length})
              </button>
            </div>

            {historyTab === 'tried' && (
              tried.length === 0 ? (
                <EmptyState
                  icon={<Clock className="w-8 h-8 text-action" />}
                  title="Nothing tried yet"
                  description="Open a clip and tap “We tried this” afterwards"
                />
              ) : (
                <div className="space-y-2.5">
                  {tried.map(t => {
                    const clip = clips.find(c => c.id === t.clipId);
                    const cat = CATEGORIES.find(c => c.id === clip?.categoryId);
                    const ratingData = TRIED_RATINGS.find(r => r.value === t.rating);
                    return clip && (
                      <article key={t.clipId} className="bg-surface border border-line rounded-[20px] p-4 space-y-3">
                        <div className="flex items-start gap-3">
                          <span className={`w-11 h-11 rounded-xl flex items-center justify-center text-xl shrink-0 ${areaTone(cat?.area ?? '')}`}>{cat?.emoji ?? '🎬'}</span>
                          <div className="flex-1 min-w-0">
                            <h3 className="text-[15px] font-semibold leading-snug">{clip.title}</h3>
                            <p className="text-[13px] text-ink-muted">{cat ? `${cat.name} · ` : ''}{new Date(t.triedAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          </div>
                          {ratingData && (
                            <span className={`shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r ${ratingData.color} text-white`}>{ratingData.label}</span>
                          )}
                        </div>
                        {(t.hisThoughts || t.herThoughts) && (
                          <div className="grid grid-cols-2 gap-2">
                            {t.hisThoughts && (
                              <div className="bg-him-tint/60 rounded-xl p-2.5">
                                <p className="text-him text-[11px] font-bold mb-0.5">{hisName}</p>
                                <p className="text-ink-2 text-[13px]">{t.hisThoughts}</p>
                              </div>
                            )}
                            {t.herThoughts && (
                              <div className="bg-her-tint/60 rounded-xl p-2.5">
                                <p className="text-her text-[11px] font-bold mb-0.5">{herName}</p>
                                <p className="text-ink-2 text-[13px]">{t.herThoughts}</p>
                              </div>
                            )}
                          </div>
                        )}
                        <button
                          onClick={() => setWatchingClip(clip)}
                          className="h-10 px-3.5 rounded-xl border border-line-strong text-[14px] font-semibold flex items-center gap-1.5 hover:bg-raised"
                        >
                          <Play className="w-4 h-4" /> Watch again
                        </button>
                      </article>
                    );
                  })}
                </div>
              )
            )}

            {historyTab === 'favorites' && (
              favorites.length === 0 ? (
                <EmptyState
                  icon={<Heart className="w-8 h-8 text-her" />}
                  title="No favourites yet"
                  description="Tap the heart on clips you both love"
                />
              ) : (
                <div className="space-y-2.5">
                  {favorites.map(favId => {
                    const clip = clips.find(c => c.id === favId);
                    const cat = CATEGORIES.find(c => c.id === clip?.categoryId);
                    return clip && (
                      <article key={favId} className="bg-surface border border-line rounded-[20px] p-3 flex items-center gap-3">
                        <button onClick={() => setWatchingClip(clip)} className="flex-1 min-w-0 flex items-center gap-3 text-left">
                          <ClipThumb url={clip.link} className="w-14 h-14" showPlay />
                          <span className="min-w-0">
                            <span className="block text-[15px] font-semibold truncate">{clip.title}</span>
                            {cat && <span className="block text-[13px] text-ink-muted truncate">{cat.emoji} {cat.name}</span>}
                            {coupleData?.favoriteNotes?.[favId] && (
                              <span className="block text-[13px] text-ink-2 italic truncate">“{coupleData.favoriteNotes[favId]}”</span>
                            )}
                          </span>
                        </button>
                        <button
                          onClick={() => toggleFavorite(favId)}
                          aria-label={`Remove ${clip.title} from favourites`}
                          className="w-11 h-11 shrink-0 rounded-xl text-ink-muted hover:text-her hover:bg-raised flex items-center justify-center"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </article>
                    );
                  })}
                </div>
              )
            )}
          </div>
        )}

        {/* Plan tab */}
        {tab === 'calendar' && isSetup && (
          <div className="space-y-4">
            <div className="flex items-end justify-between gap-3">
              <div>
                <h1 className="font-display text-[32px] leading-tight font-medium">Plan</h1>
                <p className="text-[14px] text-ink-muted">
                  {upcomingActivities.length > 0 ? `${upcomingActivities.length} date${upcomingActivities.length === 1 ? '' : 's'} coming up` : 'Nothing planned yet'}
                </p>
              </div>
              <button
                onClick={() => { setSelectedCalendarDate(undefined); setShowScheduleModal(true); }}
                className="h-11 px-4 shrink-0 rounded-xl bg-action text-ground text-[14px] font-bold flex items-center gap-1.5 hover:brightness-105"
              >
                <Plus className="w-4 h-4" /> Add date
              </button>
            </div>

            <div className="bg-surface border border-line rounded-[20px] p-4">
              <WeekStrip
                activities={scheduledActivities}
                onSelectDay={day => { setSelectedCalendarDate(day); setShowScheduleModal(true); }}
              />
            </div>

            {nextActivity && nextActivityClip && (
              <article className="bg-surface border border-line rounded-[20px] p-4 space-y-3">
                <span className="block text-[11px] font-bold tracking-[0.06em] uppercase text-action">{nextActivityLabel}</span>
                <div className="flex items-center gap-3.5">
                  <ClipThumb url={nextActivityClip.link} className="w-14 h-14" />
                  <div className="min-w-0">
                    <h2 className="text-[17px] font-semibold leading-snug">{nextActivityClip.title}</h2>
                    <p className="text-[13px] text-ink-muted truncate">
                      {CATEGORIES.find(c => c.id === nextActivityClip.categoryId)?.name}
                      {nextActivity.createdBy?.type === 'reviewer' && nextActivity.createdBy.name ? ` · planned by ${nextActivity.createdBy.name}` : ''}
                    </p>
                  </div>
                </div>
                {upNextDone ? (
                  <div className="space-y-2.5 pt-1">
                    <label className="block">
                      <span className="block text-[13px] font-semibold text-ink-2 mb-1">How was it? (optional)</span>
                      <textarea
                        value={upNextDone.note}
                        onChange={e => setUpNextDone({ ...upNextDone, note: e.target.value })}
                        placeholder="We loved this one…"
                        className="w-full h-20 resize-none rounded-xl bg-raised border border-line px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-muted"
                        autoFocus
                      />
                    </label>
                    <label className="block">
                      <span className="block text-[13px] font-semibold text-ink-2 mb-1">Made your own clip? Share the link (optional)</span>
                      <input
                        type="url"
                        value={upNextDone.clipUrl}
                        onChange={e => setUpNextDone({ ...upNextDone, clipUrl: e.target.value })}
                        placeholder="https://redgifs.com/watch/…"
                        className="w-full h-11 rounded-xl bg-raised border border-line px-3.5 text-[15px] text-ink placeholder:text-ink-muted"
                      />
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <button onClick={confirmUpNextDone} className="h-11 rounded-xl bg-action text-ground text-[14px] font-bold flex items-center justify-center gap-1.5">
                        <Check className="w-4 h-4" /> Mark as done
                      </button>
                      <button onClick={() => setUpNextDone(null)} className="h-11 rounded-xl text-ink-2 text-[14px] font-semibold hover:text-ink hover:bg-raised">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    <button onClick={() => setWatchingClip(nextActivityClip)} className="h-11 rounded-xl bg-action text-ground text-[14px] font-bold flex items-center justify-center gap-1.5">
                      <Play className="w-4 h-4 fill-current" /> Watch
                    </button>
                    <button onClick={() => handleRescheduleActivity(nextActivity.id)} className="h-11 rounded-xl border border-line-strong text-[14px] font-semibold hover:bg-raised">
                      Move
                    </button>
                    <button onClick={() => setUpNextDone({ note: '', clipUrl: '' })} className="h-11 rounded-xl text-ink-2 text-[14px] font-semibold hover:text-ink hover:bg-raised">
                      Done
                    </button>
                  </div>
                )}
              </article>
            )}

            {coupleData?.managedByReviewerId && pickForUsList.length > 0 && (
              <section className="bg-surface border border-line rounded-[20px] p-4 space-y-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h2 className="text-[15px] font-bold">Shortlist for {coupleData.managedByReviewerName || 'your coach'}</h2>
                  <span className="text-[13px] text-ink-muted">{pickForUsList.length} clip{pickForUsList.length === 1 ? '' : 's'}</span>
                </div>
                <ul className="space-y-1.5">
                  {pickForUsList.map(item => (
                    <li key={item.clipId} className="flex items-center gap-2 text-[14px] text-ink-2">
                      <Target className="w-4 h-4 text-action shrink-0" />
                      <span className="flex-1 truncate">{item.clipTitle}</span>
                      <button onClick={() => removeFromPickForUs(item.clipId)} aria-label={`Remove ${item.clipTitle}`} className="w-9 h-9 rounded-lg text-ink-muted hover:text-ink flex items-center justify-center">
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
                <button onClick={submitPickForUs} className="w-full h-12 rounded-[14px] border border-line-strong text-[15px] font-semibold hover:bg-raised">
                  Ask {coupleData.managedByReviewerName || 'your coach'} to pick one
                </button>
              </section>
            )}

            <UpcomingActivities
              scheduledActivities={scheduledActivities}
              clips={clips}
              onViewClip={clip => setWatchingClip(clip)}
              onMarkComplete={handleMarkActivityComplete}
              onReschedule={handleRescheduleActivity}
              onDelete={handleDeleteActivity}
              showCompleted
            />

            <button
              onClick={() => setShowMonthCalendar(v => !v)}
              aria-expanded={showMonthCalendar}
              className="w-full h-12 rounded-[14px] border border-line-strong text-[15px] font-semibold flex items-center justify-center gap-2 hover:bg-surface"
            >
              <Calendar className="w-4 h-4" /> {showMonthCalendar ? 'Hide month view' : 'Month view · drag clips onto days'}
            </button>

            {showMonthCalendar && (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCorners}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
              >
                <div className="flex flex-col md:flex-row gap-4">
                  <ClipSidebar
                    clips={clips}
                    scheduledActivities={scheduledActivities}
                    isCollapsed={sidebarCollapsed}
                    onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
                    onScheduleViaModal={() => {
                      setSelectedCalendarDate(undefined);
                      setShowScheduleModal(true);
                    }}
                    onScheduleClip={(clip) => {
                      const category = CATEGORIES.find(c => c.id === clip.categoryId);
                      if (category) {
                        setSelection({ category, clip });
                      }
                      setShowScheduleModal(true);
                    }}
                    // Pick For Us support for managed couples
                    pickForUsList={coupleData?.managedByReviewerId ? pickForUsList : undefined}
                    onAddToPickForUs={coupleData?.managedByReviewerId ? addToPickForUs : undefined}
                    onRemoveFromPickForUs={coupleData?.managedByReviewerId ? removeFromPickForUs : undefined}
                    onSubmitPickForUs={coupleData?.managedByReviewerId && pickForUsList.length > 0 ? submitPickForUs : undefined}
                    reviewerName={coupleData?.managedByReviewerName}
                  />
                  <div className="flex-1 min-w-0">
                    <DragDropCalendarView
                      scheduledActivities={scheduledActivities}
                      clips={clips}
                      onActivityClick={(activity) => {
                        const clip = clips.find(c => c.id === activity.clipId);
                        if (clip) setWatchingClip(clip);
                      }}
                    />
                  </div>
                </div>

                <DragOverlay>
                  {activeClip && <ClipDragPreview clip={activeClip} />}
                  {activeActivity && (
                    <ActivityDragPreview clip={activeActivity.clip} />
                  )}
                </DragOverlay>
              </DndContext>
            )}
          </div>
        )}

        {/* Inbox Tab */}
        {tab === 'inbox' && isSetup && (
          <div className="space-y-4">
            <div>
              <h1 className="font-display text-[32px] leading-tight font-medium">Inbox</h1>
              <p className="text-[14px] text-ink-muted">
                {inboxCount > 0 ? `${inboxCount} thing${inboxCount === 1 ? ' needs' : 's need'} you` : 'All caught up'}
              </p>
            </div>

            {/* Recommendations Section */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                💌 Friend Recommendations
                {recommendations.filter(r => !r.seen).length > 0 && (
                  <span className="px-2 py-0.5 bg-pink-500 rounded-full text-white text-xs font-bold">
                    {recommendations.filter(r => !r.seen).length} new
                  </span>
                )}
              </h3>
              {recommendations.length === 0 ? (
                <div className="text-center py-6 text-zinc-500">
                  <Gift className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No recommendations yet</p>
                  <p className="text-xs mt-1">Share your code <span className="font-mono text-lime-400">{coupleData?.coupleId}</span> with friends!</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {recommendations.slice().reverse().map(rec => {
                    const clip = clips.find(c => c.id === rec.clipId);
                    if (!clip) return null;
                    const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                    return (
                      <div key={rec.id}>
                        <button
                          onClick={async () => {
                            // Mark as seen
                            if (!rec.seen) {
                              const updated = recommendations.map(r =>
                                r.id === rec.id ? { ...r, seen: true } : r
                              );
                              await saveRecommendations(updated);
                            }
                            // Open clip in video modal
                            setWatchingClip(clip);
                          }}
                          className={`w-full text-left p-3 rounded-lg ${rec.seen ? 'bg-zinc-700/50' : 'bg-pink-500/10 border border-pink-500/30'}`}
                        >
                          <div className="flex items-start gap-3">
                            <div
                              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                              style={{ backgroundColor: cat?.color || '#666' }}
                            >
                              <span className="text-sm">{cat?.emoji}</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className={`font-medium truncate ${rec.seen ? 'text-zinc-300' : 'text-white'}`}>{clip.title}</p>
                              <p className="text-xs text-zinc-500">
                                From <span className="text-pink-400">{rec.fromName}</span> · {new Date(rec.createdAt).toLocaleDateString()}
                              </p>
                              {rec.message && (
                                <p className="text-xs text-zinc-400 mt-1 italic">"{rec.message}"</p>
                              )}
                            </div>
                            {!rec.seen && (
                              <span className="w-2 h-2 bg-pink-500 rounded-full shrink-0 mt-2" />
                            )}
                          </div>
                        </button>
                        {/* Reply to recommendation */}
                        {rec.coupleReply ? (
                          <div className="mt-2 bg-zinc-700/50 rounded p-2">
                            <p className="text-xs text-zinc-500">Your reply:</p>
                            <p className="text-sm text-zinc-300">{rec.coupleReply}</p>
                          </div>
                        ) : (
                          <div className="mt-2 flex gap-2">
                            <input
                              type="text"
                              id={`rec-reply-${rec.id}`}
                              placeholder="Reply to this recommendation..."
                              className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs placeholder-zinc-500"
                              onKeyDown={async (e) => {
                                if (e.key === 'Enter') {
                                  const input = e.target as HTMLInputElement;
                                  if (!input.value.trim()) return;
                                  const updated = { ...rec, coupleReply: input.value.trim(), coupleRepliedAt: new Date().toISOString() };
                                  await d1Upsert('couple_recommendations', updated);
                                  await saveRecommendations(recommendations.map(r => r.id === rec.id ? updated : r));
                                  input.value = '';
                                }
                              }}
                            />
                            <button
                              onClick={async () => {
                                const input = document.getElementById(`rec-reply-${rec.id}`) as HTMLInputElement;
                                if (!input?.value.trim()) return;
                                const updated = { ...rec, coupleReply: input.value.trim(), coupleRepliedAt: new Date().toISOString() };
                                await d1Upsert('couple_recommendations', updated);
                                await saveRecommendations(recommendations.map(r => r.id === rec.id ? updated : r));
                                input.value = '';
                              }}
                              className="px-2 py-1 bg-lime-500/20 text-lime-400 rounded text-xs"
                            >
                              Reply
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Wishlist Section */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <h3 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                ⭐ Want to Try ({wishlist.filter(w => w.coupleId === coupleData?.coupleId && w.status === 'want-to-try').length})
              </h3>
              {wishlist.filter(w => w.coupleId === coupleData?.coupleId && w.status === 'want-to-try').length === 0 ? (
                <div className="text-center py-6 text-zinc-500">
                  <Star className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No clips in your wishlist</p>
                  <p className="text-xs mt-1">Star clips to add them here!</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {wishlist.filter(w => w.coupleId === coupleData?.coupleId && w.status === 'want-to-try').map(item => {
                    const clip = clips.find(c => c.id === item.clipId);
                    if (!clip) return null;
                    const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                    return (
                      <button
                        key={item.id}
                        onClick={() => setWatchingClip(clip)}
                        className="w-full text-left p-3 rounded-lg bg-zinc-700/50 hover:bg-zinc-700"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                            style={{ backgroundColor: cat?.color || '#666' }}
                          >
                            <span className="text-sm">{cat?.emoji}</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-zinc-300 font-medium truncate">{clip.title}</p>
                            <p className="text-xs text-zinc-500">Added {new Date(item.addedAt).toLocaleDateString()}</p>
                          </div>
                          <Star className="w-4 h-4 text-amber-400 fill-amber-400 shrink-0" />
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* AI Follow-up Questions */}
            {(() => {
              const myFollowUps = coupleFollowUps.filter(fu => fu.coupleId === coupleData?.coupleId);
              const pendingFollowUps = myFollowUps.filter(fu => fu.status === 'pending');
              const answeredFollowUps = myFollowUps.filter(fu => fu.status === 'answered');

              if (myFollowUps.length === 0) return null;

              return (
                <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    🤖 Quick Questions
                    {pendingFollowUps.length > 0 && (
                      <span className="px-2 py-0.5 bg-amber-500 text-white text-xs rounded-full font-bold">
                        {pendingFollowUps.length} to answer
                      </span>
                    )}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-3">
                    These questions help us understand you better and improve recommendations.
                  </p>
                  <p className="text-xs mb-4">
                    🔒 <span className="text-zinc-400">Your answers are private and help personalize your experience.</span>
                  </p>

                  <div className="space-y-4">
                    {/* Pending questions first */}
                    {pendingFollowUps.map(fu => {
                      return (
                        <div key={fu.id} className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                              <span className="text-lg">❓</span>
                            </div>
                            <div className="flex-1 min-w-0">
                              {fu.context && (
                                <p className="text-amber-400 text-xs mb-1">{fu.context}</p>
                              )}
                              <p className="text-white mb-3">{fu.question}</p>
                              {renderLinkedQuestionClips(fu)}
                              <textarea
                                id={`couple-followup-${fu.id}`}
                                placeholder="Your answer..."
                                className="w-full bg-zinc-800 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm resize-none focus:outline-none focus:border-amber-500"
                                rows={3}
                                value={followUpAnswers[fu.id] || ''}
                                onChange={e => setFollowUpAnswers(prev => ({ ...prev, [fu.id]: e.target.value }))}
                              />

                              {/* Share links section */}
                              {(fu.context === 'Reviewer clip request' || fu.question.toLowerCase().includes('link') || fu.question.toLowerCase().includes('clip')) && (
                                <div className="mt-2 bg-zinc-900/50 rounded-lg p-3 border border-zinc-700">
                                  <p className="text-zinc-400 text-xs mb-2 flex items-center gap-1">
                                    <LinkIcon className="w-3 h-3" /> Share links (RedGifs or Google Drive)
                                  </p>
                                  {/* Already added links */}
                                  {(followUpLinks[fu.id] || []).map((link, i) => (
                                    <div key={i} className="flex items-center gap-2 mb-1">
                                      <span className="text-lime-400 text-xs truncate flex-1">{link.label || link.url}</span>
                                      <button
                                        onClick={() => setFollowUpLinks(prev => ({
                                          ...prev,
                                          [fu.id]: (prev[fu.id] || []).filter((_, idx) => idx !== i)
                                        }))}
                                        className="text-red-400 hover:text-red-300 text-xs"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ))}
                                  {/* Add link input */}
                                  <div className="flex gap-1">
                                    <input
                                      type="text"
                                      placeholder="Paste a link..."
                                      className="flex-1 bg-zinc-800 border border-zinc-600 rounded px-2 py-1.5 text-white text-xs focus:outline-none focus:border-lime-500"
                                      value={followUpLinkInput[fu.id] || ''}
                                      onChange={e => setFollowUpLinkInput(prev => ({ ...prev, [fu.id]: e.target.value }))}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') {
                                          const url = followUpLinkInput[fu.id]?.trim();
                                          if (!url) return;
                                          setFollowUpLinks(prev => ({
                                            ...prev,
                                            [fu.id]: [...(prev[fu.id] || []), { url, label: url.includes('redgifs') ? 'RedGifs clip' : url.includes('drive.google') ? 'Google Drive video' : url }]
                                          }));
                                          setFollowUpLinkInput(prev => ({ ...prev, [fu.id]: '' }));
                                        }
                                      }}
                                    />
                                    <button
                                      onClick={() => {
                                        const url = followUpLinkInput[fu.id]?.trim();
                                        if (!url) return;
                                        setFollowUpLinks(prev => ({
                                          ...prev,
                                          [fu.id]: [...(prev[fu.id] || []), { url, label: url.includes('redgifs') ? 'RedGifs clip' : url.includes('drive.google') ? 'Google Drive video' : url }]
                                        }));
                                        setFollowUpLinkInput(prev => ({ ...prev, [fu.id]: '' }));
                                      }}
                                      disabled={!followUpLinkInput[fu.id]?.trim()}
                                      className="px-2 py-1.5 bg-lime-500/20 hover:bg-lime-500/30 text-lime-400 rounded text-xs font-medium disabled:opacity-40"
                                    >
                                      + Add
                                    </button>
                                  </div>
                                </div>
                              )}

                              <div className="flex gap-2 mt-2">
                                <button
                                  onClick={async () => {
                                    const answer = followUpAnswers[fu.id]?.trim();
                                    const links = followUpLinks[fu.id] || [];
                                    if ((!answer && links.length === 0) || !saveCoupleFollowUps) return;

                                    // Mark this question as answered
                                    let updated = coupleFollowUps.map(f =>
                                      f.id === fu.id
                                        ? {
                                            ...f,
                                            answer: answer || (links.length > 0 ? `Shared ${links.length} link${links.length !== 1 ? 's' : ''}` : ''),
                                            sharedLinks: links.length > 0 ? links : undefined,
                                            answeredAt: now(),
                                            status: 'answered' as const,
                                            answeredBy: 'both' as const
                                          }
                                        : f
                                    );

                                    // Check if there's a next question in sequence to activate
                                    if (fu.sequenceId && typeof fu.sequenceOrder === 'number') {
                                      const currentOrder = fu.sequenceOrder;
                                      const nextInSequence = updated.find(f =>
                                        f.coupleId === fu.coupleId &&
                                        f.sequenceId === fu.sequenceId &&
                                        f.sequenceOrder === currentOrder + 1 &&
                                        f.status === 'queued'
                                      );
                                      if (nextInSequence) {
                                        updated = updated.map(f =>
                                          f.id === nextInSequence.id
                                            ? { ...f, status: 'pending' as const }
                                            : f
                                        );
                                      }
                                    }

                                    await saveCoupleFollowUps(updated);
                                    setFollowUpAnswers(prev => {
                                      const newAnswers = { ...prev };
                                      delete newAnswers[fu.id];
                                      return newAnswers;
                                    });
                                    setFollowUpLinks(prev => {
                                      const newLinks = { ...prev };
                                      delete newLinks[fu.id];
                                      return newLinks;
                                    });
                                  }}
                                  className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-zinc-900 rounded-lg text-sm font-medium"
                                  disabled={!followUpAnswers[fu.id]?.trim() && !(followUpLinks[fu.id]?.length)}
                                >
                                  Submit
                                </button>
                                <button
                                  onClick={async () => {
                                    if (!saveCoupleFollowUps) return;
                                    const updated = coupleFollowUps.map(f =>
                                      f.id === fu.id ? { ...f, status: 'dismissed' as const } : f
                                    );
                                    await saveCoupleFollowUps(updated);
                                  }}
                                  className="px-4 py-2 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm"
                                >
                                  Skip
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}

                    {/* Answered questions (editable) */}
                    {answeredFollowUps.length > 0 && (
                      <div className="border-t border-zinc-700 pt-4">
                        <p className="text-zinc-500 text-xs mb-2">Previously answered ({answeredFollowUps.length})</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {answeredFollowUps.map(fu => {
                            const editKey = `edit-fu-${fu.id}`;
                            const isEditing = followUpAnswers[editKey] !== undefined;
                            return (
                            <div key={fu.id} className="bg-zinc-700/30 rounded-lg p-3">
                              <p className="text-zinc-400 text-xs mb-1">{fu.question}</p>
                              {renderLinkedQuestionClips(fu)}
                              {isEditing ? (
                                <>
                                  <textarea
                                    className="w-full bg-zinc-800 border border-amber-500/30 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none mb-2"
                                    value={followUpAnswers[editKey]}
                                    onChange={e => setFollowUpAnswers(prev => ({ ...prev, [editKey]: e.target.value }))}
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={async () => {
                                        if (!saveCoupleFollowUps) return;
                                        const newAnswer = followUpAnswers[editKey]?.trim();
                                        if (!newAnswer) return;
                                        const updated = coupleFollowUps.map(f =>
                                          f.id === fu.id ? { ...f, answer: newAnswer, answeredAt: now() } : f
                                        );
                                        await saveCoupleFollowUps(updated);
                                        setFollowUpAnswers(prev => {
                                          const next = { ...prev };
                                          delete next[editKey];
                                          return next;
                                        });
                                      }}
                                      className="px-3 py-1 rounded-lg text-xs font-medium bg-amber-500 hover:bg-amber-400 text-zinc-900"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setFollowUpAnswers(prev => {
                                        const next = { ...prev };
                                        delete next[editKey];
                                        return next;
                                      })}
                                      className="px-3 py-1 rounded-lg text-xs font-medium bg-zinc-600 hover:bg-zinc-500 text-zinc-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-zinc-300 text-sm">{fu.answer}</p>
                                  <button
                                    onClick={() => setFollowUpAnswers(prev => ({ ...prev, [editKey]: fu.answer || '' }))}
                                    className="text-amber-400 text-xs hover:text-amber-300 whitespace-nowrap shrink-0"
                                  >
                                    Edit
                                  </button>
                                </div>
                              )}
                            </div>
                          );})}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Reviewer Questions - Questions from your reviewer/admin */}
            {(() => {
              const myQuestions = coupleQuestions.filter(q => q.coupleId === coupleData?.coupleId);
              // Show ALL pending questions including nested replies
              const pendingQuestions = myQuestions.filter(q => q.status === 'pending');
              const answeredQuestions = myQuestions.filter(q => q.status === 'answered' && !q.parentId);

              // Helper to get full thread chain (oldest first)
              const getThreadChain = (parentId: string | undefined): typeof myQuestions => {
                const chain: typeof myQuestions = [];
                let currentId = parentId;
                while (currentId) {
                  const found = myQuestions.find(q => q.id === currentId);
                  if (!found) break;
                  chain.unshift(found);
                  currentId = found.parentId;
                }
                return chain;
              };

              if (myQuestions.length === 0) return null;

              return (
                <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    💬 Questions from Your Reviewer
                    {pendingQuestions.length > 0 && (
                      <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full font-bold">
                        {pendingQuestions.length} to answer
                      </span>
                    )}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    Your reviewer has some questions to help personalize your experience.
                  </p>

                  <div className="space-y-3">
                    {/* Pending questions */}
                    {pendingQuestions.map(q => {
                      const threadChain = getThreadChain(q.parentId);
                      return (
                      <div key={q.id} className="bg-zinc-800/70 rounded-xl p-4">
                        {/* Thread context - full conversation history */}
                        {threadChain.length > 0 && (
                          <div className="mb-3 pb-3 border-b border-purple-500/20">
                            <p className="text-zinc-500 text-xs mb-1">Conversation history:</p>
                            <div className="space-y-2 max-h-48 overflow-y-auto">
                              {threadChain.map((t, i) => (
                                <div key={t.id} className="bg-zinc-700/50 rounded-lg p-2 text-sm" style={{ marginLeft: `${i * 8}px` }}>
                                  <p className="text-zinc-400 italic">Q: {t.question}</p>
                                  {t.answer && (
                                    <p className="text-zinc-300 mt-1">A: {t.answer}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <div className="flex items-start justify-between mb-2">
                          <span className="text-purple-400 text-xs font-medium">{q.fromReviewerName}</span>
                          {q.category && (
                            <span className="text-zinc-500 text-xs">Topic: {q.category}</span>
                          )}
                        </div>
                        <p className="text-white mb-3">Q: {q.question}</p>
                        {renderLinkedQuestionClips(q)}
                        <textarea
                          placeholder="Type your answer..."
                          className="w-full bg-zinc-700 border border-purple-500/30 rounded-lg px-3 py-2 text-white placeholder-zinc-500 text-sm h-20 resize-none mb-2"
                          value={coupleQuestionAnswers[q.id] || ''}
                          onChange={e => setCoupleQuestionAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={async () => {
                              if (!saveCoupleQuestions) return;
                              const answer = coupleQuestionAnswers[q.id];
                              if (!answer?.trim()) return;
                              const updated = coupleQuestions.map(cq =>
                                cq.id === q.id ? {
                                  ...cq,
                                  status: 'answered' as const,
                                  answer: answer.trim(),
                                  answeredAt: now(),
                                  answeredBy: coupleData?.hisName || coupleData?.herName || 'Couple'
                                } : cq
                              );
                              await saveCoupleQuestions(updated);
                              setCoupleQuestionAnswers(prev => {
                                const next = { ...prev };
                                delete next[q.id];
                                return next;
                              });
                            }}
                            disabled={!coupleQuestionAnswers[q.id]?.trim()}
                            className="flex-1 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 bg-purple-500 hover:bg-purple-600 text-white"
                          >
                            Send Answer
                          </button>
                        </div>
                      </div>
                    );})}

                    {/* Answered questions (editable) */}
                    {answeredQuestions.length > 0 && (
                      <div className="border-t border-purple-500/20 pt-4">
                        <p className="text-zinc-500 text-xs mb-2">Previously answered ({answeredQuestions.length})</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {answeredQuestions.map(q => {
                            const isEditing = coupleQuestionAnswers[`edit-${q.id}`] !== undefined;
                            return (
                            <div key={q.id} className="bg-zinc-700/30 rounded-lg p-3">
                              <p className="text-zinc-400 text-xs mb-1">Q: {q.question}</p>
                              {renderLinkedQuestionClips(q)}
                              {isEditing ? (
                                <>
                                  <textarea
                                    className="w-full bg-zinc-700 border border-purple-500/30 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none mb-2"
                                    value={coupleQuestionAnswers[`edit-${q.id}`]}
                                    onChange={e => setCoupleQuestionAnswers(prev => ({ ...prev, [`edit-${q.id}`]: e.target.value }))}
                                  />
                                  <div className="flex gap-2">
                                    <button
                                      onClick={async () => {
                                        if (!saveCoupleQuestions) return;
                                        const newAnswer = coupleQuestionAnswers[`edit-${q.id}`];
                                        if (!newAnswer?.trim()) return;
                                        const updated = coupleQuestions.map(cq =>
                                          cq.id === q.id ? { ...cq, answer: newAnswer.trim(), answeredAt: now() } : cq
                                        );
                                        await saveCoupleQuestions(updated);
                                        setCoupleQuestionAnswers(prev => {
                                          const next = { ...prev };
                                          delete next[`edit-${q.id}`];
                                          return next;
                                        });
                                      }}
                                      className="px-3 py-1 rounded-lg text-xs font-medium bg-purple-500 hover:bg-purple-600 text-white"
                                    >
                                      Save
                                    </button>
                                    <button
                                      onClick={() => setCoupleQuestionAnswers(prev => {
                                        const next = { ...prev };
                                        delete next[`edit-${q.id}`];
                                        return next;
                                      })}
                                      className="px-3 py-1 rounded-lg text-xs font-medium bg-zinc-600 hover:bg-zinc-500 text-zinc-300"
                                    >
                                      Cancel
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <div className="flex items-start justify-between gap-2">
                                  <p className="text-zinc-300 text-sm">A: {q.answer}</p>
                                  <button
                                    onClick={() => setCoupleQuestionAnswers(prev => ({ ...prev, [`edit-${q.id}`]: q.answer || '' }))}
                                    className="text-purple-400 text-xs hover:text-purple-300 whitespace-nowrap shrink-0"
                                  >
                                    Edit
                                  </button>
                                </div>
                              )}
                            </div>
                          );})}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Clip Suggestions - Rate clips curated for you */}
            {(() => {
              const mySuggestions = clipSuggestions.filter(cs => cs.coupleId === coupleData?.coupleId);
              const pendingSuggestions = mySuggestions.filter(cs => cs.status === 'pending' || cs.status === 'partial');

              if (mySuggestions.length === 0) return null;

              return (
                <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/30 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
                    🎬 Clips to Rate
                    {pendingSuggestions.length > 0 && (
                      <span className="px-2 py-0.5 bg-pink-500 text-white text-xs rounded-full font-bold">
                        {pendingSuggestions.reduce((acc, cs) => acc + (cs.clips || []).filter(c => !c.rating).length, 0)} to rate
                      </span>
                    )}
                  </h3>
                  <p className="text-zinc-400 text-sm mb-4">
                    We've picked these clips based on your interests. Rate them to help us learn your taste!
                  </p>

                  <div className="space-y-6">
                    {pendingSuggestions.map(cs => (
                      <div key={cs.id} className="bg-zinc-800/50 rounded-xl p-4">
                        {cs.context && (
                          <p className="text-pink-400 text-sm mb-3">{cs.context}</p>
                        )}
                        <div className="space-y-4">
                          {(cs.clips || []).map((clip, idx) => {
                            const response = clipSuggestionResponses[clip.id] || {};
                            const isRated = clip.rating !== undefined;

                            return (
                              <div key={clip.id} className={`bg-zinc-900/50 rounded-lg p-3 ${isRated ? 'opacity-60' : ''}`}>
                                <div className="flex items-start gap-3">
                                  <span className="text-pink-400 font-bold text-lg">{idx + 1}</span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium mb-1">{clip.title}</p>
                                    {clip.description && (
                                      <p className="text-zinc-500 text-xs mb-2">{clip.description}</p>
                                    )}

                                    {/* Inline video player */}
                                    <div className="mb-3 rounded-lg overflow-hidden">
                                      <VideoPlayer url={clip.url} />
                                    </div>

                                    {isRated ? (
                                      <div className="mt-2 flex items-center gap-2">
                                        <div className="flex">
                                          {[1,2,3,4,5].map(s => (
                                            <Star key={s} className={`w-4 h-4 ${s <= clip.rating! ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} />
                                          ))}
                                        </div>
                                        {clip.interested !== undefined && (
                                          <span className={`text-xs px-2 py-0.5 rounded ${clip.interested ? 'bg-lime-500/20 text-lime-400' : 'bg-zinc-700 text-zinc-400'}`}>
                                            {clip.interested ? 'Would try' : 'Not for us'}
                                          </span>
                                        )}
                                        {clip.comment && (
                                          <span className="text-zinc-500 text-xs italic">"{clip.comment}"</span>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="mt-3 space-y-2">
                                        {/* Star rating */}
                                        <div className="flex items-center gap-2">
                                          <span className="text-zinc-400 text-xs">Rating:</span>
                                          <div className="flex gap-1">
                                            {[1,2,3,4,5].map(s => (
                                              <button
                                                key={s}
                                                onClick={() => setClipSuggestionResponses(prev => ({
                                                  ...prev,
                                                  [clip.id]: { ...prev[clip.id], rating: s }
                                                }))}
                                                className="p-1 hover:scale-110 transition-transform"
                                              >
                                                <Star className={`w-5 h-5 ${(response.rating || 0) >= s ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} />
                                              </button>
                                            ))}
                                          </div>
                                        </div>

                                        {/* Would try toggle */}
                                        <div className="flex items-center gap-2">
                                          <span className="text-zinc-400 text-xs">Would try?</span>
                                          <button
                                            onClick={() => setClipSuggestionResponses(prev => ({
                                              ...prev,
                                              [clip.id]: { ...prev[clip.id], interested: true }
                                            }))}
                                            className={`px-2 py-1 rounded text-xs transition-colors ${response.interested === true ? 'bg-lime-500/30 text-lime-400' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'}`}
                                          >
                                            Yes!
                                          </button>
                                          <button
                                            onClick={() => setClipSuggestionResponses(prev => ({
                                              ...prev,
                                              [clip.id]: { ...prev[clip.id], interested: false }
                                            }))}
                                            className={`px-2 py-1 rounded text-xs transition-colors ${response.interested === false ? 'bg-zinc-600 text-zinc-300' : 'bg-zinc-700 text-zinc-400 hover:bg-zinc-600'}`}
                                          >
                                            Not for us
                                          </button>
                                        </div>

                                        {/* Comment */}
                                        <input
                                          type="text"
                                          placeholder="Any thoughts? (optional)"
                                          value={response.comment || ''}
                                          onChange={e => setClipSuggestionResponses(prev => ({
                                            ...prev,
                                            [clip.id]: { ...prev[clip.id], comment: e.target.value }
                                          }))}
                                          className="w-full bg-zinc-800 border border-zinc-700 rounded px-2 py-1.5 text-white text-sm placeholder-zinc-500"
                                        />

                                        {/* Submit button */}
                                        {response.rating && (
                                          <button
                                            onClick={async () => {
                                              if (!saveClipSuggestions || !response.rating) return;

                                              // Update the clip in the suggestion
                                              const updated = clipSuggestions.map(s => {
                                                if (s.id !== cs.id) return s;
                                                const updatedClips = (s.clips || []).map(c => {
                                                  if (c.id !== clip.id) return c;
                                                  return {
                                                    ...c,
                                                    rating: response.rating,
                                                    comment: response.comment,
                                                    interested: response.interested,
                                                    respondedAt: now()
                                                  };
                                                });
                                                // Check if all clips are now rated
                                                const allRated = updatedClips.every(c => c.rating !== undefined);
                                                return {
                                                  ...s,
                                                  clips: updatedClips,
                                                  status: allRated ? 'completed' as const : 'partial' as const,
                                                  completedAt: allRated ? now() : undefined
                                                };
                                              });

                                              await saveClipSuggestions(updated);
                                              // Clear local response state for this clip
                                              setClipSuggestionResponses(prev => {
                                                const { [clip.id]: _, ...rest } = prev;
                                                return rest;
                                              });
                                            }}
                                            className="w-full px-3 py-2 bg-pink-500/20 hover:bg-pink-500/30 text-pink-400 rounded-lg text-sm font-medium transition-colors"
                                          >
                                            Submit Rating
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <p className="text-zinc-500 text-xs mt-3">
                          {(cs.clips || []).filter(c => c.rating).length}/{(cs.clips || []).length} rated
                        </p>
                      </div>
                    ))}

                    {/* Completed suggestions (collapsed) */}
                    {mySuggestions.filter(cs => cs.status === 'completed').length > 0 && (
                      <div className="border-t border-zinc-700 pt-4">
                        <p className="text-zinc-500 text-xs mb-2">
                          Previously completed ({mySuggestions.filter(cs => cs.status === 'completed').length} sets)
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Your Reviewer */}
            {coupleData?.managedByReviewerName && (
              <div className="bg-gradient-to-r from-lime-500/10 to-green-500/10 border border-lime-500/30 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-lime-400 mb-2 flex items-center gap-2">
                  👤 Your Reviewer
                </h3>
                <p className="text-white font-medium">{coupleData.managedByReviewerName}</p>
                <p className="text-zinc-400 text-xs mt-1">
                  Connected {coupleData.managedByAcceptedAt ? new Date(coupleData.managedByAcceptedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : 'recently'}
                </p>
              </div>
            )}

            {/* Pick For Us - Let reviewer choose from clips you're curious about */}
            {coupleData?.managedByReviewerId && (
              <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4">
                <h3 className="text-sm font-semibold text-purple-400 mb-2 flex items-center gap-2">
                  🎯 Pick For Us
                </h3>
                <p className="text-zinc-400 text-xs mb-3">
                  Add clips you're curious about and let {coupleData.managedByReviewerName || 'your reviewer'} pick the best one for you!
                </p>

                {/* Pending requests */}
                {pickForUsRequests.filter(r => r.coupleId === coupleData.coupleId && r.status === 'pending').length > 0 && (
                  <div className="mb-3 p-2 bg-purple-500/10 rounded-lg">
                    <p className="text-purple-400 text-xs">
                      ⏳ Waiting for {coupleData.managedByReviewerName || 'reviewer'} to pick from your {pickForUsRequests.filter(r => r.coupleId === coupleData.coupleId && r.status === 'pending').reduce((sum, r) => sum + (r.items || []).length, 0)} clips
                    </p>
                  </div>
                )}

                {/* Scheduled picks */}
                {pickForUsRequests.filter(r => r.coupleId === coupleData.coupleId && r.status === 'scheduled').slice(0, 2).map(req => {
                  const pickedClip = clips.find(c => c.id === req.pickedClipId);
                  return (
                    <div key={req.id} className="mb-2 p-2 bg-lime-500/10 rounded-lg">
                      <p className="text-lime-400 text-xs flex items-center gap-1">
                        <Check className="w-3 h-3" /> {coupleData.managedByReviewerName || 'Reviewer'} picked: <span className="font-medium">{pickedClip?.title || 'Unknown'}</span>
                      </p>
                      {req.reviewerNote && (
                        <p className="text-zinc-400 text-xs mt-1">"{req.reviewerNote}"</p>
                      )}
                    </div>
                  );
                })}

                {/* Current picks being built */}
                {pickForUsList.length > 0 && (
                  <div className="mb-3 space-y-1">
                    <p className="text-zinc-400 text-xs mb-1">Your picks ({pickForUsList.length}):</p>
                    {pickForUsList.map(item => (
                      <div key={item.clipId} className="flex items-center justify-between bg-zinc-800/50 rounded px-2 py-1">
                        <span className="text-white text-xs truncate flex-1">{item.clipTitle}</span>
                        <button
                          onClick={() => removeFromPickForUs(item.clipId)}
                          className="text-zinc-500 hover:text-red-400 ml-2"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    ))}
                    <button
                      onClick={submitPickForUs}
                      className="w-full mt-2 px-3 py-2 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 rounded-lg text-sm font-medium transition-colors"
                    >
                      Send {pickForUsList.length} clips to {coupleData.managedByReviewerName || 'reviewer'}
                    </button>
                  </div>
                )}

                {/* Quick add clips */}
                <div className="space-y-1 max-h-48 overflow-y-auto">
                  <p className="text-zinc-500 text-xs mb-1">Quick add from your favorites & recommendations:</p>
                  {[
                    ...recommendations.slice(0, 3).map(r => clips.find(c => c.id === r.clipId)).filter(Boolean),
                    ...(coupleData.favorites || []).slice(0, 3).map(favId => clips.find(c => c.id === favId)).filter(Boolean)
                  ].filter((c, i, arr) => c && arr.findIndex(x => x?.id === c.id) === i).slice(0, 5).map(clip => {
                    if (!clip) return null;
                    const isAdded = pickForUsList.some(p => p.clipId === clip.id);
                    const cat = CATEGORIES.find(c => c.id === clip.categoryId);
                    return (
                      <button
                        key={clip.id}
                        onClick={() => !isAdded && addToPickForUs(clip)}
                        disabled={isAdded}
                        className={`w-full text-left px-2 py-1.5 rounded text-xs transition-colors ${
                          isAdded
                            ? 'bg-purple-500/20 text-purple-400'
                            : 'bg-zinc-800/50 hover:bg-zinc-700/50 text-zinc-300'
                        }`}
                      >
                        {cat?.emoji} {clip.title}
                        {isAdded && <Check className="w-3 h-3 inline ml-2" />}
                      </button>
                    );
                  })}
                  {pickForUsList.length === 0 && (
                    <p className="text-zinc-600 text-xs italic">Browse clips and add ones you're curious about!</p>
                  )}
                </div>
              </div>
            )}

            {/* Your Category Q&A Responses */}
            {coupleData?.categoryQA && Object.keys(coupleData.categoryQA).length > 0 && (
              <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
                <h3 className="text-sm font-semibold text-zinc-400 mb-3 flex items-center gap-2">
                  💬 Your Category Responses ({Object.keys(coupleData.categoryQA).length} categories)
                </h3>
                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {Object.entries(coupleData.categoryQA).map(([catId, qa]) => {
                    const cat = CATEGORIES.find(c => c.id === catId);
                    return (
                      <div key={catId} className="bg-zinc-700/50 rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">{cat?.emoji}</span>
                          <span className="text-white text-sm font-medium">{cat?.name || catId}</span>
                        </div>
                        <div className="space-y-1">
                          {Object.entries(qa.answers || {}).map(([qId, answer]) => (
                            <p key={qId} className="text-zinc-400 text-xs pl-3 border-l-2 border-purple-500/30">
                              {answer}
                            </p>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Access Code Reminder */}
            <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700">
              <h3 className="text-sm font-semibold text-zinc-400 mb-2">Your Codes</h3>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-pink-500/10 border border-pink-500/30 rounded-lg p-3">
                  <p className="text-pink-400 text-xs font-semibold mb-1">🔐 Access Code</p>
                  <p className="text-pink-400 font-mono text-sm">{coupleData?.accessCode || 'N/A'}</p>
                  <p className="text-zinc-500 text-xs mt-1">For login</p>
                </div>
                <div className="bg-lime-500/10 border border-lime-500/30 rounded-lg p-3">
                  <p className="text-lime-400 text-xs font-semibold mb-1">📤 Sharing Code</p>
                  <p className="text-lime-400 font-mono text-sm">{coupleData?.coupleId}</p>
                  <p className="text-zinc-500 text-xs mt-1">For friends</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tonight tab */}
        {tab === 'browse' && isSetup && !selection && (
          <div className="space-y-4">
            <div className="pb-1">
              <h1 className="font-display text-[40px] leading-[1.05] font-medium tracking-tight">{format(new Date(), 'EEEE')} night.</h1>
              <p className="font-display italic text-[26px] leading-tight text-action mt-1">What’s it going to be?</p>
            </div>

            {featuredRec && featuredRecClip && (
              <article className="bg-surface border border-[#6b3354] rounded-[20px] p-4 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-[11px] font-bold tracking-[0.06em] uppercase text-her truncate">{featuredRec.fromName} picked this for you</span>
                  {unseenRecommendations.length > 1 && (
                    <button onClick={() => setShowRecommendations(true)} className="shrink-0 text-[13px] font-semibold text-ink-2 hover:text-ink">
                      See all {unseenRecommendations.length}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => { markRecommendationSeen(featuredRec.id); openClip(featuredRecClip); }}
                  className="w-full flex items-center gap-3.5 text-left"
                >
                  <ClipThumb url={featuredRecClip.link} className="w-16 h-16" showPlay />
                  <span className="flex-1 min-w-0">
                    <span className="block text-[17px] font-semibold leading-snug">{featuredRecClip.title}</span>
                    {featuredRec.message && <span className="block text-[13px] text-ink-muted italic truncate">“{featuredRec.message}”</span>}
                  </span>
                  <ChevronRight className="w-5 h-5 text-ink-muted shrink-0" />
                </button>
              </article>
            )}

            {nextActivity && nextActivityClip && (
              <button
                onClick={() => goToTab('calendar')}
                className="w-full flex items-center gap-3.5 bg-surface border border-line rounded-[20px] p-4 text-left hover:border-line-strong"
              >
                <span className="w-12 h-12 rounded-[14px] bg-action-tint text-action flex items-center justify-center shrink-0">
                  <CalendarCheck className="w-6 h-6" />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="block text-[11px] font-bold tracking-[0.06em] uppercase text-action">{nextActivityLabel}</span>
                  <span className="block text-[16px] font-semibold truncate">{nextActivityClip.title}</span>
                </span>
                <ChevronRight className="w-5 h-5 text-ink-muted shrink-0" />
              </button>
            )}

            {(hisPick || herPick) && (
              <section className="bg-surface border border-line rounded-[20px] p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="text-[17px] font-bold">Tonight’s picks</h2>
                  <button onClick={() => { setHisPick(null); setHerPick(null); }} className="h-9 text-[13px] font-semibold text-ink-muted hover:text-ink-2">
                    Clear
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-2.5">
                  {([['him', hisName, hisPick, 'bg-him-tint text-him'], ['her', herName, herPick, 'bg-her-tint text-her']] as const).map(([key, who, pick, tone]) => (
                    <button
                      key={key}
                      disabled={!pick}
                      onClick={() => pick && openClip(pick)}
                      className="text-left bg-raised rounded-2xl p-3 disabled:opacity-60"
                    >
                      <span className={`inline-block text-[11px] font-bold px-2 py-0.5 rounded-full ${tone}`}>{who}</span>
                      <span className="block mt-2 text-[14px] font-semibold leading-snug line-clamp-2">{pick ? pick.title : 'Not picked yet'}</span>
                    </button>
                  ))}
                </div>
                {hisPick && herPick && hisPick.id !== herPick.id && (
                  <button
                    onClick={() => setShowCoinFlip(true)}
                    className="w-full h-12 rounded-[14px] bg-attention text-ground text-[15px] font-bold flex items-center justify-center gap-2"
                  >
                    <Coins className="w-5 h-5" /> Flip a coin to decide
                  </button>
                )}
                {hisPick && herPick && hisPick.id === herPick.id && (
                  <button onClick={() => openClip(hisPick)} className="w-full h-12 rounded-[14px] bg-action-tint text-action text-[15px] font-bold">
                    You both picked the same · open it
                  </button>
                )}
              </section>
            )}

            <button
              onClick={() => { goToTab('explore'); setExploreMode('clips'); setFocusSearch(true); }}
              className="w-full h-12 rounded-2xl bg-surface border border-line px-4 flex items-center gap-3 text-left text-[15px] text-ink-muted hover:border-line-strong"
            >
              <Search className="w-[18px] h-[18px]" /> Search all {clips.length} clips
            </button>

            <PopularNow clips={clips} views={clipViews} onOpen={openClip} />

            <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
              <SwipeDeck
                clips={clips.filter(c => !tried.some(t => t.clipId === c.id) && !isInWishlist(c.id) && preferences[c.categoryId] !== 'nope')}
                onWant={clip => { if (!isInWishlist(clip.id)) toggleWishlist(clip); }}
                onOpen={openClip}
              />
              {spinCard}
            </div>

            <section className="space-y-3 pt-1">
              <div className="flex items-center justify-between">
                <h2 className="text-[17px] font-bold">Browse by mood</h2>
                <button onClick={() => { setArea('all'); goToTab('explore'); }} className="h-9 text-[13px] font-semibold text-action">See all</button>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
                {MOOD_AREAS.map(key => (
                  <button
                    key={key}
                    onClick={() => { setArea(key); goToTab('explore'); }}
                    className="bg-surface border border-line rounded-[20px] p-4 text-left hover:border-line-strong"
                  >
                    <span className={`w-10 h-10 rounded-xl flex items-center justify-center text-xl ${areaTone(key)}`}>{AREAS[key].emoji}</span>
                    <span className="block mt-3 text-[15px] font-semibold">{AREAS[key].name}</span>
                    <span className="block text-[13px] text-ink-muted">{clipsInArea(key)} clips</span>
                  </button>
                ))}
              </div>
            </section>

            {(hisProfileMissing || herProfileMissing) && (
              <section className="bg-surface border border-line rounded-[20px] p-4">
                <p className="text-[15px] font-semibold">Finish your profiles</p>
                <p className="text-[13px] text-ink-muted mt-0.5">Your coach can match clips better when they know you both.</p>
                <div className="flex flex-wrap gap-2 mt-3">
                  {hisProfileMissing && (
                    <button onClick={openHisProfileModal} className="h-11 px-4 rounded-xl bg-him-tint text-him text-[14px] font-semibold">Add {hisName}’s</button>
                  )}
                  {herProfileMissing && (
                    <button onClick={openHerProfileModal} className="h-11 px-4 rounded-xl bg-her-tint text-her text-[14px] font-semibold">Add {herName}’s</button>
                  )}
                </div>
              </section>
            )}
          </div>
        )}

        {/* Explore tab (also the browse view before the couple is set up) */}
        {(tab === 'explore' || !isSetup) && !selection && (
          <div className="space-y-4">
            {isSetup ? (
              <div>
                <h1 className="font-display text-[32px] leading-tight font-medium">Explore</h1>
                <p className="text-[14px] text-ink-muted">{clips.length} clips across {filteredCategories.length} categor{filteredCategories.length === 1 ? 'y' : 'ies'}</p>
              </div>
            ) : (
              <>
                <PopularNow clips={clips} views={clipViews} onOpen={openClip} />
                {spinCard}
              </>
            )}
            <ClipSearchField query={clipQuery} onQueryChange={setClipQuery} autoFocus={focusSearch} onFocus={() => setFocusSearch(false)} />
            {!clipQuery.trim() && (
              <div role="tablist" aria-label="Browse by" className="grid grid-cols-2 p-1 rounded-2xl bg-surface border border-line">
                {([['categories', 'Categories'], ['clips', 'All clips']] as const).map(([id, label]) => (
                  <button
                    key={id}
                    role="tab"
                    aria-selected={exploreMode === id}
                    onClick={() => setExploreMode(id)}
                    className={`h-10 rounded-xl text-[14px] font-semibold ${exploreMode === id ? 'bg-raised text-ink' : 'text-ink-muted hover:text-ink-2'}`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            )}
            {areaChips}
            {tagFilterBlock}
            {!clipQuery.trim() && exploreMode === 'categories' && isSetup && (
              <PopularNow clips={clips} views={clipViews} onOpen={openClip} />
            )}
            {(clipQuery.trim() || exploreMode === 'clips') ? (
              <ClipBrowser
                clips={clips.filter(c =>
                  matchesArea(CATEGORIES.find(cat => cat.id === c.categoryId)?.area, area)
                  && (tagFilter.length === 0 || tagFilter.some(tag => c.tags?.includes(tag))))}
                query={clipQuery}
                triedIds={new Set(tried.map(t => t.clipId))}
                wishlistIds={new Set(wishlist.filter(w => w.coupleId === coupleData?.coupleId && w.status === 'want-to-try').map(w => w.clipId))}
                onOpen={openClip}
              />
            ) : (
            <>
            <p className="text-[13px] text-ink-muted">Tap a category for a random clip from it.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2.5">
              {filteredCategories.map(cat => {
                const pref = preferences[cat.id];
                const count = getClipsForCategory(cat.id).filter(clip => tagFilter.length === 0 || tagFilter.some(tag => clip.tags?.includes(tag))).length;
                return (
                  <button
                    key={cat.id}
                    onClick={() => selectCategory(cat)}
                    className={`bg-surface border border-line rounded-[20px] p-4 text-left hover:border-line-strong ${pref === 'nope' ? 'opacity-40' : ''}`}
                  >
                    <span className="flex items-start justify-between gap-2">
                      <span className={`w-11 h-11 rounded-xl flex items-center justify-center text-[22px] ${areaTone(cat.area)}`}>{cat.emoji}</span>
                      {pref && pref !== 'neutral' && (
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${PREFERENCE_BADGES[pref].className}`}>{PREFERENCE_BADGES[pref].label}</span>
                      )}
                    </span>
                    <span className="block mt-3 text-[15px] font-semibold leading-snug">{cat.name}</span>
                    <span className="block text-[13px] text-ink-muted">{count} clip{count === 1 ? '' : 's'}</span>
                  </button>
                );
              })}
            </div>
            {filteredCategories.length === 0 && (
              <div className="bg-surface border border-line rounded-[20px] p-6 text-center">
                <p className="text-[15px] font-semibold">Nothing matches those filters</p>
                <button onClick={() => { setArea('all'); setTagFilter([]); }} className="mt-2 h-11 px-4 text-[14px] font-semibold text-action">
                  Clear filters
                </button>
              </div>
            )}
            </>
            )}
          </div>
        )}

        {/* Clip View */}
        {showClipView && selection && (
          <div className={`space-y-4 transition-opacity ${revealed ? 'opacity-100' : 'opacity-50'}`}>
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => { setSelection(null); setRevealed(false); }}
                aria-label="Back to browsing"
                className="w-11 h-11 rounded-full bg-surface border border-line flex items-center justify-center text-ink-2 hover:text-ink"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              {revealed && (
                <div className="flex gap-2">
                  <button
                    onClick={() => copyClipLink(selection.clip.id)}
                    aria-label="Copy shareable link"
                    title="Copy shareable link"
                    className={`w-11 h-11 rounded-full border flex items-center justify-center ${copiedClipId === selection.clip.id ? 'bg-action-tint border-action/40 text-action' : 'bg-surface border-line text-ink-2 hover:text-ink'}`}
                  >
                    {copiedClipId === selection.clip.id ? <CheckCheck className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                  </button>
                  {isSetup && (
                    <>
                      <button
                        onClick={() => toggleFavorite(selection.clip.id)}
                        aria-pressed={favorites.includes(selection.clip.id)}
                        aria-label="Favourite"
                        title={favorites.includes(selection.clip.id) ? 'Remove from favourites' : 'Add to favourites'}
                        className={`w-11 h-11 rounded-full border flex items-center justify-center ${favorites.includes(selection.clip.id) ? 'bg-her-tint border-her/40 text-her' : 'bg-surface border-line text-ink-2 hover:text-her'}`}
                      >
                        <Heart className={`w-5 h-5 ${favorites.includes(selection.clip.id) ? 'fill-current' : ''}`} />
                      </button>
                      {saveWishlist && !tried.some(t => t.clipId === selection.clip.id) && (
                        <button
                          onClick={() => toggleWishlist(selection.clip)}
                          aria-pressed={isInWishlist(selection.clip.id)}
                          aria-label="Want to try"
                          title={isInWishlist(selection.clip.id) ? 'Remove from Want to Try' : 'Add to Want to Try'}
                          className={`w-11 h-11 rounded-full border flex items-center justify-center ${isInWishlist(selection.clip.id) ? 'bg-attention-tint border-attention/40 text-attention' : 'bg-surface border-line text-ink-2 hover:text-attention'}`}
                        >
                          <Star className={`w-5 h-5 ${isInWishlist(selection.clip.id) ? 'fill-current' : ''}`} />
                        </button>
                      )}
                      {coupleData?.managedByReviewerId && (
                        <button
                          onClick={() => {
                            if (pickForUsList.some(p => p.clipId === selection.clip.id)) {
                              removeFromPickForUs(selection.clip.id);
                            } else {
                              addToPickForUs(selection.clip);
                            }
                          }}
                          aria-pressed={pickForUsList.some(p => p.clipId === selection.clip.id)}
                          aria-label="Add to coach shortlist"
                          title={pickForUsList.some(p => p.clipId === selection.clip.id) ? 'Remove from Pick For Us' : 'Add to Pick For Us'}
                          className={`w-11 h-11 rounded-full border flex items-center justify-center ${pickForUsList.some(p => p.clipId === selection.clip.id) ? 'bg-action-tint border-action/40 text-action' : 'bg-surface border-line text-ink-2 hover:text-action'}`}
                        >
                          <Target className="w-5 h-5" />
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <span className={`inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-[12px] font-bold ${areaTone(selection.category.area)} ${spinning ? 'animate-pulse' : ''}`}>
                {selection.category.emoji} {selection.category.name}
              </span>
              <h1 className="font-display text-[34px] leading-[1.1] font-medium mt-3">{selection.clip.title}</h1>
              {revealed && selection.clip.description && (
                <p className="text-[15px] text-ink-muted mt-2">{selection.clip.description}</p>
              )}
            </div>

            {revealed && (
              <>
                <p className="text-[16px] leading-relaxed text-ink-2 border-l-2 border-action pl-3.5">{selection.category.instruction}</p>

                <VideoPlayer key={selection.clip.id} url={selection.clip.link} className="rounded-2xl" />
                <div className="flex items-center justify-center gap-4 text-[13px]">
                  <a
                    href={getRedGifsWatchUrl(selection.clip.link)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="h-9 flex items-center gap-1.5 text-ink-muted hover:text-ink-2"
                  >
                    <Eye className="w-4 h-4" /> Open on RedGifs
                  </a>
                  {isSetup && saveClipViews && (
                    clipViews.some(v => v.clipId === selection.clip.id && v.viewerId === coupleData?.coupleId) ? (
                      <span className="flex items-center gap-1 text-action"><Check className="w-4 h-4" /> Watched</span>
                    ) : (
                      <button onClick={() => markAsWatched(selection.clip)} className="h-9 flex items-center gap-1 text-her hover:brightness-110">
                        <Play className="w-4 h-4" /> Mark as watched
                      </button>
                    )
                  )}
                </div>

                {selection.clip.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {(selection.clip.tags || []).map(tag => <TagBadge key={tag} tag={tag} small />)}
                  </div>
                )}

                {coupleData && !dismissedQAPrompts.includes(selection.category.id) && (
                  <CategoryQAPrompt
                    categoryId={selection.category.id}
                    coupleData={coupleData}
                    onStartQA={() => setShowCategoryQA(selection.category.id)}
                    onDismiss={() => setDismissedQAPrompts(prev => [...prev, selection.category.id])}
                  />
                )}

                {isSetup && (
                  <div className="grid grid-cols-2 gap-2.5">
                    <button
                      onClick={() => setHisPick(selection.clip)}
                      aria-pressed={hisPick?.id === selection.clip.id}
                      className={`h-12 rounded-[14px] border text-[15px] font-semibold ${hisPick?.id === selection.clip.id ? 'bg-him-tint border-him/50 text-him' : 'bg-surface border-line text-ink-2 hover:border-him/50'}`}
                    >
                      {hisPick?.id === selection.clip.id ? `${hisName}’s pick ✓` : `${hisName}’s pick`}
                    </button>
                    <button
                      onClick={() => setHerPick(selection.clip)}
                      aria-pressed={herPick?.id === selection.clip.id}
                      className={`h-12 rounded-[14px] border text-[15px] font-semibold ${herPick?.id === selection.clip.id ? 'bg-her-tint border-her/50 text-her' : 'bg-surface border-line text-ink-2 hover:border-her/50'}`}
                    >
                      {herPick?.id === selection.clip.id ? `${herName}’s pick ✓` : `${herName}’s pick`}
                    </button>
                  </div>
                )}

                {isSetup && (
                  <div className="space-y-2.5">
                    <button
                      onClick={() => scheduleTonight(selection.clip)}
                      disabled={schedulingTonight || isPlannedTonight(selection.clip.id)}
                      className="w-full h-14 rounded-2xl bg-action text-ground text-[17px] font-bold flex items-center justify-center gap-2 hover:brightness-105 disabled:bg-action-tint disabled:text-action"
                    >
                      {isPlannedTonight(selection.clip.id) ? <><Check className="w-5 h-5" /> Planned for tonight</> : <><Moon className="w-5 h-5" /> We’re doing this tonight</>}
                    </button>
                    <div className="grid grid-cols-2 gap-2.5">
                      <button
                        onClick={() => setShowTriedModal(selection.clip)}
                        className={`h-12 rounded-[14px] border text-[15px] font-semibold flex items-center justify-center gap-1.5 ${tried.some(t => t.clipId === selection.clip.id) ? 'border-action/50 text-action' : 'border-line-strong text-ink hover:bg-surface'}`}
                      >
                        {tried.some(t => t.clipId === selection.clip.id) ? <><Check className="w-4 h-4" /> We tried this</> : <><ClipboardList className="w-4 h-4" /> We tried this</>}
                      </button>
                      <button onClick={spin} className="h-12 rounded-[14px] text-ink-2 text-[15px] font-semibold flex items-center justify-center gap-1.5 hover:text-ink hover:bg-surface">
                        <Shuffle className="w-4 h-4" /> Spin again
                      </button>
                    </div>
                  </div>
                )}
                {!isSetup && (
                  <button onClick={spin} className="w-full h-12 rounded-[14px] border border-line-strong text-[15px] font-semibold flex items-center justify-center gap-1.5 hover:bg-surface">
                    <Shuffle className="w-4 h-4" /> Spin again
                  </button>
                )}

                {isSetup && coupleData && (
                  <ClipExperienceHistory
                    clipId={selection.clip.id}
                    coupleId={coupleData.coupleId}
                    tried={tried}
                    compact={false}
                  />
                )}

                {(() => {
                  const moreClips = getClipsForCategory(selection.category.id)
                    .filter(clip => clip.id !== selection.clip.id)
                    .filter(clip => tagFilter.length === 0 || tagFilter.some(tag => clip.tags?.includes(tag)));
                  return moreClips.length > 0 && (
                    <section className="pt-2">
                      <h2 className="text-[15px] font-bold mb-2.5">More in {selection.category.name}</h2>
                      <div className="grid gap-2 sm:grid-cols-2">
                        {moreClips.map(clip => (
                          <button
                            key={clip.id}
                            onClick={() => { setSelection({ ...selection, clip }); window.scrollTo({ top: 0 }); }}
                            className="w-full flex items-center gap-3 bg-surface border border-line rounded-2xl p-2.5 text-left hover:border-line-strong"
                          >
                            <ClipThumb url={clip.link} className="w-16 h-16" showPlay />
                            <span className="flex-1 min-w-0">
                              <span className="block text-[15px] font-semibold truncate">{clip.title}</span>
                              {clip.description && <span className="block text-[13px] text-ink-muted truncate">{clip.description}</span>}
                            </span>
                            {tried.some(t => t.clipId === clip.id) && <span className="shrink-0 text-[11px] font-bold px-2 py-0.5 rounded-full bg-action-tint text-action">Tried</span>}
                            <ChevronRight className="w-4 h-4 text-ink-muted shrink-0" />
                          </button>
                        ))}
                      </div>
                    </section>
                  );
                })()}
              </>
            )}
          </div>
        )}

        {/* Couple Setup Modal */}
        <Modal
          isOpen={showCoupleSetup}
          onClose={() => setShowCoupleSetup(false)}
          title="Settings"
        >
          <div className="p-4">
            {/* Tab Navigation - only show tabs when setup is complete */}
            {isSetup && (
              <div className="flex gap-1 mb-4 bg-zinc-800 rounded-xl p-1">
                {[
                  { id: 'profile' as const, label: '👤 Profile' },
                  { id: 'content' as const, label: '📹 Content' },
                  { id: 'more' as const, label: '⚙️ More' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setSetupTab(tab.id)}
                    className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      setupTab === tab.id
                        ? 'bg-pink-500/30 text-pink-400'
                        : 'text-zinc-400 hover:text-white'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}

            {/* Tab Content */}
            <div className="max-h-[60vh] overflow-y-auto pr-1">
              {/* Profile Tab (or initial setup) */}
              {(setupTab === 'profile' || !isSetup) && (
                <div className="space-y-4">
                  {/* Names */}
                  <div>
                    <label className="block text-sm text-blue-400 mb-1">👨 His Name</label>
                    <input
                      type="text"
                      value={coupleForm.hisName || coupleData?.hisName || ''}
                      onChange={e => setCoupleForm({ ...coupleForm, hisName: e.target.value })}
                      placeholder="His name..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-pink-400 mb-1">👩 Her Name</label>
                    <input
                      type="text"
                      value={coupleForm.herName || coupleData?.herName || ''}
                      onChange={e => setCoupleForm({ ...coupleForm, herName: e.target.value })}
                      placeholder="Her name..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    />
                  </div>

                  {/* Your Codes */}
                  {coupleData?.coupleId && (
                    <div className="space-y-2">
                      {/* Share Code - for friends */}
                      <div className="bg-lime-500/10 border border-lime-500/30 rounded-xl p-3">
                        <p className="text-lime-400 text-xs font-semibold mb-1">📤 SHARE CODE (Give to friends)</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-zinc-900 rounded-lg px-3 py-2 font-mono text-base text-lime-400 text-center tracking-wider">
                            {coupleData.coupleId}
                          </div>
                          <button
                            onClick={copyCode}
                            className="px-3 py-2 rounded-lg bg-lime-500/20 text-lime-400 hover:bg-lime-500/30 transition-colors"
                          >
                            {codeCopied ? <CheckCheck className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
                          </button>
                        </div>
                        <p className="text-zinc-500 text-xs mt-1">Friends use this to recommend clips for you</p>
                      </div>

                      {/* Access Code - for login */}
                      <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-3">
                        <p className="text-pink-400 text-xs font-semibold mb-1">🔐 ACCESS CODE (Keep private!)</p>
                        <div className="flex items-center gap-2">
                          <div className="flex-1 bg-zinc-900 rounded-lg px-3 py-2 font-mono text-base text-pink-400 text-center tracking-wider">
                            {coupleData.accessCode || 'Not set'}
                          </div>
                          <button
                            onClick={async () => {
                              if (coupleData.accessCode) {
                                await navigator.clipboard.writeText(coupleData.accessCode);
                                setCodeCopied(true);
                                setTimeout(() => setCodeCopied(false), 2000);
                              }
                            }}
                            className="px-3 py-2 rounded-lg bg-pink-500/20 text-pink-400 hover:bg-pink-500/30 transition-colors"
                          >
                            <Copy className="w-5 h-5" />
                          </button>
                        </div>
                        <p className="text-zinc-500 text-xs mt-1">Use this to log back in - don't share!</p>
                        {coupleData.accessCode && (
                          <div className="mt-2 flex items-center gap-2">
                            <CopyLinkButton url={coupleLoginLink(coupleData.accessCode)} label="Copy my login link" />
                            <span className="text-zinc-500 text-xs">Opens your Date Night on any device, no code needed</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* His Stats Section (Optional) */}
                  <div>
                    <button
                      onClick={() => setShowHisProfile(!showHisProfile)}
                      className="w-full flex items-center justify-between bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/30 rounded-xl p-3 hover:border-blue-500/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🍆</span>
                        <span className="text-white text-sm font-medium">His Stats</span>
                        <span className="text-zinc-500 text-xs">(optional)</span>
                      </div>
                      <span className="text-zinc-400">{showHisProfile ? '−' : '+'}</span>
                    </button>

                    {showHisProfile && (
                      <div className="mt-2 bg-zinc-800/50 rounded-xl p-3 space-y-4 border border-zinc-700">
                        <p className="text-zinc-500 text-xs">Reviewer sees this to personalize recs</p>

                        {/* Size Scale */}
                        <div>
                          <label className="block text-xs text-zinc-400 mb-1">Size</label>
                          <div className="flex gap-1">
                            {HIS_SIZE_SCALE.map(item => (
                              <button
                                key={item.value}
                                onClick={() => setHisProfileForm({ ...hisProfileForm, size: item.value })}
                                className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                                  (hisProfileForm.size ?? coupleData?.hisProfile?.size) === item.value
                                    ? 'bg-blue-500/30 border border-blue-500'
                                    : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                                }`}
                              >
                                <span className="text-lg">{item.emoji}</span>
                                <span className="text-[10px] text-zinc-400">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Girth Scale */}
                        <div>
                          <label className="block text-xs text-zinc-400 mb-1">Girth</label>
                          <div className="flex gap-1">
                            {HIS_GIRTH_SCALE.map(item => (
                              <button
                                key={item.value}
                                onClick={() => setHisProfileForm({ ...hisProfileForm, girth: item.value })}
                                className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                                  (hisProfileForm.girth ?? coupleData?.hisProfile?.girth) === item.value
                                    ? 'bg-blue-500/30 border border-blue-500'
                                    : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                                }`}
                              >
                                <span className="text-lg">{item.emoji}</span>
                                <span className="text-[10px] text-zinc-400">{item.size}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Cum Intensity Scale */}
                        <div>
                          <label className="block text-xs text-zinc-400 mb-1">Usual Finish</label>
                          <div className="flex gap-1">
                            {HIS_CUM_SCALE.map(item => (
                              <button
                                key={item.value}
                                onClick={() => setHisProfileForm({ ...hisProfileForm, cumIntensity: item.value })}
                                className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                                  (hisProfileForm.cumIntensity ?? coupleData?.hisProfile?.cumIntensity) === item.value
                                    ? 'bg-blue-500/30 border border-blue-500'
                                    : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                                }`}
                              >
                                <span className="text-lg">{item.emoji}</span>
                                <span className="text-[10px] text-zinc-400">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Her Stats Section (Optional) */}
                  <div>
                    <button
                      onClick={() => setShowHerProfileOnboarding(!showHerProfileOnboarding)}
                      className="w-full flex items-center justify-between bg-gradient-to-r from-pink-500/10 to-rose-500/10 border border-pink-500/30 rounded-xl p-3 hover:border-pink-500/50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-lg">🌸</span>
                        <span className="text-white text-sm font-medium">Her Stats</span>
                        <span className="text-zinc-500 text-xs">(optional)</span>
                      </div>
                      <span className="text-zinc-400">{showHerProfileOnboarding ? '−' : '+'}</span>
                    </button>

                    {showHerProfileOnboarding && (
                      <div className="mt-2 bg-zinc-800/50 rounded-xl p-3 space-y-4 border border-zinc-700">
                        <p className="text-zinc-500 text-xs">Helps personalize content for her pleasure</p>

                        {/* Wetness Scale */}
                        <div>
                          <label className="block text-xs text-zinc-400 mb-1">Typical Wetness</label>
                          <div className="flex gap-1">
                            {HER_WETNESS_SCALE.map(item => (
                              <button
                                key={item.value}
                                onClick={() => setHerProfileForm({ ...herProfileForm, typicalWetness: item.value })}
                                className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                                  (herProfileForm.typicalWetness ?? coupleData?.herProfile?.typicalWetness) === item.value
                                    ? 'bg-pink-500/30 border border-pink-500'
                                    : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                                }`}
                              >
                                <span className="text-lg">{item.emoji}</span>
                                <span className="text-[10px] text-zinc-400">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Squirting Scale */}
                        <div>
                          <label className="block text-xs text-zinc-400 mb-1">Squirting</label>
                          <div className="flex gap-1">
                            {HER_SQUIRT_SCALE.map(item => (
                              <button
                                key={item.value}
                                onClick={() => setHerProfileForm({ ...herProfileForm, squirtingAbility: item.value })}
                                className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                                  (herProfileForm.squirtingAbility ?? coupleData?.herProfile?.squirtingAbility) === item.value
                                    ? 'bg-violet-500/30 border border-violet-500'
                                    : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                                }`}
                              >
                                <span className="text-lg">{item.emoji}</span>
                                <span className="text-[10px] text-zinc-400">{item.label}</span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Save/Setup Button */}
                  <PrimaryButton
                    onClick={setupCouple}
                    disabled={!coupleForm.hisName?.trim() || !coupleForm.herName?.trim()}
                    className="w-full"
                  >
                    {isSetup ? 'Update Names' : 'Start Date Night'} <ArrowRight className="w-4 h-4" />
                  </PrimaryButton>
                </div>
              )}

              {/* Content Tab */}
              {setupTab === 'content' && isSetup && (
                <div className="space-y-4">
                  {/* Content Request Preferences */}
                  <div className="mb-4 bg-zinc-800 border border-zinc-700 rounded-xl p-4">
                    <h4 className="text-white font-semibold text-sm mb-2">What Content Are You Open To?</h4>
                    <p className="text-xs text-zinc-400 mb-2">Let reviewers know what kind of requests you're comfortable with</p>
                    <textarea
                      id="content-preferences-input"
                      defaultValue={coupleData?.contentRequestPreferences || ''}
                      placeholder="e.g., Currently only taking solo male requests. Happy to discuss angles and positions!"
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                      rows={2}
                    />
                    <button
                      onClick={async () => {
                        const input = document.getElementById('content-preferences-input') as HTMLTextAreaElement;
                        if (!coupleData || !input) return;
                        const updated = { ...coupleData, contentRequestPreferences: input.value.trim() };
                        await saveCoupleData(updated);
                        alert('Preferences saved!');
                      }}
                      className="mt-2 px-3 py-1.5 text-xs font-medium rounded-lg text-zinc-900"
                      style={{ backgroundColor: COLORS.lime }}
                    >
                      Save Preferences
                    </button>
                  </div>

                  {/* Reviewer Feedback on Your Content */}
                  {submissionFeedback.totalReviews > 0 && (
                    <div className="mb-4 bg-gradient-to-r from-lime-500/10 to-emerald-500/10 border border-lime-500/20 rounded-xl p-4">
                      <h4 className="text-lime-400 font-semibold text-sm mb-2">Reviewer Feedback on Your Content</h4>
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                          <p className="text-2xl font-bold text-white">{submissionFeedback.avgRating}/5</p>
                          <p className="text-xs text-zinc-400">Avg Rating</p>
                        </div>
                        <div className="bg-zinc-800/50 rounded-lg p-2 text-center">
                          <p className="text-2xl font-bold text-white">{submissionFeedback.totalReviews}</p>
                          <p className="text-xs text-zinc-400">Reviews</p>
                        </div>
                      </div>
                      {submissionFeedback.wantMore > 0 && (
                        <p className="text-sm text-emerald-400 mb-1">🔥 {submissionFeedback.wantMore} reviewer{submissionFeedback.wantMore > 1 ? 's' : ''} want more couple clips from you!</p>
                      )}
                      {submissionFeedback.wantSolo > 0 && (
                        <p className="text-sm text-purple-400 mb-1">👀 {submissionFeedback.wantSolo} reviewer{submissionFeedback.wantSolo > 1 ? 's' : ''} would love solo male content too</p>
                      )}
                      {submissionFeedback.quotes.length > 0 && (
                        <div className="mt-2 space-y-1">
                          <p className="text-xs text-zinc-500">What reviewers said:</p>
                          {submissionFeedback.quotes.map((q, i) => (
                            <p key={i} className="text-xs text-zinc-300 italic">&ldquo;{q.substring(0, 150)}{q.length > 150 ? '...' : ''}&rdquo;</p>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Reviewer Content Requests */}
                  {requests.filter(r => r.fromReviewerId).length > 0 && (
                    <div className="mb-4 bg-purple-500/10 border border-purple-500/20 rounded-xl p-4">
                      <h4 className="text-purple-400 font-semibold text-sm mb-2">Content Requests from Your Reviewer</h4>
                      <div className="space-y-2">
                        {requests.filter(r => r.fromReviewerId && r.status === 'pending').map(req => (
                          <div key={req.id} className="bg-zinc-800/50 rounded-lg p-3">
                            <p className="text-xs text-purple-400 mb-1">{req.fromReviewerName || 'Your Reviewer'} would love to see:</p>
                            <p className="text-sm text-white whitespace-pre-line">{req.description}</p>
                            <div className="mt-2 flex gap-2">
                              <button
                                onClick={() => {
                                  // Open submission form pre-filled
                                  setSubmissionForm({ videoUrl: '', title: (req.description.split('\n')[0] || '').substring(0, 50), description: `In response to reviewer request: ${req.description}` });
                                  setShowSubmissionModal(true);
                                }}
                                className="px-2 py-1 bg-lime-500/20 text-lime-400 rounded text-xs"
                              >
                                Submit Content
                              </button>
                              <button
                                onClick={async () => {
                                  await d1Upsert('couple_requests', { ...req, status: 'declined', responseNote: 'Not right now' });
                                  setRequests(requests.map(r => r.id === req.id ? { ...r, status: 'declined' as const } : r));
                                }}
                                className="px-2 py-1 bg-zinc-700 text-zinc-400 rounded text-xs"
                              >
                                Not Right Now
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Submit Content */}
                  <div className="bg-gradient-to-r from-pink-500/10 to-red-500/10 border border-pink-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">📹</span>
                      <span className="text-white font-medium">Submit Your Content</span>
                      {submissions.filter(s => s.status === 'pending').length > 0 && (
                        <span className="text-amber-400 text-xs">({submissions.filter(s => s.status === 'pending').length} pending)</span>
                      )}
                    </div>
                    <p className="text-zinc-400 text-xs mb-3">Share videos of your experiences with VB supplements!</p>

                    <button
                      onClick={() => setShowSubmissionModal(true)}
                      className="w-full py-2.5 flex items-center justify-center gap-2 rounded-xl font-medium text-zinc-900"
                      style={{ backgroundColor: COLORS.lime }}
                    >
                      <Video className="w-4 h-4" /> Submit a Video
                    </button>

                    {/* Submission History */}
                    {submissions.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <p className="text-zinc-500 text-xs font-medium">Recent Submissions</p>
                        {submissions.slice(-3).reverse().map(sub => (
                          <div key={sub.id} className="flex items-center justify-between bg-zinc-700/50 rounded-lg px-3 py-2">
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm truncate">{sub.title}</p>
                              <p className="text-zinc-500 text-xs">{new Date(sub.createdAt).toLocaleDateString()}</p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              sub.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                              sub.status === 'approved' ? 'bg-green-500/20 text-green-400' :
                              sub.status === 'reviewed' ? 'bg-blue-500/20 text-blue-400' :
                              'bg-zinc-500/20 text-zinc-400'
                            }`}>
                              {sub.status === 'pending' ? '⏳' :
                               sub.status === 'approved' ? '✅' :
                               sub.status === 'reviewed' ? '👀' : '📝'}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Request Content */}
                  <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 border border-purple-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-lg">💡</span>
                      <span className="text-white font-medium">Request Content</span>
                    </div>
                    <p className="text-zinc-400 text-xs mb-3">Tell your reviewer what you'd like to see!</p>

                    <button
                      onClick={() => setShowRequestModal(true)}
                      className="w-full py-2.5 flex items-center justify-center gap-2 rounded-xl font-medium bg-purple-500/20 text-purple-300 border border-purple-500/30 hover:bg-purple-500/30"
                    >
                      <Lightbulb className="w-4 h-4" /> Make a Request
                    </button>

                    {/* Request History */}
                    {requests.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <p className="text-zinc-500 text-xs font-medium">Recent Requests</p>
                        {requests.slice(-3).reverse().map(req => (
                          <div key={req.id} className="bg-zinc-700/50 rounded-lg px-3 py-2">
                            <div className="flex items-center justify-between">
                              <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-600 text-zinc-300">
                                {req.type === 'clip' ? '🎬' : '📁'}
                              </span>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => {
                                    setEditingRequestId(req.id);
                                    setRequestForm({
                                      type: req.type,
                                      description: req.description,
                                      categoryId: req.categoryId || '',
                                      exampleUrl: req.exampleUrl || '',
                                      exampleUrls: req.exampleUrls?.length ? req.exampleUrls : [''],
                                      categoryName: req.categoryName || '',
                                      whyWanted: req.whyWanted || ''
                                    });
                                    setShowRequestModal(true);
                                  }}
                                  className="text-purple-400 hover:text-purple-300 text-xs"
                                >
                                  Edit
                                </button>
                                <button
                                  onClick={() => {
                                    if (confirm('Delete this request?')) deleteRequest(req.id);
                                  }}
                                  className="text-red-400 hover:text-red-300 text-xs"
                                >
                                  Delete
                                </button>
                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  req.status === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                                  req.status === 'fulfilled' ? 'bg-green-500/20 text-green-400' :
                                  'bg-zinc-500/20 text-zinc-400'
                                }`}>
                                  {req.status === 'pending' ? '⏳' : req.status === 'fulfilled' ? '✅' : '❌'}
                                </span>
                              </div>
                            </div>
                            <p className="text-white text-sm mt-1 line-clamp-1">{req.description}</p>
                            {req.responseNote && (
                              <p className="text-zinc-400 text-xs mt-1 italic">Reviewer: "{req.responseNote}"</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* More Tab */}
              {setupTab === 'more' && isSetup && (
                <div className="space-y-4">
                  {/* Feedback Section */}
                  <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-lg">💬</span>
                      <span className="text-white font-medium">Your Feedback</span>
                      {coupleData?.feedbackSubmittedAt && (
                        <span className="text-zinc-500 text-xs">(submitted)</span>
                      )}
                    </div>

                    {/* Would Submit Clips */}
                    <div className="mb-4">
                      <label className="block text-sm text-zinc-300 mb-2">Would you submit your own clips?</label>
                      <div className="grid grid-cols-3 gap-2">
                        {[
                          { value: 'yes' as const, label: '✅ Yes!', color: 'border-green-500 bg-green-500/20 text-green-400' },
                          { value: 'maybe' as const, label: '🤔 Maybe', color: 'border-amber-500 bg-amber-500/20 text-amber-400' },
                          { value: 'no' as const, label: '❌ No', color: 'border-zinc-500 bg-zinc-500/20 text-zinc-400' },
                        ].map(opt => (
                          <button
                            key={opt.value}
                            onClick={async () => {
                              await saveFeedback({ wouldSubmitClips: opt.value });
                            }}
                            className={`px-2 py-1.5 rounded-lg text-xs font-medium border-2 ${coupleData?.wouldSubmitClips === opt.value ? opt.color : 'border-zinc-600 bg-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Product Excitement */}
                    <div className="mb-4">
                      <label className="block text-sm text-zinc-300 mb-2">Product excitement</label>
                      <div className="flex justify-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button
                            key={star}
                            onClick={async () => {
                              await saveFeedback({ productExcitement: star as 1 | 2 | 3 | 4 | 5 });
                            }}
                            className={`text-2xl transition-transform hover:scale-110 ${(coupleData?.productExcitement ?? 0) >= star ? 'text-amber-400' : 'text-zinc-600'}`}
                          >
                            ★
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* General Feedback */}
                    <div>
                      <label className="block text-sm text-zinc-300 mb-2">Suggestions</label>
                      <textarea
                        value={coupleData?.generalFeedback || ''}
                        onChange={async e => {
                          const updated = { ...coupleData!, generalFeedback: e.target.value, lastActivityAt: now() };
                          await saveCoupleData(updated);
                          await saveCoupleByCode(updated);
                        }}
                        placeholder="Tell us what you think..."
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
                        maxLength={500}
                      />
                    </div>
                  </div>

                  {/* Privacy Note */}
                  <div className="bg-zinc-900 rounded-xl p-3 border border-zinc-700">
                    <p className="text-zinc-500 text-xs">Your data is stored privately.</p>
                  </div>

                  {/* Logout Option */}
                  <button
                    onClick={() => {
                      if (confirm('This will clear all your couple data (history, favorites, preferences). Are you sure?')) {
                        localStorage.removeItem('vb-couple-data');
                        localStorage.removeItem(`user:${localStorage.getItem('vb-user-id')}:vb-couple-data`);
                        setShowCoupleSetup(false);
                        setCoupleForm({ hisName: '', herName: '' });
                        onBack();
                        window.location.reload();
                      }
                    }}
                    className="w-full py-3 text-red-400 hover:text-red-300 text-sm border border-red-500/20 rounded-xl hover:border-red-500/40"
                  >
                    Log Out / Clear Data
                  </button>
                </div>
              )}
            </div>
          </div>
        </Modal>

        {/* Returning Couple Modal */}
        <Modal
          isOpen={showReturningModal}
          onClose={() => {
            setShowReturningModal(false);
            setReturningCode('');
            setReturningError('');
          }}
          title="Welcome Back!"
        >
          <div className="p-6">
            <p className="text-zinc-400 mb-6">
              Enter your couple code to access your account on this device.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-400 mb-2">Your Couple Code</label>
                <input
                  type="text"
                  value={returningCode}
                  onChange={e => {
                    setReturningCode(e.target.value.toUpperCase());
                    setReturningError('');
                  }}
                  placeholder="VBA or VBC code"
                  maxLength={10}
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white font-mono text-lg text-center tracking-widest uppercase"
                  autoFocus
                />
              </div>

              {returningError && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3">
                  <p className="text-red-400 text-sm">{returningError}</p>
                </div>
              )}

              <PrimaryButton
                onClick={handleReturningCouple}
                disabled={returningLoading || returningCode.length < 10}
                className="w-full"
              >
                {returningLoading ? 'Looking up...' : 'Access Your Account'}
              </PrimaryButton>

              <button
                onClick={() => {
                  setShowReturningModal(false);
                  setShowCoupleSetup(true);
                }}
                className="w-full text-zinc-400 hover:text-white text-sm"
              >
                Don't have a code? Start fresh
              </button>
            </div>
          </div>
        </Modal>


        {/* His Profile Modal (Dedicated Screen) */}
        <Modal
          isOpen={showHisProfileModal}
          onClose={() => setShowHisProfileModal(false)}
          title="His Profile"
        >
          <div className="p-4">
            {/* Tab Navigation */}
            <div className="flex gap-1 mb-4 bg-zinc-800 rounded-xl p-1">
              {[
                { id: 'stats' as const, label: '📊 Stats' },
                { id: 'details' as const, label: '📝 Details' },
                { id: 'settings' as const, label: '⚙️ Settings' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setProfileTab(tab.id)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    profileTab === tab.id
                      ? 'bg-pink-500/30 text-pink-400'
                      : 'text-zinc-400 hover:text-white'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
              {/* Stats Tab */}
              {profileTab === 'stats' && (
                <>
                  {/* Size Scale */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">🍆 Size</label>
                    <div className="flex gap-1 flex-wrap">
                      {HIS_SIZE_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, size: item.value })}
                          className={`flex-1 min-w-[55px] flex flex-col items-center p-2 rounded-lg transition-colors ${
                            hisProfileEditForm.size === item.value
                              ? 'bg-blue-500/30 border border-blue-500'
                              : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400 mt-1">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Girth Scale */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">○ Girth</label>
                    <div className="flex gap-1">
                      {HIS_GIRTH_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, girth: item.value })}
                          className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                            hisProfileEditForm.girth === item.value
                              ? 'bg-blue-500/30 border border-blue-500'
                              : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400 mt-1">{item.size}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Cum Intensity Scale */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">💦 Usual Finish Volume</label>
                    <div className="flex gap-1">
                      {HIS_CUM_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, cumIntensity: item.value })}
                          className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                            hisProfileEditForm.cumIntensity === item.value
                              ? 'bg-blue-500/30 border border-blue-500'
                              : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400 mt-1">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Details Tab */}
              {profileTab === 'details' && (
                <>
                  {/* Consistency Scale */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">🧴 Consistency</label>
                    <div className="flex gap-1">
                      {HIS_CONSISTENCY_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, cumConsistency: item.value })}
                          className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                            hisProfileEditForm.cumConsistency === item.value
                              ? 'bg-purple-500/30 border border-purple-500'
                              : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400 mt-1">{item.label}</span>
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={hisProfileEditForm.consistencyNotes || ''}
                      onChange={e => setHisProfileEditForm({ ...hisProfileEditForm, consistencyNotes: e.target.value })}
                      placeholder="Notes (optional)..."
                      maxLength={200}
                      className="w-full mt-2 bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>

                  {/* Taste Scale */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">🍬 Taste</label>
                    <div className="flex gap-1">
                      {HIS_TASTE_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, cumTaste: item.value })}
                          className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                            hisProfileEditForm.cumTaste === item.value
                              ? 'bg-amber-500/30 border border-amber-500'
                              : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400 mt-1">{item.label}</span>
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={hisProfileEditForm.tasteNotes || ''}
                      onChange={e => setHisProfileEditForm({ ...hisProfileEditForm, tasteNotes: e.target.value })}
                      placeholder="Notes (optional)..."
                      maxLength={200}
                      className="w-full mt-2 bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>

                  {/* Precum Scale */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">💧 Precum Production</label>
                    <div className="flex gap-1">
                      {HIS_PRECUM_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, precumProduction: item.value })}
                          className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                            hisProfileEditForm.precumProduction === item.value
                              ? 'bg-blue-500/30 border border-blue-500'
                              : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400 mt-1">{item.label}</span>
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={hisProfileEditForm.precumNotes || ''}
                      onChange={e => setHisProfileEditForm({ ...hisProfileEditForm, precumNotes: e.target.value })}
                      placeholder="Notes (optional)..."
                      maxLength={200}
                      className="w-full mt-2 bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>
                </>
              )}

              {/* Settings Tab */}
              {profileTab === 'settings' && (
                <>
                  {/* Profile Ownership Selector */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">Profile managed by</label>
                    <div className="flex gap-2">
                      {(['him', 'her', 'both'] as const).map(option => (
                        <button
                          key={option}
                          onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, managedBy: option })}
                          className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                            hisProfileEditForm.managedBy === option
                              ? 'bg-pink-500/30 border border-pink-500 text-pink-400'
                              : 'bg-zinc-700 border border-zinc-600 text-zinc-400 hover:bg-zinc-600'
                          }`}
                        >
                          {option === 'him' ? `👨 ${hisName}` : option === 'her' ? `👩 ${herName}` : '👫 Both'}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* General Profile Notes */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">📝 General Notes</label>
                    <textarea
                      value={hisProfileEditForm.profileNotes || ''}
                      onChange={e => setHisProfileEditForm({ ...hisProfileEditForm, profileNotes: e.target.value })}
                      placeholder="Any notes about preferences, what works for you, etc..."
                      maxLength={500}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white resize-none"
                      rows={3}
                    />
                    <p className="text-zinc-500 text-xs mt-1 text-right">{(hisProfileEditForm.profileNotes || '').length}/500</p>
                  </div>

                  {/* Available Days for Scheduling */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">📅 Available Days</label>
                    <p className="text-zinc-500 text-xs mb-2">Days when activities can be scheduled</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { day: 0, label: 'Sun' },
                        { day: 1, label: 'Mon' },
                        { day: 2, label: 'Tue' },
                        { day: 3, label: 'Wed' },
                        { day: 4, label: 'Thu' },
                        { day: 5, label: 'Fri' },
                        { day: 6, label: 'Sat' },
                      ].map(({ day, label }) => {
                        const isSelected = (hisProfileEditForm.availableDays || []).includes(day);
                        return (
                          <button
                            key={day}
                            onClick={() => {
                              const current = hisProfileEditForm.availableDays || [];
                              const newDays = isSelected
                                ? current.filter(d => d !== day)
                                : [...current, day].sort((a, b) => a - b);
                              setHisProfileEditForm({ ...hisProfileEditForm, availableDays: newDays });
                            }}
                            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                              isSelected
                                ? 'bg-lime-500 text-zinc-900'
                                : 'bg-zinc-700 text-zinc-300 hover:bg-zinc-600'
                            }`}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                    {(hisProfileEditForm.availableDays || []).length === 0 && (
                      <p className="text-zinc-500 text-xs mt-2 italic">No days selected - all days available</p>
                    )}
                  </div>

                  {/* Visual Sharing Prompt - Show when stats exist */}
                  {(hisProfileEditForm.size || hisProfileEditForm.girth || hisProfileEditForm.cumIntensity || hisProfileEditForm.precumProduction) && (
                    <div className="bg-gradient-to-r from-pink-500/10 to-purple-500/10 rounded-xl p-4 border border-pink-500/30">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="text-xl">📸</span>
                        <p className="text-white font-medium text-sm">Visual Verification (Optional)</p>
                      </div>
                      <p className="text-zinc-400 text-xs mb-4">
                        Want to share pics/videos to back up your stats? This helps us understand our users better and creates more accurate content recommendations.
                      </p>

                      <div className="mb-4">
                        <label className="block text-sm text-zinc-400 mb-2">Would you be open to sharing visuals?</label>
                        <div className="flex gap-2">
                          {([
                            { value: 'yes', label: '💕 Yes!', color: 'pink' },
                            { value: 'maybe', label: '🤔 Maybe', color: 'amber' },
                            { value: 'no', label: 'Not now', color: 'zinc' },
                          ] as const).map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => setHisProfileEditForm({ ...hisProfileEditForm, visualSharingOpen: opt.value })}
                              className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                                hisProfileEditForm.visualSharingOpen === opt.value
                                  ? opt.color === 'pink' ? 'border-pink-500 bg-pink-500/20 text-pink-400'
                                    : opt.color === 'amber' ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                                    : 'border-zinc-500 bg-zinc-700 text-zinc-300'
                                  : 'border-zinc-600 bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Show URL inputs when yes or maybe */}
                      {(hisProfileEditForm.visualSharingOpen === 'yes' || hisProfileEditForm.visualSharingOpen === 'maybe') && (
                        <div className="space-y-3 pt-3 border-t border-pink-500/20">
                          <p className="text-zinc-500 text-xs">Share links to private folders (Google Drive, Dropbox, etc.) - only seen by admins</p>

                          {hisProfileEditForm.size && (
                            <div>
                              <label className="block text-xs text-zinc-500 mb-1">📏 Size verification</label>
                              <input
                                type="text"
                                value={hisProfileEditForm.visualUrls?.sizeUrl || ''}
                                onChange={e => setHisProfileEditForm({
                                  ...hisProfileEditForm,
                                  visualUrls: { ...hisProfileEditForm.visualUrls, sizeUrl: e.target.value }
                                })}
                                placeholder="Link to pic/video..."
                                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                              />
                            </div>
                          )}

                          {hisProfileEditForm.cumIntensity && (
                            <div>
                              <label className="block text-xs text-zinc-500 mb-1">💦 Cum shot verification</label>
                              <input
                                type="text"
                                value={hisProfileEditForm.visualUrls?.cumUrl || ''}
                                onChange={e => setHisProfileEditForm({
                                  ...hisProfileEditForm,
                                  visualUrls: { ...hisProfileEditForm.visualUrls, cumUrl: e.target.value }
                                })}
                                placeholder="Link to pic/video..."
                                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                              />
                            </div>
                          )}

                          {hisProfileEditForm.precumProduction && (
                            <div>
                              <label className="block text-xs text-zinc-500 mb-1">💧 Precum verification</label>
                              <input
                                type="text"
                                value={hisProfileEditForm.visualUrls?.precumUrl || ''}
                                onChange={e => setHisProfileEditForm({
                                  ...hisProfileEditForm,
                                  visualUrls: { ...hisProfileEditForm.visualUrls, precumUrl: e.target.value }
                                })}
                                placeholder="Link to pic/video..."
                                className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                              />
                            </div>
                          )}

                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">🔗 Other / General</label>
                            <input
                              type="text"
                              value={hisProfileEditForm.visualUrls?.otherUrl || ''}
                              onChange={e => setHisProfileEditForm({
                                ...hisProfileEditForm,
                                visualUrls: { ...hisProfileEditForm.visualUrls, otherUrl: e.target.value }
                              })}
                              placeholder="Any other links..."
                              className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                            />
                          </div>

                          <div>
                            <label className="block text-xs text-zinc-500 mb-1">Notes about visuals (optional)</label>
                            <textarea
                              value={hisProfileEditForm.visualSharingNotes || ''}
                              onChange={e => setHisProfileEditForm({ ...hisProfileEditForm, visualSharingNotes: e.target.value })}
                              placeholder="Any context about the visuals..."
                              className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 resize-none"
                              rows={2}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Last Activity Display */}
                  {coupleData?.lastActivityAt && (
                    <div className="text-center text-zinc-500 text-sm pt-2">
                      Last updated: {new Date(coupleData.lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Save Button - Always visible */}
            <div className="pt-4 mt-4 border-t border-zinc-700">
              <PrimaryButton onClick={saveHisProfileModal} className="w-full">
                Save Changes
              </PrimaryButton>
            </div>
          </div>
        </Modal>

        {/* Her Profile Modal (Dedicated Screen) */}
        <Modal
          isOpen={showHerProfileModal}
          onClose={() => setShowHerProfileModal(false)}
          title="Her Profile"
        >
          <div className="p-4">
            {/* Tab Navigation */}
            <div className="flex gap-2 mb-4">
              {(['stats', 'details', 'settings'] as const).map(t => (
                <button
                  key={t}
                  onClick={() => setHerProfileTab(t)}
                  className={`flex-1 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${
                    herProfileTab === t
                      ? 'bg-pink-500/30 border border-pink-500 text-pink-400'
                      : 'bg-zinc-700/50 border border-zinc-600 text-zinc-400 hover:text-white'
                  }`}
                >
                  {t === 'stats' ? '📊 Stats' : t === 'details' ? '📝 Details' : '⚙️ Settings'}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pb-4">
              {herProfileTab === 'stats' && (
                <>
                  {/* Wetness Level */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">💦 Typical Wetness</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {HER_WETNESS_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHerProfileEditForm({ ...herProfileEditForm, typicalWetness: item.value })}
                          className={`flex-1 min-w-[55px] flex flex-col items-center p-2 rounded-lg transition-colors ${
                            herProfileEditForm.typicalWetness === item.value
                              ? 'bg-pink-500/30 border border-pink-500'
                              : 'bg-zinc-700/50 border border-zinc-600 hover:border-zinc-500'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Squirting Ability */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">🌊 Squirting Ability</label>
                    <div className="flex gap-1.5 flex-wrap">
                      {HER_SQUIRT_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHerProfileEditForm({ ...herProfileEditForm, squirtingAbility: item.value })}
                          className={`flex-1 min-w-[50px] flex flex-col items-center p-2 rounded-lg transition-colors ${
                            herProfileEditForm.squirtingAbility === item.value
                              ? 'bg-violet-500/30 border border-violet-500'
                              : 'bg-zinc-700/50 border border-zinc-600 hover:border-zinc-500'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {herProfileTab === 'details' && (
                <>
                  {/* Sensitivity */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">⚡ Sensitivity Level</label>
                    <div className="flex gap-1.5">
                      {HER_SENSITIVITY_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHerProfileEditForm({ ...herProfileEditForm, sensitivity: item.value })}
                          className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                            herProfileEditForm.sensitivity === item.value
                              ? 'bg-fuchsia-500/30 border border-fuchsia-500'
                              : 'bg-zinc-700/50 border border-zinc-600 hover:border-zinc-500'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400">{item.label}</span>
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={herProfileEditForm.sensitivityNotes || ''}
                      onChange={e => setHerProfileEditForm({ ...herProfileEditForm, sensitivityNotes: e.target.value })}
                      placeholder="Notes about sensitivity (optional)..."
                      maxLength={200}
                      className="w-full mt-2 bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>

                  {/* Orgasm Ease */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">🎆 Orgasm Ease</label>
                    <div className="flex gap-1.5">
                      {HER_ORGASM_EASE_SCALE.map(item => (
                        <button
                          key={item.value}
                          onClick={() => setHerProfileEditForm({ ...herProfileEditForm, orgasmEase: item.value })}
                          className={`flex-1 flex flex-col items-center p-2 rounded-lg transition-colors ${
                            herProfileEditForm.orgasmEase === item.value
                              ? 'bg-rose-500/30 border border-rose-500'
                              : 'bg-zinc-700/50 border border-zinc-600 hover:border-zinc-500'
                          }`}
                        >
                          <span className="text-xl">{item.emoji}</span>
                          <span className="text-xs text-zinc-400">{item.label}</span>
                        </button>
                      ))}
                    </div>
                    <textarea
                      value={herProfileEditForm.orgasmNotes || ''}
                      onChange={e => setHerProfileEditForm({ ...herProfileEditForm, orgasmNotes: e.target.value })}
                      placeholder="Notes about orgasm (optional)..."
                      maxLength={200}
                      className="w-full mt-2 bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>

                  {/* Wetness Notes */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">💦 Wetness Notes</label>
                    <textarea
                      value={herProfileEditForm.wetnessNotes || ''}
                      onChange={e => setHerProfileEditForm({ ...herProfileEditForm, wetnessNotes: e.target.value })}
                      placeholder="Any notes about wetness patterns..."
                      maxLength={200}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>

                  {/* Squirting Notes */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">🌊 Squirting Notes</label>
                    <textarea
                      value={herProfileEditForm.squirtingNotes || ''}
                      onChange={e => setHerProfileEditForm({ ...herProfileEditForm, squirtingNotes: e.target.value })}
                      placeholder="Any notes about squirting patterns, what triggers it..."
                      maxLength={200}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm resize-none"
                      rows={2}
                    />
                  </div>
                </>
              )}

              {herProfileTab === 'settings' && (
                <>
                  {/* Visual Sharing */}
                  <div>
                    <label className="block text-sm text-zinc-400 mb-2">📸 Open to Visual Sharing?</label>
                    <p className="text-zinc-500 text-xs mb-2">Optional - share visuals with your reviewer for personalized feedback</p>
                    <div className="flex gap-2">
                      {([
                        { value: 'yes', label: 'Yes!', color: 'pink' },
                        { value: 'maybe', label: 'Maybe', color: 'amber' },
                        { value: 'no', label: 'No', color: 'zinc' }
                      ] as const).map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setHerProfileEditForm({ ...herProfileEditForm, visualSharingOpen: opt.value })}
                          className={`flex-1 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                            herProfileEditForm.visualSharingOpen === opt.value
                              ? opt.color === 'pink' ? 'border-pink-500 bg-pink-500/20 text-pink-400'
                                : opt.color === 'amber' ? 'border-amber-500 bg-amber-500/20 text-amber-400'
                                : 'border-zinc-500 bg-zinc-500/20 text-zinc-400'
                              : 'border-zinc-600 bg-zinc-700/50 text-zinc-400 hover:border-zinc-500'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>

                    {herProfileEditForm.visualSharingOpen === 'yes' && (
                      <div className="mt-3 space-y-2">
                        <p className="text-pink-400 text-xs">Optional: Share links to visuals</p>
                        <div>
                          <label className="block text-xs text-zinc-500 mb-1">💦 Wetness verification</label>
                          <input
                            type="text"
                            value={herProfileEditForm.visualUrls?.wetnessUrl || ''}
                            onChange={e => setHerProfileEditForm({
                              ...herProfileEditForm,
                              visualUrls: { ...herProfileEditForm.visualUrls, wetnessUrl: e.target.value }
                            })}
                            placeholder="Link to wetness photo/video..."
                            className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-500 mb-1">🌊 Squirt verification</label>
                          <input
                            type="text"
                            value={herProfileEditForm.visualUrls?.squirtUrl || ''}
                            onChange={e => setHerProfileEditForm({
                              ...herProfileEditForm,
                              visualUrls: { ...herProfileEditForm.visualUrls, squirtUrl: e.target.value }
                            })}
                            placeholder="Link to squirt video..."
                            className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-500 mb-1">🔗 Other</label>
                          <input
                            type="text"
                            value={herProfileEditForm.visualUrls?.otherUrl || ''}
                            onChange={e => setHerProfileEditForm({
                              ...herProfileEditForm,
                              visualUrls: { ...herProfileEditForm.visualUrls, otherUrl: e.target.value }
                            })}
                            placeholder="Any other visuals..."
                            className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-zinc-500 mb-1">Notes about visuals (optional)</label>
                          <textarea
                            value={herProfileEditForm.visualSharingNotes || ''}
                            onChange={e => setHerProfileEditForm({ ...herProfileEditForm, visualSharingNotes: e.target.value })}
                            placeholder="Any context about the visuals..."
                            className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm placeholder-zinc-500 resize-none"
                            rows={2}
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Last Activity Display */}
                  {coupleData?.lastActivityAt && (
                    <div className="text-center text-zinc-500 text-sm pt-2">
                      Last updated: {new Date(coupleData.lastActivityAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Save Button - Always visible */}
            <div className="pt-4 mt-4 border-t border-zinc-700">
              <PrimaryButton onClick={saveHerProfileModal} className="w-full">
                Save Changes
              </PrimaryButton>
            </div>
          </div>
        </Modal>

        {/* New Couple Codes Modal */}
        {showNewCode && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-md border border-zinc-700 text-center">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ backgroundColor: COLORS.lime }}
              >
                <Check className="w-8 h-8 text-zinc-900" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">You're All Set!</h2>
              <p className="text-zinc-400 mb-4">
                Save these codes - you'll need them!
              </p>

              {/* Access Code - for login */}
              <div className="bg-pink-500/10 border border-pink-500/30 rounded-xl p-4 mb-3">
                <p className="text-pink-400 text-xs font-semibold mb-1">🔐 YOUR ACCESS CODE (Keep Private!)</p>
                <p className="text-2xl font-mono font-bold tracking-wider text-pink-400">
                  {newAccessCode}
                </p>
                <p className="text-zinc-500 text-xs mt-1">Use this to log back in</p>
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(newAccessCode);
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                  className="mt-2 px-3 py-1.5 bg-pink-500/20 text-pink-400 rounded-lg text-xs font-medium hover:bg-pink-500/30 flex items-center gap-1 mx-auto"
                >
                  {codeCopied ? <CheckCheck className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                  {codeCopied ? 'Copied!' : 'Copy Access Code'}
                </button>
              </div>

              {/* Sharing Code - for friends */}
              <div className="bg-zinc-900 rounded-xl p-4 border border-zinc-700 mb-4">
                <p className="text-lime-400 text-xs font-semibold mb-1">📤 SHARING CODE (Share with Friends)</p>
                <p className="text-2xl font-mono font-bold tracking-wider" style={{ color: COLORS.lime }}>
                  {newCoupleCode}
                </p>
                <p className="text-zinc-500 text-xs mt-1">Friends use this to recommend clips</p>
                <button
                  onClick={async () => {
                    await navigator.clipboard.writeText(newCoupleCode);
                    setCodeCopied(true);
                    setTimeout(() => setCodeCopied(false), 2000);
                  }}
                  className="mt-2 px-3 py-1.5 bg-lime-500/20 text-lime-400 rounded-lg text-xs font-medium hover:bg-lime-500/30 flex items-center gap-1 mx-auto"
                >
                  <Copy className="w-3 h-3" />
                  Copy Sharing Code
                </button>
              </div>

              <PrimaryButton
                onClick={() => {
                  setShowNewCode(false);
                  setShowOnboarding(true);
                  setOnboardingStep(0);
                }}
                className="w-full"
              >
                Got It, Continue
              </PrimaryButton>
            </div>
          </div>
        )}

        {/* Feedback Prompt for Old Tried Clips */}
        {showFeedbackPrompt && currentFeedbackClipId && (() => {
          const triedClip = tried.find(t => t.clipId === currentFeedbackClipId);
          const clip = clips.find(c => c.id === currentFeedbackClipId);
          if (!triedClip || !clip) return null;
          return (
            <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
              <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-md border border-zinc-700">
                <div className="text-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center mx-auto mb-3">
                    <Lightbulb className="w-6 h-6 text-amber-400" />
                  </div>
                  <h2 className="text-xl font-bold text-white mb-1">Quick Feedback!</h2>
                  <p className="text-zinc-400 text-sm">
                    You tried this clip - tell us more!
                  </p>
                  <p className="text-zinc-500 text-xs mt-1">
                    {pendingFeedbackClips.length} clip{pendingFeedbackClips.length > 1 ? 's' : ''} to review
                  </p>
                </div>

                <div className="space-y-4">
                  {/* Clip preview - clickable to watch */}
                  <button
                    onClick={() => setWatchingClip(clip)}
                    className="w-full bg-zinc-700/50 hover:bg-zinc-700 rounded-lg p-3 transition-colors group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg bg-zinc-600 flex items-center justify-center group-hover:bg-lime-500/30 transition-colors">
                        <Play className="w-6 h-6 text-zinc-400 group-hover:text-lime-400" />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="text-white font-medium text-sm truncate">{clip.title}</p>
                        <p className="text-zinc-500 text-xs">Tap to watch again</p>
                      </div>
                      <span className={`text-lg ${triedClip.rating === 'hot' ? 'text-red-400' : triedClip.rating === 'good' ? 'text-green-400' : triedClip.rating === 'meh' ? 'text-amber-400' : 'text-zinc-500'}`}>
                        {triedClip.rating === 'hot' ? '🔥🔥🔥' : triedClip.rating === 'good' ? '🔥🔥' : triedClip.rating === 'meh' ? '🔥' : '❌'}
                      </span>
                    </div>
                  </button>

                  {/* Would do again */}
                  <div>
                    <label className="block text-sm text-lime-400 mb-2">🔄 Would you do it again?</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'yes', label: 'Yes!', emoji: '✅' },
                        { value: 'maybe', label: 'Maybe', emoji: '🤔' },
                        { value: 'no', label: 'No', emoji: '❌' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setPendingFeedbackForm({ ...pendingFeedbackForm, wouldDoAgain: opt.value as 'yes' | 'maybe' | 'no' })}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                            pendingFeedbackForm.wouldDoAgain === opt.value
                              ? 'bg-lime-500/20 border-lime-500 text-lime-400'
                              : 'border-zinc-600 bg-zinc-700 text-zinc-300'
                          }`}
                        >
                          {opt.emoji} {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Inspired differently */}
                  <div>
                    <label className="block text-sm text-amber-400 mb-2">💡 Did it inspire you to do something differently?</label>
                    <div className="flex gap-2">
                      {[
                        { value: 'yes', label: 'Yes', emoji: '💡' },
                        { value: 'no', label: 'No', emoji: '➖' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setPendingFeedbackForm({ ...pendingFeedbackForm, inspiresDifferent: opt.value as 'yes' | 'no', howDifferent: opt.value === 'no' ? '' : pendingFeedbackForm.howDifferent })}
                          className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                            pendingFeedbackForm.inspiresDifferent === opt.value
                              ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                              : 'border-zinc-600 bg-zinc-700 text-zinc-300'
                          }`}
                        >
                          {opt.emoji} {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* How differently */}
                  {pendingFeedbackForm.inspiresDifferent === 'yes' && (
                    <div>
                      <label className="block text-sm text-amber-400 mb-1">How would you do it differently?</label>
                      <textarea
                        value={pendingFeedbackForm.howDifferent}
                        onChange={e => setPendingFeedbackForm({ ...pendingFeedbackForm, howDifferent: e.target.value })}
                        placeholder="What would you change or try next time?"
                        className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
                      />
                    </div>
                  )}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={skipFeedback}
                    className="flex-1 px-4 py-2 bg-zinc-700 text-zinc-300 rounded-xl hover:bg-zinc-600"
                  >
                    Skip
                  </button>
                  <PrimaryButton
                    onClick={savePendingFeedback}
                    disabled={!pendingFeedbackForm.wouldDoAgain}
                    className="flex-1"
                  >
                    Save
                  </PrimaryButton>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Activity Follow-Up Survey Modal */}
        {showFollowUpSurvey && currentFollowUpActivity && coupleData && (
          <ActivityFollowUpModal
            activity={currentFollowUpActivity}
            clip={clips.find(c => c.id === currentFollowUpActivity.clipId)}
            coupleData={coupleData}
            remainingCount={pendingFollowUpActivities.length}
            onComplete={saveActivityFollowUpSurvey}
            onSkip={skipActivityFollowUp}
            onDismissAll={dismissAllActivityFollowUps}
            onWatchClip={setWatchingClip}
          />
        )}

        {/* Recommendations Modal */}
        <Modal
          isOpen={showRecommendations}
          onClose={() => setShowRecommendations(false)}
          title="Recommendations"
        >
          <div className="p-6">
            <p className="text-zinc-400 text-sm mb-4">
              Friends have recommended these clips for your date night!
            </p>

            {recommendations.length === 0 ? (
              <EmptyState
                icon={<Gift className="w-8 h-8" style={{ color: COLORS.lime }} />}
                title="No recommendations yet"
                description="Share your code with friends!"
              />
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {recommendations.map(rec => {
                  const clip = clips.find(c => c.id === rec.clipId);
                  const cat = clip ? CATEGORIES.find(c => c.id === clip.categoryId) : null;
                  return clip && cat && (
                    <div key={rec.id}>
                      <button
                        onClick={() => viewRecommendation(rec)}
                        className={`w-full bg-zinc-700/50 hover:bg-zinc-700 rounded-xl p-4 text-left transition-colors ${!rec.seen ? 'border-2 border-purple-500/50' : 'border border-zinc-600'}`}
                      >
                        <div className="flex items-start gap-3">
                          <span className="text-2xl">{cat.emoji}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <p className="text-white font-medium truncate">{clip.title}</p>
                              {!rec.seen && (
                                <span className="px-2 py-0.5 bg-purple-500 text-white text-xs rounded-full">New</span>
                              )}
                            </div>
                            <p className="text-zinc-500 text-sm">{cat.name}</p>
                            {rec.message && (
                              <p className="text-zinc-400 text-sm mt-2 italic">"{rec.message}"</p>
                            )}
                            <p className="text-zinc-600 text-xs mt-2">From {rec.fromName}</p>
                          </div>
                          <Play className="w-5 h-5 text-zinc-400 flex-shrink-0" />
                        </div>
                      </button>
                      {/* Reply to recommendation */}
                      {rec.coupleReply ? (
                        <div className="mt-2 bg-zinc-700/50 rounded p-2">
                          <p className="text-xs text-zinc-500">Your reply:</p>
                          <p className="text-sm text-zinc-300">{rec.coupleReply}</p>
                        </div>
                      ) : (
                        <div className="mt-2 flex gap-2">
                          <input
                            type="text"
                            id={`rec-reply-modal-${rec.id}`}
                            placeholder="Reply to this recommendation..."
                            className="flex-1 bg-zinc-700 border border-zinc-600 rounded px-2 py-1 text-white text-xs placeholder-zinc-500"
                            onKeyDown={async (e) => {
                              if (e.key === 'Enter') {
                                const input = e.target as HTMLInputElement;
                                if (!input.value.trim()) return;
                                const updated = { ...rec, coupleReply: input.value.trim(), coupleRepliedAt: new Date().toISOString() };
                                await d1Upsert('couple_recommendations', updated);
                                await saveRecommendations(recommendations.map(r => r.id === rec.id ? updated : r));
                                input.value = '';
                              }
                            }}
                          />
                          <button
                            onClick={async () => {
                              const input = document.getElementById(`rec-reply-modal-${rec.id}`) as HTMLInputElement;
                              if (!input?.value.trim()) return;
                              const updated = { ...rec, coupleReply: input.value.trim(), coupleRepliedAt: new Date().toISOString() };
                              await d1Upsert('couple_recommendations', updated);
                              await saveRecommendations(recommendations.map(r => r.id === rec.id ? updated : r));
                              input.value = '';
                            }}
                            className="px-2 py-1 bg-lime-500/20 text-lime-400 rounded text-xs"
                          >
                            Reply
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </Modal>

        {/* Onboarding Modal */}
        {showOnboarding && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-auto">
            <div className="bg-zinc-800 rounded-2xl p-6 w-full max-w-lg border border-zinc-700 my-8">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-white">
                  {onboardingStep < onboardingCategories.length ? 'Set Your Preferences' : onboardingStep === onboardingCategories.length ? 'Quick Feedback' : 'All Set!'}
                </h2>
                <button onClick={() => {
                  // Mark as dismissed so we don't keep prompting
                  if (coupleData?.coupleId) {
                    localStorage.setItem(`vb-onboarding-dismissed:${coupleData.coupleId}`, 'true');
                  }
                  setShowOnboarding(false);
                }} className="text-zinc-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <p className="text-zinc-400 text-sm mb-4">
                {onboardingStep < onboardingCategories.length ? "Tell us what you're into! This helps personalize your experience." : onboardingStep === onboardingCategories.length ? 'Help us improve! (Optional)' : ''}
              </p>

              <div className="h-1 bg-zinc-700 rounded-full mb-6">
                <div
                  className="h-full rounded-full transition-all"
                  style={{ width: `${((onboardingStep + 1) / (onboardingCategories.length + 2)) * 100}%`, backgroundColor: COLORS.lime }}
                />
              </div>

              {onboardingStep < onboardingCategories.length ? (
                <div>
                  {(() => {
                    const cat = onboardingCategories[onboardingStep]!;
                    const currentPref = preferences[cat.id];
                    return (
                      <>
                        <div className={`bg-gradient-to-br ${cat.color} rounded-xl p-6 text-center mb-4`}>
                          <span className="text-5xl">{cat.emoji}</span>
                          <h3 className="text-2xl font-bold text-white mt-2">{cat.name}</h3>
                          <p className="text-white/70 mt-1">{cat.description}</p>
                        </div>

                        <p className="text-zinc-400 text-center mb-4">How do you both feel about this?</p>

                        <div className="grid grid-cols-2 gap-3 mb-4">
                          {[
                            { value: 'love' as CategoryPreference, label: '❤️ Love it!', color: 'border-pink-500 bg-pink-500/20 text-pink-400' },
                            { value: 'like' as CategoryPreference, label: '👍 We like it', color: 'border-green-500 bg-green-500/20 text-green-400' },
                            { value: 'curious' as CategoryPreference, label: '🤔 Curious', color: 'border-amber-500 bg-amber-500/20 text-amber-400' },
                            { value: 'nope' as CategoryPreference, label: '❌ Not for us', color: 'border-zinc-500 bg-zinc-500/20 text-zinc-400' },
                          ].map(opt => (
                            <button
                              key={opt.value}
                              onClick={() => savePreference(cat.id, opt.value)}
                              className={`px-4 py-3 rounded-xl text-sm font-medium border-2 ${currentPref === opt.value ? opt.color : 'border-zinc-600 bg-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>

                        <div className="flex gap-3">
                          {onboardingStep > 0 && (
                            <button
                              onClick={() => setOnboardingStep(onboardingStep - 1)}
                              className="flex-1 bg-zinc-700 text-zinc-300 px-4 py-3 rounded-xl font-semibold"
                            >
                              <ArrowLeft className="w-4 h-4 inline mr-2" /> Back
                            </button>
                          )}
                          <button
                            onClick={() => setOnboardingStep(onboardingStep + 1)}
                            className="flex-1 px-4 py-3 rounded-xl font-semibold text-zinc-900"
                            style={{ backgroundColor: COLORS.lime }}
                          >
                            {currentPref ? 'Next' : 'Skip'} <ArrowRight className="w-4 h-4 inline ml-2" />
                          </button>
                        </div>
                      </>
                    );
                  })()}
                </div>
              ) : onboardingStep === onboardingCategories.length ? (
                /* Feedback Step */
                <div className="space-y-5">
                  {/* Would Submit Clips */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">Would you consider submitting your own clips?</label>
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'yes' as const, label: '✅ Yes!', color: 'border-green-500 bg-green-500/20 text-green-400' },
                        { value: 'maybe' as const, label: '🤔 Maybe', color: 'border-amber-500 bg-amber-500/20 text-amber-400' },
                        { value: 'no' as const, label: '❌ No', color: 'border-zinc-500 bg-zinc-500/20 text-zinc-400' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setFeedbackForm({ ...feedbackForm, wouldSubmitClips: opt.value })}
                          className={`px-3 py-2 rounded-xl text-sm font-medium border-2 ${feedbackForm.wouldSubmitClips === opt.value ? opt.color : 'border-zinc-600 bg-zinc-700 text-zinc-300 hover:border-zinc-500'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Product Excitement */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">How excited are you about this product?</label>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setFeedbackForm({ ...feedbackForm, productExcitement: star as 1 | 2 | 3 | 4 | 5 })}
                          className={`text-3xl transition-transform hover:scale-110 ${(feedbackForm.productExcitement ?? 0) >= star ? 'text-amber-400' : 'text-zinc-600'}`}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                    {feedbackForm.productExcitement && (
                      <p className="text-center text-zinc-500 text-xs mt-1">
                        {feedbackForm.productExcitement === 1 && 'Not very excited'}
                        {feedbackForm.productExcitement === 2 && 'Slightly interested'}
                        {feedbackForm.productExcitement === 3 && 'Moderately excited'}
                        {feedbackForm.productExcitement === 4 && 'Pretty excited!'}
                        {feedbackForm.productExcitement === 5 && 'Super excited!!! 🎉'}
                      </p>
                    )}
                  </div>

                  {/* General Feedback */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">Any other feedback? (Optional)</label>
                    <textarea
                      value={feedbackForm.generalFeedback}
                      onChange={e => setFeedbackForm({ ...feedbackForm, generalFeedback: e.target.value })}
                      placeholder="Tell us what you think, suggestions, ideas..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-20 resize-none"
                      maxLength={500}
                    />
                    <p className="text-zinc-500 text-xs text-right mt-1">{feedbackForm.generalFeedback.length}/500</p>
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setOnboardingStep(onboardingStep - 1)}
                      className="flex-1 bg-zinc-700 text-zinc-300 px-4 py-3 rounded-xl font-semibold"
                    >
                      <ArrowLeft className="w-4 h-4 inline mr-2" /> Back
                    </button>
                    <button
                      onClick={async () => {
                        if (feedbackForm.wouldSubmitClips || feedbackForm.productExcitement || feedbackForm.generalFeedback) {
                          await saveFeedback({
                            wouldSubmitClips: feedbackForm.wouldSubmitClips || undefined,
                            productExcitement: feedbackForm.productExcitement || undefined,
                            generalFeedback: feedbackForm.generalFeedback || undefined,
                          });
                        }
                        setOnboardingStep(onboardingStep + 1);
                      }}
                      className="flex-1 px-4 py-3 rounded-xl font-semibold text-zinc-900"
                      style={{ backgroundColor: COLORS.lime }}
                    >
                      {feedbackForm.wouldSubmitClips || feedbackForm.productExcitement || feedbackForm.generalFeedback ? 'Submit' : 'Skip'} <ArrowRight className="w-4 h-4 inline ml-2" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-6">
                  <div className="text-5xl mb-4">🎉</div>
                  <h3 className="text-2xl font-bold text-white mb-2">You're All Set!</h3>
                  <p className="text-zinc-400 mb-6">Your preferences have been saved. Time to explore!</p>
                  <PrimaryButton onClick={() => setShowOnboarding(false)} className="w-full">
                    Start Exploring <ArrowRight className="w-4 h-4" />
                  </PrimaryButton>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tried Modal */}
        <Modal
          isOpen={!!showTriedModal}
          onClose={() => setShowTriedModal(null)}
          title="We Tried This!"
        >
          {showTriedModal && (
            <div className="p-6">
              <p className="text-zinc-400 text-sm mb-4">
                How was <span className="text-white">{showTriedModal.title}</span>?
              </p>

              <div className="grid grid-cols-2 gap-3 mb-4">
                {TRIED_RATINGS.map(opt => (
                  <button
                    key={opt.value}
                    onClick={() => setTriedForm({ ...triedForm, rating: opt.value })}
                    className={`px-4 py-3 rounded-xl text-sm font-medium border-2 ${triedForm.rating === opt.value ? `bg-gradient-to-r ${opt.color} text-white border-transparent` : 'border-zinc-600 bg-zinc-700 text-zinc-300'}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="space-y-3 mb-4">
                <div>
                  <label className="block text-sm text-blue-400 mb-1">👨 {hisName}'s Thoughts</label>
                  <textarea
                    value={triedForm.hisThoughts}
                    onChange={e => setTriedForm({ ...triedForm, hisThoughts: e.target.value })}
                    placeholder="What happened? How was it?"
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
                  />
                </div>
                <div>
                  <label className="block text-sm text-pink-400 mb-1">👩 {herName}'s Thoughts</label>
                  <textarea
                    value={triedForm.herThoughts}
                    onChange={e => setTriedForm({ ...triedForm, herThoughts: e.target.value })}
                    placeholder="What happened? How was it?"
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
                  />
                </div>

                {/* Would do again? */}
                <div>
                  <label className="block text-sm text-lime-400 mb-2">🔄 Would you do it again?</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'yes', label: 'Yes!', emoji: '👍' },
                      { value: 'maybe', label: 'Maybe', emoji: '🤔' },
                      { value: 'no', label: 'No', emoji: '👎' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setTriedForm({ ...triedForm, wouldDoAgain: opt.value as 'yes' | 'maybe' | 'no' })}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                          triedForm.wouldDoAgain === opt.value
                            ? 'bg-lime-500/20 border-lime-500 text-lime-400'
                            : 'border-zinc-600 bg-zinc-700 text-zinc-300'
                        }`}
                      >
                        {opt.emoji} {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Inspires different? */}
                <div>
                  <label className="block text-sm text-amber-400 mb-2">💡 Did this clip inspire you to try something differently?</label>
                  <div className="flex gap-2">
                    {[
                      { value: 'yes', label: 'Yes!', emoji: '✨' },
                      { value: 'no', label: 'No', emoji: '➖' }
                    ].map(opt => (
                      <button
                        key={opt.value}
                        onClick={() => setTriedForm({ ...triedForm, inspiresDifferent: opt.value as 'yes' | 'no', howDifferent: opt.value === 'no' ? '' : triedForm.howDifferent })}
                        className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium border ${
                          triedForm.inspiresDifferent === opt.value
                            ? 'bg-amber-500/20 border-amber-500 text-amber-400'
                            : 'border-zinc-600 bg-zinc-700 text-zinc-300'
                        }`}
                      >
                        {opt.emoji} {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* How differently - only show if inspiresDifferent is yes */}
                {triedForm.inspiresDifferent === 'yes' && (
                  <div>
                    <label className="block text-sm text-amber-400 mb-1">How would you do it differently?</label>
                    <textarea
                      value={triedForm.howDifferent}
                      onChange={e => setTriedForm({ ...triedForm, howDifferent: e.target.value })}
                      placeholder="What would you change or try next time?"
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-16 resize-none"
                    />
                  </div>
                )}

                {/* His load review - shown for cum/oral/squirt/wet categories */}
                {showTriedModal && ['cum', 'oral', 'squirt', 'wet'].some(cat =>
                  showTriedModal.categoryId === cat || showTriedModal.additionalCategoryIds?.includes(cat)
                ) && (
                  <div className="border-t border-zinc-600 pt-3">
                    <label className="block text-sm text-blue-300 mb-3 font-medium">💦 Review His Load</label>

                    <div className="mb-3">
                      <label className="block text-xs text-zinc-400 mb-1.5">Volume</label>
                      <div className="flex gap-1">
                        {HIS_CUM_SCALE.map(item => (
                          <button
                            key={item.value}
                            onClick={() => setTriedForm({ ...triedForm, cumVolume: item.value })}
                            className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                              triedForm.cumVolume === item.value
                                ? 'bg-blue-500/30 border border-blue-500'
                                : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-base">{item.emoji}</span>
                            <span className="text-[10px] text-zinc-400 mt-0.5">{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs text-zinc-400 mb-1.5">Thickness</label>
                      <div className="flex gap-1">
                        {HIS_CONSISTENCY_SCALE.map(item => (
                          <button
                            key={item.value}
                            onClick={() => setTriedForm({ ...triedForm, cumConsistency: item.value })}
                            className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                              triedForm.cumConsistency === item.value
                                ? 'bg-purple-500/30 border border-purple-500'
                                : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-base">{item.emoji}</span>
                            <span className="text-[10px] text-zinc-400 mt-0.5">{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs text-zinc-400 mb-1.5">Taste</label>
                      <div className="flex gap-1">
                        {HIS_TASTE_SCALE.map(item => (
                          <button
                            key={item.value}
                            onClick={() => setTriedForm({ ...triedForm, cumTaste: item.value })}
                            className={`flex-1 flex flex-col items-center p-1.5 rounded-lg transition-colors ${
                              triedForm.cumTaste === item.value
                                ? 'bg-amber-500/30 border border-amber-500'
                                : 'bg-zinc-700/50 border border-transparent hover:bg-zinc-700'
                            }`}
                          >
                            <span className="text-base">{item.emoji}</span>
                            <span className="text-[10px] text-zinc-400 mt-0.5">{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    <textarea
                      value={triedForm.cumNotes}
                      onChange={e => setTriedForm({ ...triedForm, cumNotes: e.target.value })}
                      placeholder="Any notes about his load... (optional)"
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm h-12 resize-none"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm text-zinc-400 mb-1">📅 When did you try this?</label>
                  <input
                    type="date"
                    value={triedForm.triedAt}
                    onChange={e => setTriedForm({ ...triedForm, triedAt: e.target.value })}
                    max={new Date().toISOString().split('T')[0]}
                    className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-3 py-2 text-white text-sm"
                  />
                </div>
              </div>

              <PrimaryButton
                onClick={() => triedForm.rating && markAsTried(showTriedModal.id, {
                  rating: triedForm.rating as TriedRatingValue,
                  hisThoughts: triedForm.hisThoughts,
                  herThoughts: triedForm.herThoughts,
                  triedAt: triedForm.triedAt,
                  wouldDoAgain: triedForm.wouldDoAgain || undefined,
                  inspiresDifferent: triedForm.inspiresDifferent || undefined,
                  howDifferent: triedForm.howDifferent || undefined,
                  cumVolume: triedForm.cumVolume || undefined,
                  cumConsistency: triedForm.cumConsistency || undefined,
                  cumTaste: triedForm.cumTaste || undefined,
                  cumNotes: triedForm.cumNotes || undefined
                })}
                disabled={!triedForm.rating}
                className="w-full"
              >
                <Check className="w-4 h-4" /> Save
              </PrimaryButton>
            </div>
          )}
        </Modal>

        {/* Submit Video Modal */}
        <Modal
          isOpen={showSubmissionModal}
          onClose={() => setShowSubmissionModal(false)}
          title="Submit Your Video"
        >
          <div className="p-6">
            <p className="text-zinc-400 text-sm mb-4">
              Share a video of your experience with VB supplements. Your reviewer will see this and may provide feedback!
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-zinc-300 mb-1">Video URL *</label>
                <input
                  type="url"
                  value={submissionForm.videoUrl}
                  onChange={e => setSubmissionForm({ ...submissionForm, videoUrl: e.target.value })}
                  placeholder="https://redgifs.com/watch/..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                />
                <p className="text-zinc-500 text-xs mt-1">Paste your RedGifs or other video link</p>
              </div>

              <div>
                <label className="block text-sm text-zinc-300 mb-1">Title *</label>
                <input
                  type="text"
                  value={submissionForm.title}
                  onChange={e => setSubmissionForm({ ...submissionForm, title: e.target.value })}
                  placeholder="e.g., After using Load Boost for 2 weeks"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                  maxLength={100}
                />
              </div>

              <div>
                <label className="block text-sm text-zinc-300 mb-1">Description (optional)</label>
                <textarea
                  value={submissionForm.description}
                  onChange={e => setSubmissionForm({ ...submissionForm, description: e.target.value })}
                  placeholder="Any notes about the experience..."
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white h-20 resize-none"
                  maxLength={500}
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => setShowSubmissionModal(false)}
                className="flex-1 py-3 rounded-xl font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
              >
                Cancel
              </button>
              <button
                onClick={() => saveSubmission(submissionForm)}
                disabled={!submissionForm.videoUrl.trim() || !submissionForm.title.trim()}
                className="flex-1 py-3 rounded-xl font-medium text-zinc-900 disabled:opacity-50"
                style={{ backgroundColor: COLORS.lime }}
              >
                Submit
              </button>
            </div>
          </div>
        </Modal>

        {/* Request Content Modal */}
        <Modal
          isOpen={showRequestModal}
          onClose={() => { setShowRequestModal(false); setEditingRequestId(null); setRequestForm({ type: 'clip', description: '', categoryId: '', exampleUrl: '', exampleUrls: [''], categoryName: '', whyWanted: '' }); }}
          title={editingRequestId ? "Edit Request" : "Request Content"}
        >
          <div className="p-6">
            <p className="text-zinc-400 text-sm mb-4">
              Tell your reviewer what kind of content you'd like to see more of!
            </p>

            <div className="space-y-4">
              {/* Request Type Toggle */}
              <div>
                <label className="block text-sm text-zinc-300 mb-2">What are you requesting?</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setRequestForm({ ...requestForm, type: 'clip', categoryName: '' })}
                    className={`px-4 py-3 rounded-xl font-medium border-2 ${
                      requestForm.type === 'clip'
                        ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                        : 'border-zinc-600 bg-zinc-700 text-zinc-300'
                    }`}
                  >
                    🎬 Specific Clips
                  </button>
                  <button
                    onClick={() => setRequestForm({ ...requestForm, type: 'category', categoryId: '' })}
                    className={`px-4 py-3 rounded-xl font-medium border-2 ${
                      requestForm.type === 'category'
                        ? 'border-purple-500 bg-purple-500/20 text-purple-300'
                        : 'border-zinc-600 bg-zinc-700 text-zinc-300'
                    }`}
                  >
                    📁 New Category
                  </button>
                </div>
              </div>

              {/* CLIP REQUEST FIELDS */}
              {requestForm.type === 'clip' && (
                <>
                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-2">Related category (optional)</label>
                    <select
                      value={requestForm.categoryId}
                      onChange={e => setRequestForm({ ...requestForm, categoryId: e.target.value })}
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    >
                      <option value="">Any category</option>
                      {CATEGORIES.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.emoji} {cat.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-1">Describe what you want to see *</label>
                    <textarea
                      value={requestForm.description}
                      onChange={e => setRequestForm({ ...requestForm, description: e.target.value })}
                      placeholder="e.g., More romantic massage videos, slower paced content, realistic couple scenarios..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white h-20 resize-none"
                      maxLength={500}
                    />
                  </div>

                  {/* Example URL */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-1">Example link (optional)</label>
                    <p className="text-zinc-500 text-xs mb-2">Paste a RedGifs link showing the style you're looking for</p>
                    <input
                      type="url"
                      value={requestForm.exampleUrl}
                      onChange={e => setRequestForm({ ...requestForm, exampleUrl: e.target.value })}
                      placeholder="https://redgifs.com/watch/..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                    />
                  </div>

                  {/* Why they want it */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-1">Why do you want this? (optional)</label>
                    <textarea
                      value={requestForm.whyWanted}
                      onChange={e => setRequestForm({ ...requestForm, whyWanted: e.target.value })}
                      placeholder="e.g., We love the buildup and intimacy, it matches our vibe..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white h-16 resize-none"
                      maxLength={300}
                    />
                  </div>
                </>
              )}

              {/* CATEGORY REQUEST FIELDS */}
              {requestForm.type === 'category' && (
                <>
                  {/* Category Name */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-1">Category name suggestion *</label>
                    <input
                      type="text"
                      value={requestForm.categoryName}
                      onChange={e => setRequestForm({ ...requestForm, categoryName: e.target.value })}
                      placeholder="e.g., Roleplay, Outdoor, Sensory Play..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white"
                      maxLength={50}
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-1">What would this category include? *</label>
                    <textarea
                      value={requestForm.description}
                      onChange={e => setRequestForm({ ...requestForm, description: e.target.value })}
                      placeholder="e.g., Scenarios with costumes, characters, or fantasy situations like teacher/student, boss/employee..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white h-20 resize-none"
                      maxLength={500}
                    />
                  </div>

                  {/* Multiple Example URLs */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-1">Example links (optional)</label>
                    <p className="text-zinc-500 text-xs mb-2">Paste RedGifs links showing what would fit this category</p>
                    <div className="space-y-2">
                      {requestForm.exampleUrls.map((url, idx) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="url"
                            value={url}
                            onChange={e => {
                              const updated = [...requestForm.exampleUrls];
                              updated[idx] = e.target.value;
                              setRequestForm({ ...requestForm, exampleUrls: updated });
                            }}
                            placeholder="https://redgifs.com/watch/..."
                            className="flex-1 bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-2.5 text-white text-sm"
                          />
                          {requestForm.exampleUrls.length > 1 && (
                            <button
                              onClick={() => {
                                const updated = requestForm.exampleUrls.filter((_, i) => i !== idx);
                                setRequestForm({ ...requestForm, exampleUrls: updated });
                              }}
                              className="px-3 py-2 bg-red-500/20 text-red-400 rounded-xl hover:bg-red-500/30"
                            >
                              ✕
                            </button>
                          )}
                        </div>
                      ))}
                      {requestForm.exampleUrls.length < 5 && (
                        <button
                          onClick={() => setRequestForm({ ...requestForm, exampleUrls: [...requestForm.exampleUrls, ''] })}
                          className="w-full py-2 border-2 border-dashed border-zinc-600 rounded-xl text-zinc-400 text-sm hover:border-purple-500 hover:text-purple-400"
                        >
                          + Add another link
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Why they want it */}
                  <div>
                    <label className="block text-sm text-zinc-300 mb-1">Why do you want this category?</label>
                    <textarea
                      value={requestForm.whyWanted}
                      onChange={e => setRequestForm({ ...requestForm, whyWanted: e.target.value })}
                      placeholder="e.g., We're really into this and there's nothing like it in the app..."
                      className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-4 py-3 text-white h-16 resize-none"
                      maxLength={300}
                    />
                  </div>
                </>
              )}
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => { setShowRequestModal(false); setEditingRequestId(null); setRequestForm({ type: 'clip', description: '', categoryId: '', exampleUrl: '', exampleUrls: [''], categoryName: '', whyWanted: '' }); }}
                className="flex-1 py-3 rounded-xl font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600"
              >
                Cancel
              </button>
              <button
                onClick={() => editingRequestId ? updateRequest(editingRequestId) : saveRequest(requestForm)}
                disabled={!requestForm.description.trim() || (requestForm.type === 'category' && !requestForm.categoryName?.trim())}
                className="flex-1 py-3 rounded-xl font-medium text-white bg-purple-500 disabled:opacity-50 hover:bg-purple-600"
              >
                <Lightbulb className="w-4 h-4 inline mr-1" /> {editingRequestId ? 'Save Changes' : 'Submit Request'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Coin Flip */}
        {showCoinFlip && hisPick && herPick && (
          <CoinFlip
            hisName={hisName}
            herName={herName}
            hisPick={hisPick}
            herPick={herPick}
            onResult={(clip) => {
              const cat = CATEGORIES.find(c => c.id === clip.categoryId);
              if (cat) {
                selectClipWithTracking({ category: cat, clip });
                setRevealed(true);
              }
              setShowCoinFlip(false);
              setHisPick(null);
              setHerPick(null);
            }}
            onClose={() => setShowCoinFlip(false)}
          />
        )}

        {/* Video Watch Modal */}
        <VideoModal
          isOpen={!!watchingClip}
          onClose={() => setWatchingClip(null)}
          url={watchingClip?.link}
          title={watchingClip?.title}
          description={watchingClip?.description}
          clipId={watchingClip?.id}
        />

        {/* Favorite Note Prompt Modal */}
        {favoritePrompt && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-zinc-800 rounded-2xl w-full max-w-md border border-zinc-700 shadow-2xl">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-pink-500/20 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-pink-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">Adding to Favorites!</h3>
                    <p className="text-zinc-400 text-sm">{favoritePrompt.clipTitle}</p>
                  </div>
                </div>

                <p className="text-zinc-300 text-sm mb-4">
                  What made you want to save this clip? Your note helps your reviewer understand your preferences better!
                </p>

                {/* Quick options */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    '🔥 Looks hot!',
                    '💕 Want to try this',
                    '😍 Love this style',
                    '🎯 Perfect for us',
                    '✨ Inspiring'
                  ].map(quick => (
                    <button
                      key={quick}
                      onClick={() => setFavoriteNote(favoriteNote ? `${favoriteNote} ${quick}` : quick)}
                      className="px-3 py-1 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-full text-xs transition-colors"
                    >
                      {quick}
                    </button>
                  ))}
                </div>

                <textarea
                  value={favoriteNote}
                  onChange={e => setFavoriteNote(e.target.value)}
                  placeholder="Add a note (optional)... e.g., 'Love the energy!' or 'Would try this position'"
                  className="w-full bg-zinc-700 border border-zinc-600 rounded-lg px-4 py-3 text-white text-sm resize-none mb-4"
                  rows={3}
                  autoFocus
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setFavoritePrompt(null);
                      setFavoriteNote('');
                    }}
                    className="flex-1 px-4 py-3 bg-zinc-700 hover:bg-zinc-600 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => saveFavoriteWithNote()}
                    className="px-4 py-3 bg-zinc-600 hover:bg-zinc-500 text-zinc-300 rounded-lg text-sm font-medium transition-colors"
                  >
                    Skip Note
                  </button>
                  <button
                    onClick={() => saveFavoriteWithNote(favoriteNote)}
                    className="flex-1 px-4 py-3 rounded-lg text-sm font-medium text-zinc-900 transition-colors flex items-center justify-center gap-2"
                    style={{ backgroundColor: COLORS.lime }}
                  >
                    <Heart className="w-4 h-4" /> Save Favorite
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Activity Modal */}
        <ScheduleActivityModal
          isOpen={showScheduleModal}
          onClose={() => {
            setShowScheduleModal(false);
            setSelectedCalendarDate(undefined);
            setRescheduleActivityId(null);
          }}
          clips={clips}
          preferences={coupleData?.tried?.map(t => ({
            clipId: t.clipId,
            categoryId: clips.find(c => c.id === t.clipId)?.categoryId || '',
            clipTitle: clips.find(c => c.id === t.clipId)?.title || '',
            rating: 'try' as const,
            ratedAt: t.triedAt,
          })) || []}
          initialDate={selectedCalendarDate}
          onSchedule={handleScheduleActivity}
          context={getCoupleContext()}
          coupleId={coupleData?.coupleId}
          tried={tried}
        />

        {/* Category Q&A Modal */}
        {showCategoryQA && coupleData && (
          <CategoryQAModal
            categoryId={showCategoryQA}
            coupleData={coupleData}
            onSave={saveCategoryQA}
            onClose={() => setShowCategoryQA(null)}
          />
        )}

        {/* Floating Chat */}
        {isSetup && coupleData?.coupleId && (
          <FloatingChat
            coupleId={coupleData.coupleId}
            userType="couple"
            userName={herName || hisName || 'You'}
            partnerLabel={coupleData.managedByReviewerName ? `Chat with ${coupleData.managedByReviewerName}` : 'Chat with Reviewer'}
            clips={clips}
            accentColor="pink"
            aboveTabBar
          />
        )}
      </div>
      {isSetup && <DateNiteTabBar active={tab} inboxCount={inboxCount} onSelect={goToTab} />}
      {isSetup && <DateNiteSideNav active={tab} inboxCount={inboxCount} onSelect={goToTab} onHome={onBack} hisName={hisName} herName={herName} />}
    </div>
  );
};
